import React, { Component } from 'react';
import { Row, Col, Container, Card, Form, Modal } from "react-bootstrap";
import { gql } from "apollo-boost";
import { withApollo } from "react-apollo";
import * as Cookies from "es-cookie";
import moment from "moment";
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
    BarChart,
    Bar,
    LineChart,
    Line,
    ComposedChart,
    XAxis,
    YAxis,
    CartesianGrid,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
} from "recharts";
import { Link } from "react-router-dom";
import SelectDropDown from "../selectdropdown/SelectDropDown";
import CardLessDataTableWithSearch from "../datatables/CardLessDataTableWithSearch";
import DateBlock from "./DateBlock";
import UpcomingExamsWidget from "./UpcomingExamsWidget";
import ExamDetailsModal from "./examReportsAnalytics/ExamDetailsModal";
import {
    buildScopedReportCard,
    buildReportCardHTML,
    printReportCardHTML,
    findStudentInExam,
} from "./examReportsAnalytics/studentReportCard";
import DashboardAIChatbot from "./DashboardAIChatbot";
import { buildModuleVisibility } from "../navbars/SideNavbar";
import { MODULE_ROUTES } from "../../../moduleAccessGuard";
import {
    getAuthorizedBranchIds,
    getAuthorizedClassIds,
    getAuthorizedSectionIds,
    filterByAuthorizedIds,
} from "../../utils/authHelper";

// How many exams a single report-card click may look through before giving up. The exam
// the row names is probed first, so this only bites when that exam can't be identified.
const REPORT_CARD_EXAM_PROBE_LIMIT = 6;

const shimmerCss = `
@keyframes lectureExamShimmer { 0% { background-position: -250px 0; } 100% { background-position: 250px 0; } }
.lecture-exam-shim { background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 250px 100%; animation: lectureExamShimmer 1.4s infinite linear; border-radius: 6px; }
@keyframes scheduleItemFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
.schedule-item-anim { animation: scheduleItemFadeIn 0.28s ease both; transition: box-shadow 0.2s ease, transform 0.2s ease; }
.schedule-item-anim:hover { box-shadow: 0 4px 14px rgba(0,0,0,0.08); transform: translateY(-1px); }
@keyframes moduleCardFadeIn { from { opacity: 0; transform: translateY(8px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
.module-access-card {
    display: block;
    text-align: center;
    text-decoration: none;
    background: #fff;
    border: 1px solid #f1f5f9;
    border-radius: 14px;
    padding: 18px 10px;
    animation: moduleCardFadeIn 0.3s ease both;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}
.module-access-card:hover {
    text-decoration: none;
    transform: translateY(-4px);
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.1);
    border-color: transparent;
}
.module-access-icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 10px;
    font-size: 19px;
    transition: transform 0.2s ease;
}
.module-access-card:hover .module-access-icon { transform: scale(1.08); }
.module-access-label {
    display: block;
    font-size: 12.5px;
    font-weight: 700;
    color: #1f2937;
    line-height: 1.3;
}
.modern-card {
    border-radius: 16px !important;
    background: linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%);
    border-top: none !important;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.modern-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.09) !important;
}

/* ── Design system ─────────────────────────────────────────────────────────
   Scoped to .lecturer-dashboard-modern (the dashboard's outer Container, plus
   dialogClassName on every react-bootstrap Modal, since Modals portal to
   document.body and would otherwise fall outside this scope) so these broad
   Bootstrap-class overrides (.card/.btn/.form-control/table/...) never leak
   into the rest of the app. Pure visual layer — no markup/behavior changes. */
.lecturer-dashboard-modern {
    --lec-primary: #2563eb;
    --lec-primary-light: #eff6ff;
    --lec-success: #16a34a;
    --lec-danger: #dc2626;
    --lec-muted: #64748b;
    --lec-border: #eef1f6;
    --lec-radius: 16px;
}
.lecturer-dashboard-modern h3,
.lecturer-dashboard-modern h5,
.lecturer-dashboard-modern h6 { letter-spacing: -0.01em; }

.lecturer-dashboard-modern .card {
    border-radius: var(--lec-radius) !important;
    border: 1px solid var(--lec-border) !important;
}
.lecturer-dashboard-modern .card:not(.modern-card) {
    transition: box-shadow 0.25s ease, transform 0.25s ease;
}
.lecturer-dashboard-modern .card:not(.modern-card):hover {
    box-shadow: 0 10px 26px rgba(15, 23, 42, 0.08) !important;
}

.lecturer-dashboard-modern .btn {
    border-radius: 10px;
    font-weight: 600;
    transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
}
.lecturer-dashboard-modern .btn:active { transform: scale(0.96); }
.lecturer-dashboard-modern .btn-primary {
    background: linear-gradient(135deg, #2563eb, #3b82f6);
    border: none;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
}
.lecturer-dashboard-modern .btn-primary:hover {
    box-shadow: 0 6px 18px rgba(37, 99, 235, 0.32);
    transform: translateY(-1px);
}
.lecturer-dashboard-modern .btn-outline-secondary,
.lecturer-dashboard-modern .btn-outline-primary,
.lecturer-dashboard-modern .btn-outline-danger {
    background: #fff;
}
.lecturer-dashboard-modern .btn-outline-secondary:hover,
.lecturer-dashboard-modern .btn-outline-primary:hover,
.lecturer-dashboard-modern .btn-outline-danger:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.08);
}

.lecturer-dashboard-modern .form-control,
.lecturer-dashboard-modern select {
    border-radius: 10px;
    border-color: #e2e8f0;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.lecturer-dashboard-modern .form-control:focus {
    border-color: var(--lec-primary);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}
.lecturer-dashboard-modern .form-label { color: var(--lec-muted); font-weight: 600; }

/* react-select (SelectDropDown) renders emotion-hashed classnames that still end
   in these conventional suffixes regardless of prefix — safe best-effort hook. */
.lecturer-dashboard-modern [class*="-control"] {
    border-radius: 10px !important;
    border-color: #e2e8f0 !important;
    min-height: 38px !important;
    transition: border-color 0.2s ease, box-shadow 0.2s ease !important;
}
.lecturer-dashboard-modern [class*="-control"]:hover { border-color: #cbd5e1 !important; }

.lecturer-dashboard-modern table { border-collapse: separate !important; border-spacing: 0; }
.lecturer-dashboard-modern table thead th {
    background: linear-gradient(180deg, #f8fafc, #f1f5f9) !important;
    letter-spacing: 0.02em;
}
.lecturer-dashboard-modern table tbody tr { transition: background-color 0.15s ease; }
.lecturer-dashboard-modern table tbody tr:hover { background-color: #f8fafc; }

.lecturer-dashboard-modern .badge { border-radius: 20px; font-weight: 700; }

.lecturer-dashboard-modern *::-webkit-scrollbar { height: 8px; width: 8px; }
.lecturer-dashboard-modern *::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 8px; }
.lecturer-dashboard-modern *::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
.lecturer-dashboard-modern *::-webkit-scrollbar-track { background: transparent; }

/* Cascading entrance for each top-level dashboard section (direct children of the
   page only — deliberately not applied to nested rows/filters/charts, so dozens of
   inner grids don't all animate at once). */
@keyframes lecDashboardFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.lecturer-dashboard-modern > .card,
.lecturer-dashboard-modern > .row > [class*="col"] {
    animation: lecDashboardFadeIn 0.4s ease both;
}
.lecturer-dashboard-modern > .row > [class*="col"]:nth-child(2) { animation-delay: 60ms; }
.lecturer-dashboard-modern > .row > [class*="col"]:nth-child(3) { animation-delay: 120ms; }
.lecturer-dashboard-modern > .row > [class*="col"]:nth-child(4) { animation-delay: 180ms; }

.lec-hero-card {
    background: linear-gradient(120deg, #1d4ed8 0%, #2563eb 45%, #4f46e5 100%) !important;
    border: none !important;
    position: relative;
    overflow: hidden;
}
.lec-hero-card::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 85% -10%, rgba(255,255,255,0.18), transparent 55%);
    pointer-events: none;
}
.lec-hero-card:hover { transform: none !important; box-shadow: 0 14px 32px rgba(29, 78, 216, 0.28) !important; }

/* Plain bordered panel wrappers used throughout the exam-analytics sub-dashboards
   (Section helper components) — these aren't Bootstrap .card elements, so they don't
   pick up the .card hover rules above; this gives them the same hover polish. */
.lec-panel {
    transition: box-shadow 0.25s ease, border-color 0.25s ease;
}
.lec-panel:hover {
    box-shadow: 0 8px 22px rgba(15, 23, 42, 0.07);
    border-color: #e2e8f0 !important;
}

/* Small stat-card tiles used across the analytics overview grids. */
.lec-stat-card {
    transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
}
.lec-stat-card:hover {
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
    transform: translateY(-2px);
    border-color: #e2e8f0 !important;
}
.lec-stat-card:hover .lec-stat-icon { transform: scale(1.1); }
.lec-stat-icon { transition: transform 0.2s ease; }
.lecturer-dashboard-modern .card:hover .lec-stat-icon,
.modern-card:hover .lec-stat-icon { transform: scale(1.1) rotate(-3deg); }
`;

// Distinct route metadata so every accessible module card gets a unique, clear title,
// matching icon, specific description, and color theme — preventing duplicate module names.
const ROUTE_MODULE_META = {
    "/attendance-regularization": {
        label: "Attendance Regularization",
        icon: "fal fa-user-check",
        color: "#2563eb",
        bg: "#eff6ff",
        desc: "Mark and regularize daily student & staff attendance."
    },
    "/Employee_Attendance_Report": {
        label: "Attendance Reports",
        icon: "fal fa-chart-line",
        color: "#7c3aed",
        bg: "#f5f3ff",
        desc: "Explore detailed attendance analytics and register logs."
    },
    "/hostel/attendance-report": {
        label: "Hostel Attendance",
        icon: "fal fa-building-user",
        color: "#0891b2",
        bg: "#ecfeff",
        desc: "Track hostel resident check-ins and night rolls."
    },
    "/student-permission": {
        label: "Student Permissions",
        icon: "fal fa-user-lock",
        color: "#16a34a",
        bg: "#f0fdf4",
        desc: "Review and approve student leave & permission requests."
    },
    "/Student_Gate_Pass": {
        label: "Gate Pass Management",
        icon: "fal fa-id-badge",
        color: "#0284c7",
        bg: "#e0f2fe",
        desc: "Issue and verify student campus gate passes."
    },
    "/timetable-builder": {
        label: "Timetable Builder",
        icon: "fal fa-calendar-alt",
        color: "#c2410c",
        bg: "#fff7ed",
        desc: "View and organize class period schedules & timetables."
    },
    "/academic-planner": {
        label: "Academic Planner",
        icon: "fal fa-calendar-edit",
        color: "#0891b2",
        bg: "#ecfeff",
        desc: "Plan curriculum milestones, exams, and academic events."
    },
    "/student-diary": {
        label: "Student Diary",
        icon: "fal fa-journal-whills",
        color: "#db2777",
        bg: "#fdf2f8",
        desc: "Record daily class notes, homework, and teacher remarks."
    },
    "/assignment": {
        label: "Assignments & Homework",
        icon: "fal fa-tasks-alt",
        color: "#65a30d",
        bg: "#f7fee7",
        desc: "Create, assign, and grade student coursework and tests."
    },
    "/settings/students": {
        label: "Student Directory",
        icon: "fal fa-users",
        color: "#d97706",
        bg: "#fffbeb",
        desc: "Access student profiles, enrollments, and records."
    },
    "/ExamReport_tabs": {
        label: "Exam Reports & Analytics",
        icon: "fal fa-file-chart-line",
        color: "#4f46e5",
        bg: "#eef2ff",
        desc: "View comprehensive exam results and marksheets."
    },
    "/questions/manage-question-paper": {
        label: "Question Papers",
        icon: "fal fa-file-alt",
        color: "#059669",
        bg: "#ecfdf5",
        desc: "Create and manage question papers and test series."
    },
    "/offline_exams/manage-question-paper": {
        label: "Offline Question Papers",
        icon: "fal fa-file-edit",
        color: "#d97706",
        bg: "#fffbeb",
        desc: "Draft and print physical offline examination papers."
    },
    "/admissions-dashboard": {
        label: "Admissions Dashboard",
        icon: "fal fa-user-plus",
        color: "#2563eb",
        bg: "#eff6ff",
        desc: "Track student admission applications and inquiries."
    },
    "/Fee-Structure": {
        label: "Fee Structure & Collections",
        icon: "fal fa-file-invoice-dollar",
        color: "#059669",
        bg: "#ecfdf5",
        desc: "Manage fee categories, installments, and receipts."
    },
    "/expense-management": {
        label: "Expense Management",
        icon: "fal fa-wallet",
        color: "#dc2626",
        bg: "#fef2f2",
        desc: "Track department expenses and financial disbursements."
    },
    "/communication-management": {
        label: "Communication Center",
        icon: "fal fa-comments",
        color: "#9333ea",
        bg: "#faf5ff",
        desc: "Send broadcast SMS, emails, and parent notifications."
    },
    "/lecture-library": {
        label: "Lecture Library",
        icon: "fal fa-books",
        color: "#4f46e5",
        bg: "#eef2ff",
        desc: "Access recorded lectures, slides, and study resources."
    },
};

// Rotating palette so the module grid reads as distinct, colorful tiles rather than
// one repeated color — MODULE_ROUTES carries no icon/color metadata of its own.
const MODULE_ICON_PALETTE = [
    { icon: "fal fa-th-large", color: "#2563eb", bg: "#eff6ff", desc: "Access your main dashboard and overview metrics." },
    { icon: "fal fa-chart-network", color: "#7c3aed", bg: "#f5f3ff", desc: "Explore analytics, charts, and performance insights." },
    { icon: "fal fa-graduation-cap", color: "#16a34a", bg: "#f0fdf4", desc: "Manage student records and academic progress." },
    { icon: "fal fa-calendar-check", color: "#c2410c", bg: "#fff7ed", desc: "View and manage attendance registers and reports." },
    { icon: "fal fa-file-invoice", color: "#0891b2", bg: "#ecfeff", desc: "Access invoices, fee records, and financial data." },
    { icon: "fal fa-users-class", color: "#db2777", bg: "#fdf2f8", desc: "Manage class groups, sections, and student lists." },
    { icon: "fal fa-clipboard-list", color: "#65a30d", bg: "#f7fee7", desc: "Create and review assignments, tasks, and tests." },
    { icon: "fal fa-bell", color: "#d97706", bg: "#fffbeb", desc: "Send and manage notifications and communications." },
];

// Exam Reports' date/endDate have been seen as either a unix-second value or an
// already-formatted date string depending on backend version — same flexible parse
// used for start_time elsewhere in this dashboard, just rendered as DD-MM-YYYY here.
const formatReportDate = (value) => {
    if (!value) return "-";
    const raw = String(value).trim();
    const m = /^-?\d+$/.test(raw) ? moment.unix(Number(raw)) : moment(raw);
    return m.isValid() ? m.format("DD-MM-YYYY") : raw;
};

const STATUS_STYLES = {
    Present: { background: "#dcfce7", color: "#15803d" },
    Absent: { background: "#fee2e2", color: "#b91c1c" },
    Leave: { background: "#ffedd5", color: "#c2410c" },
    "-": { background: "#f3f4f6", color: "#6b7280" },
};

const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// The API has no exam-per-period field, so the (JEE)/(NEET)/... tag is resolved by
// matching this lecturer's exam list (fetched separately) against the class/section
// name — best-effort, and simply omitted when nothing matches.
const EXAM_CELL_COLORS = {
    JEE: { bar: "#4f46e5", bg: "#eef2ff", color: "#3730a3" },
    NEET: { bar: "#16a34a", bg: "#f0fdf4", color: "#166534" },
    EAMCET: { bar: "#d97706", bg: "#fffbeb", color: "#92400e" },
    FOUNDATION: { bar: "#7c3aed", bg: "#f5f3ff", color: "#5b21b6" },
};
const DEFAULT_CELL_COLOR = { bar: "#6366f1", bg: "#eef2ff", color: "#3730a3" };

const SCHEDULE_STATUS_STYLES = {
    Completed: { background: "#dcfce7", color: "#15803d" },
    "In Progress": { background: "#2563eb", color: "#fff" },
    Upcoming: { background: "#fbbf24", color: "#78350f" },
};

// Placeholder data — no backend endpoint for syllabus progress / teaching metrics
// exists yet; swap this for a real query once one is available.
const SYLLABUS_OVERALL_DUMMY = 78;
const SYLLABUS_PROGRESS_DUMMY = [
    { subject: "Physics (Mechanics & Electrodynamics)", percent: 84, color: "#2563eb" },
    { subject: "Mathematics (Calculus & Algebra)", percent: 76, color: "#16a34a" },
    { subject: "NEET Medical Physics (Optics & Modern)", percent: 81, color: "#06b6d4" },
    { subject: "Foundation Basic Sciences", percent: 70, color: "#f59e0b" },
];
const TEACHING_METRICS_DUMMY = [
    { label: "Lectures", value: 124 },
    { label: "Doubts", value: 342 },
    { label: "Tests Set", value: 18 },
    { label: "Rating", value: "4.9 / 5.0", color: "#15803d" },
];

// Single call covers both the header's exam dropdown (subjects[].exam_id/exam_name)
// and the "My Assignments" panel (classes/subjects[].subject_id,subject_name/branches/
// sections) — one getLecturerPerformanceReport request instead of two.
const GET_LECTURER_DASHBOARD_HEADER = gql`
  query ($institution_id: Int!, $employee_id: Int!) {
    getLecturerPerformanceReport(institution_id: $institution_id, employee_id: $employee_id) {
      subjects {
        exam_id
        exam_name
        subject_id
        subject_name
      }
      classes {
        class_id
        class_name
      }
      branches {
        branch_id
        branch_name
      }
      sections {
        section_id
        section_name
      }
    }
  }
`;

const GET_ACADEMIC_YEARS = gql`
  query getAcademicYears($id: Int) {
    getAcademicYears(id: $id) {
      id
      academic_year
    }
  }
`;

const GET_BRANCHES = gql`
  query getBranches($institution_id: Int!) {
    getBranches(institution_id: $institution_id) {
      id
      branch_name
      sections {
        id
        class_id
        section_name
      }
    }
  }
`;

const GET_CLASSES = gql`
  query ($id: Int) {
    getExamClasses(id: $id) {
      id
      class
    }
  }
`;

const GET_MULTI_DATE_ATTENDANCE_REPORT = gql`
  query getMultiDateAttendanceReport(
    $academic_year_id: Int!
    $institute_id: Int!
    $branch_id: Int
    $branch_ids: String
    $class_id: String
    $section_id: String
    $from_date: String
    $to_date: String
    $mainSource: String
  ) {
    getMultiDateAttendanceReport(
      academic_year_id: $academic_year_id
      institute_id: $institute_id
      branch_id: $branch_id
      branch_ids: $branch_ids
      class_id: $class_id
      section_id: $section_id
      from_date: $from_date
      to_date: $to_date
      mainSource: $mainSource
    ) {
      class_id
      section_id
      branch_id
      att_date
      total_students
      present
      absent
      percentage
    }
  }
`;

const GET_LECTURER_TIME_TABLE = gql`
  query getLecturerTimeTable(
    $institute_id: Int!
    $branch_id: Int!
    $employee_id: Int!
    $class_start_date: String!
    $class_end_date: String
  ) {
    getLecturerTimeTable(
      institute_id: $institute_id
      branch_id: $branch_id
      employee_id: $employee_id
      class_start_date: $class_start_date
      class_end_date: $class_end_date
    ) {
      employee_name
      period_name
      period_time
      subject_id
      subject_name
      class_id
      class_name
      section_id
      section_name
      class_date
      class_topic
    }
  }
`;

const GET_STUDENT_ATTENDANCE_REPORT_DETAILS = gql`
  query getStudentAttendanceReportDetails(
    $academic_year_id: Int!
    $institute_id: Int!
    $branch_id: Int
    $branch_ids: String
    $class_id: String
    $section_id: String
    $from_date: String
    $to_date: String
    $mainSource: String
  ) {
    getStudentAttendanceReportDetails(
      academic_year_id: $academic_year_id
      institute_id: $institute_id
      branch_id: $branch_id
      branch_ids: $branch_ids
      class_id: $class_id
      section_id: $section_id
      from_date: $from_date
      to_date: $to_date
      mainSource: $mainSource
    ) {
      id
      admission_no
      not_marked
      roll_no
      student_name
      branch_id
      class_id
      section_id
      present
      absent
      attendance_percentage
    }
  }
`;

const GET_LECTURER_SUBJECT_PERFORMANCE = gql`
  query getLecturerSubjectPerformance($institution_id: Int!, $lecturer_id: Int!, $from_date: String!, $to_date: String!) {
    getLecturerSubjectPerformance(
      institution_id: $institution_id
      lecturer_id: $lecturer_id
      from_date: $from_date
      to_date: $to_date
    ) {
      subject_id
      subject_name
      exams_count
      average_marks
      average_accuracy
      highest_marks
      lowest_marks
      pass_percentage
      attendance_percentage
      students_appeared
      students_passed
      students_failed
    }
  }
`;

const GET_LECTURER_CLASS_PERFORMANCE = gql`
  query getLecturerClassPerformance($institution_id: Int!, $lecturer_id: Int!, $from_date: String!, $to_date: String!) {
    getLecturerClassPerformance(
      institution_id: $institution_id
      lecturer_id: $lecturer_id
      from_date: $from_date
      to_date: $to_date
    ) {
      class_id
      class_name
      students
      average_marks
      average_accuracy
      highest_marks
      lowest_marks
      attendance_percentage
      pass_percentage
    }
  }
`;

const GET_LECTURER_SECTION_PERFORMANCE = gql`
  query getLecturerSectionPerformance($institution_id: Int!, $lecturer_id: Int!, $from_date: String!, $to_date: String!) {
    getLecturerSectionPerformance(
      institution_id: $institution_id
      lecturer_id: $lecturer_id
      from_date: $from_date
      to_date: $to_date
    ) {
      section_id
      section_name
      students
      average_marks
      average_accuracy
      highest_marks
      lowest_marks
      attendance_percentage
      pass_percentage
    }
  }
`;

const GET_TOP_PERFORMING_STUDENTS = gql`
  query getTopPerformingStudents($institution_id: Int!, $lecturer_id: Int!, $from_date: String!, $to_date: String!, $limit: Int) {
    getTopPerformingStudents(
      institution_id: $institution_id
      lecturer_id: $lecturer_id
      from_date: $from_date
      to_date: $to_date
      limit: $limit
    ) {
      rank
      student_id
      student_name
      class_name
      section_name
      marks
      accuracy
      overall_rank
      average_marks
      exam_name
      subject_name
    }
  }
`;

const GET_STUDENTS_NEEDING_ATTENTION = gql`
  query getStudentsNeedingAttention($institution_id: Int!, $lecturer_id: Int!, $from_date: String!, $to_date: String!) {
    getStudentsNeedingAttention(
      institution_id: $institution_id
      lecturer_id: $lecturer_id
      from_date: $from_date
      to_date: $to_date
    ) {
      student_id
      student_name
      class_name
      section_name
      marks
      accuracy
      attendance_percentage
      weak_subject
      recommendation
    }
  }
`;

const GET_EXAM_REPORTS = gql`
  query ($institute_id: Int!, $branch_id: String, $class_id: String, $section_id: String, $stream_id: String) {
    getExamReports(institute_id: $institute_id, branch_id: $branch_id, class_id: $class_id, section_id: $section_id, stream_id: $stream_id) {
      id
      name
      className
      date
      endDate
      appeared
      totalStudents
      avgPct
      highPct
      lowPct
      passPct
      errorPct
      totalQuestions
    }
  }
`;

const GET_EXAM_RESULT_DETAIL = gql`
  query ($exam_id: Int!) {
    getExamResultDetail(exam_id: $exam_id) {
         id
      name
      className
      date
      appeared
      avgPct
      highPct
      lowPct
      passPct
      totalQuestions
      maxMarks
      marksPerCorrect
      marksPerWrong
      avgMarks
      highMarks
      lowMarks
      sections
      subjectNames
      subjectAvg {
        subject
        pct
        avgMarks
        maxMarks
      }
      students {
        rank
        name
        mobile
        sectionName
        studentUserId
        correct
        wrong
        totalQuestions
        pct
        percentile
        marks
        correctMarks
        negativeMarks
        maxMarks
        subjects {
          subject
          correct
          wrong
          total
          pct
          marks
          maxMarks
        }
      }
    }
  }
`;

// College branding for the printable student report card (logo / name / address).
const GET_ORG_PROFILE = gql`
  query ($institution_id: Int!) {
    getOrganisationProfile(institution_id: $institution_id) {
      id
      organisation_name
      logo
      complete_address
    }
  }
`;

const GET_STUDENT_DOUBTS = gql`
  query getStudentDoubts($institution_id: Int!, $branch_id: Int) {
    getStudentDoubts(institution_id: $institution_id, branch_id: $branch_id) {
      id
      title
      description
      image_path
      subject
      subject_name
      chapter
      chapter_name
      doubt_status
      status
      name
      mobile
      institution_id
      branch_id
      branch_name
      class_id
      class_name
      section_id
      section_name
      raised_time
      no_of_views
      is_public
      is_clarified
      assigned_faculty_id
      assigned_faculty_name
      answer_text
      answer_image
      answered_by
      answered_time
      awaiting_reply
      pending_reply_count
    }
  }
`;

class LectureDashboardSection extends Component {
    constructor(props) {
        super(props);

        // from_date/to_date on these attendance APIs are unix-second timestamps (see
        // attendanceReport.jsx's startDateCalender/endDateCalender), not "YYYY-MM-DD" strings.
        const todayUnix = moment().startOf("day").unix();

        this.state = {
            exams: [],
            selectedExamId: "",
            selectedExamName: "",
            examsLoading: true,
            examsError: false,
            currentDateTime: new Date(),

            todayUnix,
            academicYearId: null,
            branchId: parseInt(Cookies.get("branch_id")) || null,
            classOptions: [],
            allSectionOptions: [],
            classMap: {},
            // Comma-joined class/section ids this lecturer is authorized for (from the
            // class_id(s)/section_id(s) cookies). Used as the query scope whenever the
            // modal filters are left unset, so an empty filter never falls through to
            // "every class/section in the branch" — only the lecturer's own access.
            authorizedClassIdsCsv: "",
            authorizedSectionIdsCsv: "",

            totalStudents: 0,
            averageAttendance: 0,
            presentCount: 0,
            absentCount: 0,
            notMarkedCount: 0,
            attendanceSummaryLoading: true,
            attendanceSummaryError: false,
            // Class/Section filters on the Attendance Overview card itself — client-side
            // scoped refetch, independent of the deeper Attendance modal's own filters.
            attendanceCardClassId: "",
            attendanceCardSectionId: "",

            weeklySchedule: [],
            weeklyScheduleLoading: true,
            weeklyScheduleError: false,

            scheduleModalShow: false,
            scheduleModalDate: todayUnix,
            scheduleModalPeriods: [],
            scheduleModalLoading: false,
            scheduleModalError: false,
            // Class/Section filters inside the Timetable Details modal — client-side only,
            // narrowing scheduleModalPeriods for the already-fetched date.
            scheduleModalClassId: "",
            scheduleModalSectionId: "",

            showAttendanceModal: false,
            activeCard: "",
            modalClassId: "",
            modalSectionId: "",
            modalFromDate: todayUnix,
            modalToDate: todayUnix,
            // Present/Absent/Leave filter applied client-side to the already-fetched
            // modalDetails — never triggers a new API call.
            modalStatusFilter: "",
            modalDetails: [],
            modalLoading: false,
            modalError: false,

            // Derived from the same GET_LECTURER_DASHBOARD_HEADER response as `exams` —
            // no separate loading/error state, it shares examsLoading/examsError.
            lecturerAssignments: null,

            topStudentsView: "table",
            attentionStudentsView: "table",

            // Subject/Class/Section Performance share one Graph/Table toggle and one
            // date range (single header control for all three, not one each).
            performanceView: "graph",
            performanceFromDate: moment().subtract(30, "days").startOf("day").unix(),
            performanceToDate: todayUnix,

            // Independent chart-type selector for the Section Performance panel.
            // Values: "bar" | "line" | "pie"
            sectionChartType: "bar",

            subjectPerformance: [],
            subjectPerformanceLoading: true,
            subjectPerformanceError: false,

            classPerformance: [],
            classPerformanceLoading: true,
            classPerformanceError: false,

            sectionPerformance: [],
            sectionPerformanceLoading: true,
            sectionPerformanceError: false,

            topStudents: [],
            topStudentsLoading: true,
            topStudentsError: false,

            // Printable report card: `orgProfile` is the college header (logo/address),
            // reportCardBusyId is the row whose card is being resolved (per-row spinner),
            // reportCardError is surfaced inline above the table.
            orgProfile: null,
            reportCardBusyId: "",
            reportCardError: "",
            reportCardErrorSource: "",

            attentionStudents: [],
            attentionStudentsLoading: true,
            attentionStudentsError: false,

            examReports: [],
            examReportsLoading: true,
            examReportsError: false,

            classSectionModalShow: false,
            classSectionModalType: "",

            highestMarksModalShow: false,
            highestMarksExamDetail: null,
            highestMarksDetailLoading: false,

            examDetailsModalShow: false,
            selectedExamForDetails: null,

            studentActionModalShow: false,
            selectedStudentForAction: null,
            studentActionType: "",
            actionNotificationSent: false,

            studentDoubts: [],
            studentDoubtsLoading: true,
            studentDoubtsError: false,
            studentDoubtsFilter: "all",
            studentDoubtsSubjectFilter: "",
            studentDoubtsView: "cards",
            selectedDoubtModal: null,
        };

        // Cache of already-fetched student attendance detail lists, keyed by
        // `date|classId|sectionId`, so re-opening the same filter combo (e.g. toggling
        // between cards without changing filters) skips the network call.
        this.detailsCache = {};

        // getExamResultDetail responses keyed by exam id — printing several report cards
        // from the same exam must not re-fetch the whole cohort each time.
        this.examDetailCache = {};
    }

    componentDidMount() {
        this.getExams();
        this.loadDashboardMeta();
        this.loadOrgProfile();
        this.fetchSubjectPerformance();
        this.fetchClassPerformance();
        this.fetchSectionPerformance();
        this.fetchTopStudents();
        this.fetchAttentionStudents();
        this.fetchStudentDoubts();
        this.clockTimer = setInterval(() => {
            this.setState({ currentDateTime: new Date() });
        }, 1000);
    }

    componentWillUnmount() {
        clearInterval(this.clockTimer);
    }

    getExams = async () => {
        this.setState({ examsLoading: true, examsError: false });
        try {
            const result = await this.props.client.query({
                query: GET_LECTURER_DASHBOARD_HEADER,
                variables: {
                    institution_id: parseInt(Cookies.get("institutionid")),
                    employee_id: parseInt(Cookies.get("id") || Cookies.get("employee_id")),
                },
                fetchPolicy: "network-only",
            });
            const report = result.data.getLecturerPerformanceReport || {};
            const subjects = report.subjects || [];

            const examsById = new Map();
            subjects.forEach((subject) => {
                if (subject.exam_id != null && !examsById.has(subject.exam_id)) {
                    examsById.set(subject.exam_id, { id: subject.exam_id, exam: subject.exam_name });
                }
            });

            // Same subjects array also carries subject_id/subject_name — dedup those
            // separately for the "My Assignments" panel (multiple exam rows can repeat
            // the same subject).
            const subjectsById = new Map();
            subjects.forEach((subject) => {
                if (subject.subject_id != null && !subjectsById.has(subject.subject_id)) {
                    subjectsById.set(subject.subject_id, { subject_id: subject.subject_id, subject_name: subject.subject_name });
                }
            });

            this.setState({
                exams: Array.from(examsById.values()),
                examsLoading: false,
                lecturerAssignments: {
                    classes: report.classes || [],
                    subjects: Array.from(subjectsById.values()),
                    branches: report.branches || [],
                    sections: report.sections || [],
                },
            });
        } catch (err) {
            console.error("LectureDashboardSection getExams error", err);
            this.setState({ examsError: true, examsLoading: false });
        }
    };

    handleExamChange = (e) => {
        const selectedExamId = e.target.value;
        const selectedExam = this.state.exams.find(
            (exam) => String(exam.id) === String(selectedExamId)
        );
        this.setState({
            selectedExamId,
            selectedExamName: selectedExam ? selectedExam.exam : "",
        });
    };

    getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return "Good morning";
        if (hour < 17) return "Good afternoon";
        return "Good evening";
    };

    // Academic year + branch/class/section metadata is only needed to build the filters
    // and query variables — fetch it once on mount, then kick off the attendance summary.
    loadDashboardMeta = async () => {
        try {
            const [yearsResult, branchesResult, classesResult] = await Promise.all([
                this.props.client.query({ query: GET_ACADEMIC_YEARS, fetchPolicy: "network-only" }),
                this.props.client.query({
                    query: GET_BRANCHES,
                    variables: { institution_id: parseInt(Cookies.get("institutionid")) },
                    fetchPolicy: "network-only",
                }),
                this.props.client.query({ query: GET_CLASSES, fetchPolicy: "network-only" }),
            ]);

            const academicYears = yearsResult.data.getAcademicYears || [];
            const academicYearId = academicYears.length > 0 ? academicYears[0].id : null;

            // Scope every dimension to what this lecturer is actually authorized for —
            // an empty cookie means "unrestricted" (see authHelper), never "show everyone
            // else's classes/sections too".
            const authorizedBranchIds = getAuthorizedBranchIds();
            const authorizedClassIds = getAuthorizedClassIds();
            const authorizedSectionIds = getAuthorizedSectionIds();

            let branches = branchesResult.data.getBranches || [];
            branches = filterByAuthorizedIds(branches, authorizedBranchIds, "id");
            const branch =
                branches.find((b) => String(b.id) === String(this.state.branchId)) || branches[0] || null;

            const allClasses = classesResult.data.getExamClasses || [];
            const classMap = {};
            allClasses.forEach((c) => {
                classMap[c.id] = c.class;
            });

            const branchSections = (branch && branch.sections) || [];
            const authorizedSections = filterByAuthorizedIds(branchSections, authorizedSectionIds, "id");
            const authorizedSectionsInClassScope = authorizedClassIds.length
                ? authorizedSections.filter((s) => authorizedClassIds.includes(String(s.class_id)))
                : authorizedSections;

            const classIds = [...new Set(authorizedSectionsInClassScope.map((s) => s.class_id))];
            const classOptions = classIds
                .map((classId) => (classMap[classId] ? { value: classId, label: classMap[classId] } : null))
                .filter(Boolean);
            const allSectionOptions = authorizedSectionsInClassScope.map((s) => ({
                value: s.id,
                label: s.section_name,
                class_id: s.class_id,
            }));

            this.setState(
                {
                    academicYearId,
                    branchId: branch ? branch.id : this.state.branchId,
                    classOptions,
                    allSectionOptions,
                    classMap,
                    authorizedClassIdsCsv: classOptions.map((c) => c.value).join(","),
                    authorizedSectionIdsCsv: allSectionOptions.map((s) => s.value).join(","),
                },
                () => {
                    // Independent of each other — fire both in parallel once branch/class
                    // metadata is ready (neither awaits the other's result).
                    this.fetchAttendanceSummary();
                    this.fetchWeeklySchedule();
                    this.fetchExamReports();
                }
            );
        } catch (err) {
            console.error("LectureDashboardSection loadDashboardMeta error", err);
            this.setState({ attendanceSummaryError: true, attendanceSummaryLoading: false });
        }
    };

    fetchAttendanceSummary = async () => {
        const {
            academicYearId,
            branchId,
            todayUnix,
            authorizedClassIdsCsv,
            authorizedSectionIdsCsv,
            attendanceCardClassId,
            attendanceCardSectionId,
        } = this.state;
        if (!academicYearId || !branchId) {
            this.setState({ attendanceSummaryError: true, attendanceSummaryLoading: false });
            return;
        }

        this.setState({ attendanceSummaryLoading: true, attendanceSummaryError: false });
        try {
            const result = await this.props.client.query({
                query: GET_MULTI_DATE_ATTENDANCE_REPORT,
                variables: {
                    academic_year_id: academicYearId,
                    institute_id: parseInt(Cookies.get("institutionid")),
                    branch_id: parseInt(branchId),
                    class_id: attendanceCardClassId ? String(attendanceCardClassId) : authorizedClassIdsCsv || undefined,
                    section_id: attendanceCardSectionId ? String(attendanceCardSectionId) : authorizedSectionIdsCsv || undefined,
                    from_date: String(todayUnix),
                    to_date: String(todayUnix),
                },
                fetchPolicy: "network-only",
            });
            const rows = result.data.getMultiDateAttendanceReport || [];
            let totalStudents = rows.reduce((sum, r) => sum + (r.total_students || 0), 0);
            const presentCount = rows.reduce((sum, r) => sum + (r.present || 0), 0);
            const absentCount = rows.reduce((sum, r) => sum + (r.absent || 0), 0);

            if (totalStudents === 0) {
                try {
                    const detailsRes = await this.props.client.query({
                        query: GET_STUDENT_ATTENDANCE_REPORT_DETAILS,
                        variables: {
                            academic_year_id: academicYearId,
                            institute_id: parseInt(Cookies.get("institutionid")),
                            branch_id: parseInt(branchId),
                            class_id: attendanceCardClassId ? String(attendanceCardClassId) : authorizedClassIdsCsv || undefined,
                            section_id: attendanceCardSectionId ? String(attendanceCardSectionId) : authorizedSectionIdsCsv || undefined,
                            from_date: String(todayUnix),
                            to_date: String(todayUnix),
                        },
                        fetchPolicy: "network-only",
                    });
                    const details = (detailsRes && detailsRes.data && detailsRes.data.getStudentAttendanceReportDetails) || [];
                    if (details.length > 0) {
                        totalStudents = details.length;
                    }
                } catch (e) {
                    console.log("fallback student details count error", e);
                }
            }

            // The report has no explicit "not marked" field — any gap between
            // total_students and present+absent is bucketed here rather than assumed.
            const notMarkedCount = Math.max(0, totalStudents - presentCount - absentCount);
            const averageAttendance = totalStudents > 0 ? Math.round((presentCount / totalStudents) * 1000) / 10 : 0;

            this.setState({
                totalStudents,
                averageAttendance,
                presentCount,
                absentCount,
                notMarkedCount,
                attendanceSummaryLoading: false,
            });
        } catch (err) {
            console.error("LectureDashboardSection fetchAttendanceSummary error", err);
            this.setState({ attendanceSummaryError: true, attendanceSummaryLoading: false });
        }
    };

    fetchExamReports = async () => {
        const { branchId, authorizedClassIdsCsv, authorizedSectionIdsCsv } = this.state;
        this.setState({ examReportsLoading: true, examReportsError: false });
        try {
            const result = await this.props.client.query({
                query: GET_EXAM_REPORTS,
                variables: {
                    institute_id: parseInt(Cookies.get("institutionid")),
                    branch_id: branchId ? String(branchId) : undefined,
                    class_id: authorizedClassIdsCsv || undefined,
                    section_id: authorizedSectionIdsCsv || undefined,
                },
                fetchPolicy: "network-only",
            });
            const examReports = result.data.getExamReports || [];
            this.setState({ examReports, examReportsLoading: false });
        } catch (err) {
            console.error("LectureDashboardSection fetchExamReports error", err);
            this.setState({ examReportsError: true, examReportsLoading: false });
        }
    };

    handleAttendanceCardClassChange = (name, value) => {
        this.setState({ attendanceCardClassId: value || "", attendanceCardSectionId: "" }, this.fetchAttendanceSummary);
    };

    handleAttendanceCardSectionChange = (name, value) => {
        this.setState({ attendanceCardSectionId: value || "" }, this.fetchAttendanceSummary);
    };

    renderAttendanceDonut = () => {
        const { presentCount, absentCount, notMarkedCount, totalStudents } = this.state;
        if (!totalStudents) {
            return (
                <div className="d-flex align-items-center justify-content-center text-muted" style={{ height: 200, fontSize: 13 }}>
                    No attendance data
                </div>
            );
        }
        const data = [
            { name: "Present", value: presentCount, color: "#16a34a" },
            { name: "Absent", value: absentCount, color: "#dc2626" },
            { name: "Not Marked", value: notMarkedCount, color: "#f59e0b" },
        ].filter((d) => d.value > 0);

        return (
            <div style={{ width: "100%", height: 200 }}>
                <ResponsiveContainer>
                    <PieChart>
                        <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={2} isAnimationActive={false}>
                            {data.map((d) => (
                                <Cell key={d.name} fill={d.color} />
                            ))}
                        </Pie>
                        <Tooltip shared={false} />
                        <Legend verticalAlign="bottom" height={24} iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        );
    };

    fetchSubjectPerformance = async () => {
        const { performanceFromDate, performanceToDate } = this.state;
        this.setState({ subjectPerformanceLoading: true, subjectPerformanceError: false });
        try {
            const result = await this.props.client.query({
                query: GET_LECTURER_SUBJECT_PERFORMANCE,
                variables: {
                    institution_id: parseInt(Cookies.get("institutionid")),
                    lecturer_id: parseInt(Cookies.get("id") || Cookies.get("employee_id")),
                    from_date: String(performanceFromDate),
                    to_date: String(performanceToDate),
                },
                fetchPolicy: "network-only",
            });
            const subjectPerformance = result.data.getLecturerSubjectPerformance || [];
            this.setState({ subjectPerformance, subjectPerformanceLoading: false });
        } catch (err) {
            console.error("LectureDashboardSection fetchSubjectPerformance error", err);
            this.setState({ subjectPerformanceError: true, subjectPerformanceLoading: false });
        }
    };

    // Shared by the Subject/Class/Section Performance card — one date range and one
    // Graph/Table toggle drive all three queries/views, not one control set each.
    handlePerformanceDateChange = (startDate, endDate) => {
        this.setState(
            {
                performanceFromDate: moment(startDate).startOf("day").unix(),
                performanceToDate: moment(endDate).startOf("day").unix(),
            },
            () => {
                this.fetchSubjectPerformance();
                this.fetchClassPerformance();
                this.fetchSectionPerformance();
                this.fetchTopStudents();
                this.fetchAttentionStudents();
            }
        );
    };

    handlePerformanceViewChange = (view) => {
        this.setState({ performanceView: view });
    };

    renderSubjectPerformanceChart = () => {
        const { subjectPerformance } = this.state;
        return (
            <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                    <BarChart data={subjectPerformance}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="subject_name" tick={{ fontSize: 11 }} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                        <Tooltip shared={false} />
                        <Legend />
                        <Bar dataKey="average_accuracy" name="Avg Score %" fill="#4f46e5" radius={[4, 4, 0, 0]} isAnimationActive={false} label={{ position: "top", fontSize: 10, fill: "#4f46e5", fontWeight: 700, formatter: (v) => `${v}%` }} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    };

    renderClassPerformanceChart = () => {
        const { classPerformance } = this.state;
        return (
            <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                    <BarChart data={classPerformance}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="class_name" tick={{ fontSize: 11 }} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                        <Tooltip shared={false} />
                        <Legend />
                        <Bar dataKey="average_accuracy" name="Class Avg %" fill="#f59e0b" radius={[4, 4, 0, 0]} isAnimationActive={false} label={{ position: "top", fontSize: 10, fill: "#f59e0b", fontWeight: 700, formatter: (v) => `${v}%` }} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    };

    renderSectionPerformanceChart = () => {
        const { sectionPerformance, sectionChartType } = this.state;

        const SECTION_METRICS = [
            { key: "average_marks", name: "Avg Marks", color: "#2563eb" },
            { key: "pass_percentage", name: "Pass %", color: "#16a34a" },
            { key: "attendance_percentage", name: "Attend %", color: "#f59e0b" },
            { key: "average_accuracy", name: "Accuracy %", color: "#7c3aed" },
        ];

        // ── Bar chart (Avg Score, custom tooltip) ────────────────────────────────
        if (sectionChartType === "bar") {
            const SectionScoreTooltip = ({ active, payload, label }) => {
                if (!active || !payload || !payload.length) return null;
                const score = payload[0].value;
                const color = score >= 75 ? "#16a34a" : score >= 50 ? "#f59e0b" : "#dc2626";
                return (
                    <div style={{
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                        padding: "10px 14px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        minWidth: 140,
                    }}>
                        <p style={{ margin: "0 0 6px", fontWeight: 700, fontSize: 12.5, color: "#1f2937" }}>
                            {label}
                        </p>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <i className="fal fa-chart-bar" style={{ color, fontSize: 13 }} />
                            <span style={{ fontSize: 12, color: "#64748b" }}>Avg Score:</span>
                            <span style={{ fontWeight: 800, fontSize: 14, color }}>{score}</span>
                        </div>
                    </div>
                );
            };
            return (
                <div style={{ width: "100%", height: 280 }}>
                    <ResponsiveContainer>
                        <BarChart data={sectionPerformance} barCategoryGap="35%" barGap={4}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis
                                dataKey="section_name"
                                tick={{ fontSize: 10, fill: "#64748b" }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                tick={{ fontSize: 10, fill: "#64748b" }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <Tooltip content={<SectionScoreTooltip />} cursor={{ fill: "rgba(37,99,235,0.05)" }} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar
                                dataKey="average_marks"
                                name="Avg Score"
                                fill="#2563eb"
                                radius={[6, 6, 0, 0]}
                                isAnimationActive={false}
                                label={{ position: "top", fontSize: 10, fill: "#2563eb", fontWeight: 700 }}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            );
        }


        // ── Line chart ───────────────────────────────────────────────────────────
        if (sectionChartType === "line") {
            return (
                <div style={{ width: "100%", height: 280 }}>
                    <ResponsiveContainer>
                        <LineChart data={sectionPerformance}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis
                                dataKey="section_name"
                                tick={{ fontSize: 10, fill: "#64748b" }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                domain={[0, 100]}
                                tick={{ fontSize: 10, fill: "#64748b" }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <Tooltip
                                contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }}
                            />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {SECTION_METRICS.map((m) => (
                                <Line
                                    key={m.key}
                                    type="monotone"
                                    dataKey={m.key}
                                    name={m.name}
                                    stroke={m.color}
                                    strokeWidth={2.5}
                                    dot={{ r: 4, fill: m.color, strokeWidth: 0 }}
                                    activeDot={{ r: 6 }}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        // ── Pie chart (one pie per metric, 2×2 grid) ─────────────────────────────
        // Each section becomes a slice; up to 4 pie charts in a 2-column grid.
        const PIE_COLORS = ["#2563eb", "#16a34a", "#f59e0b", "#7c3aed", "#0891b2", "#db2777", "#c2410c", "#65a30d"];
        return (
            <div style={{ width: "100%" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {SECTION_METRICS.map((metric) => {
                        const pieData = sectionPerformance.map((s) => ({
                            name: s.section_name,
                            value: parseFloat(s[metric.key]) || 0,
                        }));
                        const CustomTooltipPie = ({ active, payload }) => {
                            if (active && payload && payload.length) {
                                return (
                                    <div style={{
                                        background: "#fff",
                                        border: "1px solid #e2e8f0",
                                        borderRadius: 8,
                                        padding: "6px 10px",
                                        fontSize: 11,
                                    }}>
                                        <strong>{payload[0].name}</strong>: {payload[0].value}
                                    </div>
                                );
                            }
                            return null;
                        };
                        return (
                            <div key={metric.key} style={{ textAlign: "center" }}>
                                <p style={{ fontSize: 10.5, fontWeight: 700, color: metric.color, marginBottom: 2 }}>
                                    {metric.name}
                                </p>
                                <ResponsiveContainer width="100%" height={120}>
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius="40%"
                                            outerRadius="70%"
                                            paddingAngle={3}
                                            dataKey="value"
                                        >
                                            {pieData.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={PIE_COLORS[index % PIE_COLORS.length]}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip content={<CustomTooltipPie />} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        );
                    })}
                </div>
                {/* Shared legend for all pie slices (section names) */}
                <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "4px 10px", marginTop: 6 }}>
                    {sectionPerformance.map((s, i) => (
                        <span key={s.section_name} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10 }}>
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: PIE_COLORS[i % PIE_COLORS.length], display: "inline-block" }} />
                            {s.section_name}
                        </span>
                    ))}
                </div>
            </div>
        );
    };

    renderTopStudentsChart = (rows) => {
        if (!rows || rows.length === 0) return null;
        const chartData = rows.map((r) => ({
            name: r.student_name || "Student",
            marks: Number(r.marks ?? 0),
            accuracy: Number(r.accuracy ?? 0),
        }));
        const minWidth = Math.max(100, chartData.length * 55);
        return (
            <div style={{ width: "100%", overflowX: "auto", overflowY: "hidden", paddingBottom: 6 }}>
                <div style={{ width: `${minWidth}px`, minWidth: "100%", height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={chartData} margin={{ top: 22, right: 20, left: 0, bottom: 35 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis dataKey="name" tick={{ fontSize: 10.5, fill: "#475569" }} interval={0} angle={-30} textAnchor="end" height={55} />
                            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#16a34a" }} label={{ value: "Marks", angle: -90, position: "insideLeft", fontSize: 10.5, fill: "#16a34a" }} />
                            <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 11, fill: "#0369a1" }} label={{ value: "Accuracy %", angle: 90, position: "insideRight", fontSize: 10.5, fill: "#0369a1" }} />
                            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(val, name) => [name === "Accuracy %" ? `${val}%` : `${val}`, name]} />
                            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
                            <Bar yAxisId="left" dataKey="marks" name="Marks" fill="#16a34a" radius={[4, 4, 0, 0]} maxBarSize={36} isAnimationActive={false} label={{ position: "top", fontSize: 10, fill: "#16a34a", fontWeight: 700 }} />
                            <Line yAxisId="right" type="monotone" dataKey="accuracy" name="Accuracy %" stroke="#0369a1" strokeWidth={2.5} isAnimationActive={false} dot={{ r: 4, fill: "#0369a1", strokeWidth: 2, stroke: "#fff" }} label={{ position: "top", fontSize: 10, fill: "#0369a1", fontWeight: 700, formatter: (val) => `${val}%` }} />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    };

    renderAttentionStudentsChart = (rows) => {
        if (!rows || rows.length === 0) return null;
        const chartData = rows.map((r) => ({
            name: r.student_name || "Student",
            marks: Number(r.marks ?? 0),
            accuracy: Number(r.accuracy ?? 0),
        }));
        const minWidth = Math.max(100, chartData.length * 55);
        return (
            <div style={{ width: "100%", overflowX: "auto", overflowY: "hidden", paddingBottom: 6 }}>
                <div style={{ width: `${minWidth}px`, minWidth: "100%", height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={chartData} margin={{ top: 22, right: 20, left: 0, bottom: 35 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis dataKey="name" tick={{ fontSize: 10.5, fill: "#475569" }} interval={0} angle={-30} textAnchor="end" height={55} />
                            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#dc2626" }} label={{ value: "Marks", angle: -90, position: "insideLeft", fontSize: 10.5, fill: "#dc2626" }} />
                            <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 11, fill: "#c2410c" }} label={{ value: "Accuracy %", angle: 90, position: "insideRight", fontSize: 10.5, fill: "#c2410c" }} />
                            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(val, name) => [name === "Accuracy %" ? `${val}%` : `${val}`, name]} />
                            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
                            <Bar yAxisId="left" dataKey="marks" name="Avg Marks" fill="#dc2626" radius={[4, 4, 0, 0]} maxBarSize={36} isAnimationActive={false} label={{ position: "top", fontSize: 10, fill: "#dc2626", fontWeight: 700 }} />
                            <Line yAxisId="right" type="monotone" dataKey="accuracy" name="Accuracy %" stroke="#c2410c" strokeWidth={2.5} isAnimationActive={false} dot={{ r: 4, fill: "#c2410c", strokeWidth: 2, stroke: "#fff" }} label={{ position: "top", fontSize: 10, fill: "#c2410c", fontWeight: 700, formatter: (val) => `${val}%` }} />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    };

    fetchClassPerformance = async () => {
        const { performanceFromDate, performanceToDate } = this.state;
        this.setState({ classPerformanceLoading: true, classPerformanceError: false });
        try {
            const result = await this.props.client.query({
                query: GET_LECTURER_CLASS_PERFORMANCE,
                variables: {
                    institution_id: parseInt(Cookies.get("institutionid")),
                    lecturer_id: parseInt(Cookies.get("id") || Cookies.get("employee_id")),
                    from_date: String(performanceFromDate),
                    to_date: String(performanceToDate),
                },
                fetchPolicy: "network-only",
            });
            const classPerformance = result.data.getLecturerClassPerformance || [];
            this.setState({ classPerformance, classPerformanceLoading: false });
        } catch (err) {
            console.error("LectureDashboardSection fetchClassPerformance error", err);
            this.setState({ classPerformanceError: true, classPerformanceLoading: false });
        }
    };

    fetchSectionPerformance = async () => {
        const { performanceFromDate, performanceToDate } = this.state;
        this.setState({ sectionPerformanceLoading: true, sectionPerformanceError: false });
        try {
            const result = await this.props.client.query({
                query: GET_LECTURER_SECTION_PERFORMANCE,
                variables: {
                    institution_id: parseInt(Cookies.get("institutionid")),
                    lecturer_id: parseInt(Cookies.get("id") || Cookies.get("employee_id")),
                    from_date: String(performanceFromDate),
                    to_date: String(performanceToDate),
                },
                fetchPolicy: "network-only",
            });
            const sectionPerformance = result.data.getLecturerSectionPerformance || [];
            this.setState({ sectionPerformance, sectionPerformanceLoading: false });
        } catch (err) {
            console.error("LectureDashboardSection fetchSectionPerformance error", err);
            this.setState({ sectionPerformanceError: true, sectionPerformanceLoading: false });
        }
    };

    fetchTopStudents = async () => {
        const { performanceFromDate, performanceToDate } = this.state;
        this.setState({ topStudentsLoading: true, topStudentsError: false });
        try {
            const result = await this.props.client.query({
                query: GET_TOP_PERFORMING_STUDENTS,
                variables: {
                    institution_id: parseInt(Cookies.get("institutionid")),
                    lecturer_id: parseInt(Cookies.get("id") || Cookies.get("employee_id")),
                    from_date: String(performanceFromDate),
                    to_date: String(performanceToDate),
                    limit: 10,
                },
                fetchPolicy: "network-only",
            });
            const topStudents = result.data.getTopPerformingStudents || [];
            this.setState({ topStudents, topStudentsLoading: false });
        } catch (err) {
            console.error("LectureDashboardSection fetchTopStudents error", err);
            this.setState({ topStudentsError: true, topStudentsLoading: false });
        }
    };

    fetchAttentionStudents = async () => {
        const { performanceFromDate, performanceToDate } = this.state;
        this.setState({ attentionStudentsLoading: true, attentionStudentsError: false });
        try {
            const result = await this.props.client.query({
                query: GET_STUDENTS_NEEDING_ATTENTION,
                variables: {
                    institution_id: parseInt(Cookies.get("institutionid")),
                    lecturer_id: parseInt(Cookies.get("id") || Cookies.get("employee_id")),
                    from_date: String(performanceFromDate),
                    to_date: String(performanceToDate),
                },
                fetchPolicy: "network-only",
            });
            const attentionStudents = result.data.getStudentsNeedingAttention || [];
            this.setState({ attentionStudents, attentionStudentsLoading: false });
        } catch (err) {
            console.error("LectureDashboardSection fetchAttentionStudents error", err);
            this.setState({ attentionStudentsError: true, attentionStudentsLoading: false });
        }
    };

    // getTopPerformingStudents/getStudentsNeedingAttention are scoped server-side by
    // lecturer_id only (no class/section/subject params) — this repeats the same
    // "never trust a single filter path" check used elsewhere in this dashboard,
    // matching by name since these two APIs return class_name/section_name/subject_name
    // rather than ids. Empty authorized lists mean unrestricted (see authHelper).
    filterStudentsByAccess = (rows) => {
        const { classOptions, allSectionOptions, lecturerAssignments } = this.state;
        const norm = (v) => String(v || "").trim().toLowerCase();
        const authorizedClassNames = classOptions.map((c) => norm(c.label));
        const authorizedSectionNames = allSectionOptions.map((s) => norm(s.label));
        const authorizedSubjectNames = ((lecturerAssignments && lecturerAssignments.subjects) || []).map((s) =>
            norm(s.subject_name)
        );

        return rows.filter((r) => {
            if (authorizedClassNames.length && r.class_name && !authorizedClassNames.includes(norm(r.class_name))) {
                return false;
            }
            if (authorizedSectionNames.length && r.section_name && !authorizedSectionNames.includes(norm(r.section_name))) {
                return false;
            }
            if (authorizedSubjectNames.length && r.subject_name && !authorizedSubjectNames.includes(norm(r.subject_name))) {
                return false;
            }
            return true;
        });
    };

    // ── Printable student report card ────────────────────────────────────────────
    // College branding for the card header. Non-blocking: if it fails the card still
    // prints, falling back to the institution name in cookies.
    loadOrgProfile = async () => {
        try {
            const result = await this.props.client.query({
                query: GET_ORG_PROFILE,
                variables: { institution_id: parseInt(Cookies.get("institutionid")) },
            });
            this.setState({ orgProfile: (result.data && result.data.getOrganisationProfile) || null });
        } catch (err) {
            console.error("LectureDashboardSection loadOrgProfile error", err);
        }
    };

    // The three name-based authorization lists these dashboard APIs can be scoped against
    // (they return class_name/section_name/subject_name, not ids). Empty means unrestricted.
    getAuthorizedNames = () => {
        const { classOptions, allSectionOptions, lecturerAssignments } = this.state;
        return {
            authorizedClassNames: classOptions.map((c) => c.label),
            authorizedSectionNames: allSectionOptions.map((s) => s.label),
            authorizedSubjectNames: ((lecturerAssignments && lecturerAssignments.subjects) || []).map((s) => s.subject_name),
        };
    };

    fetchExamDetailCached = async (examId) => {
        const key = String(examId);
        if (this.examDetailCache[key] !== undefined) return this.examDetailCache[key];
        const result = await this.props.client.query({
            query: GET_EXAM_RESULT_DETAIL,
            variables: { exam_id: parseInt(examId) },
            fetchPolicy: "network-only",
        });
        const detail = (result.data && result.data.getExamResultDetail) || null;
        this.examDetailCache[key] = detail;
        return detail;
    };

    // getTopPerformingStudents carries an exam_name but no exam_id, so the exam has to be
    // resolved against the (already class/section-scoped) exam reports list: the row's own
    // exam first, then the same class, then most recent — one probe normally settles it.
    reportCardExamCandidates = (row) => {
        const norm = (v) => String(v || "").trim().toLowerCase();
        const { examReports } = this.state;
        const { authorizedClassNames } = this.getAuthorizedNames();
        const allowedClasses = authorizedClassNames.map(norm);

        const inScope = (e) => !allowedClasses.length || !e.className || allowedClasses.includes(norm(e.className));
        const score = (e) => {
            let s = 0;
            if (row.exam_name && norm(e.name) === norm(row.exam_name)) s += 4;
            if (row.class_name && norm(e.className) === norm(row.class_name)) s += 2;
            return s;
        };

        return examReports
            .filter(inScope)
            .slice()
            .sort((a, b) => score(b) - score(a) || (Number(b.date) || 0) - (Number(a.date) || 0));
    };

    // Both student tables print through this one handler, so `source` keeps the spinner and
    // the notice attached to the table the click came from.
    renderReportCardNotice = (source) => {
        const { reportCardError, reportCardErrorSource } = this.state;
        if (!reportCardError || reportCardErrorSource !== source) return null;
        return (
            <div
                className="d-flex align-items-start justify-content-between mb-2"
                style={{ gap: 10, background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e", borderRadius: 8, padding: "8px 10px", fontSize: 12, fontWeight: 600 }}
            >
                <span>
                    <i className="fal fa-exclamation-triangle me-1" />
                    {reportCardError}
                </span>
                <button
                    type="button"
                    className="btn btn-sm p-0"
                    style={{ color: "#92400e", fontSize: 12, lineHeight: 1 }}
                    onClick={() => this.setState({ reportCardError: "", reportCardErrorSource: "" })}
                >
                    <i className="fal fa-times" />
                </button>
            </div>
        );
    };

    // Prints one student's Exam Report Card. Subject rows, totals, and the cohort behind
    // rank / percentile / appeared are all scoped to the lecturer's own subjects and
    // sections — never the whole exam (see buildScopedReportCard).
    printStudentReportCard = async (row, source = "top") => {
        const busyId = `${source}:${row.id}`;
        const candidates = this.reportCardExamCandidates(row);
        if (candidates.length === 0) {
            this.setState({ reportCardError: "No exam reports are available for your classes yet.", reportCardErrorSource: source });
            return;
        }

        const { authorizedSectionNames, authorizedSubjectNames } = this.getAuthorizedNames();
        this.setState({ reportCardBusyId: busyId, reportCardError: "", reportCardErrorSource: "" });

        try {
            // Sequential on purpose: stop at the first exam that actually holds this
            // student's result within the lecturer's scope. Capped so a student with no
            // result never walks the whole exam list.
            for (const exam of candidates.slice(0, REPORT_CARD_EXAM_PROBE_LIMIT)) {
                const detail = await this.fetchExamDetailCached(exam.id);
                if (!detail) continue;

                const student = findStudentInExam(detail, row);
                if (!student) continue;

                const card = buildScopedReportCard(detail, student, { authorizedSectionNames, authorizedSubjectNames });
                // Falsy when this paper carries none of the subjects assigned to this
                // lecturer — there is nothing in scope to print, so try the next exam.
                if (!card) continue;

                printReportCardHTML(
                    `Report Card — ${student.name}`,
                    buildReportCardHTML(
                        card,
                        detail,
                        this.state.orgProfile || {},
                        Cookies.get("institutionname") || Cookies.get("institutename") || Cookies.get("institute_name") || "Institution"
                    )
                );
                this.setState({ reportCardBusyId: "" });
                return;
            }

            this.setState({
                reportCardBusyId: "",
                reportCardError: `No exam result found for ${row.student_name} in the subjects and sections assigned to you.`,
                reportCardErrorSource: source,
            });
        } catch (err) {
            console.error("LectureDashboardSection printStudentReportCard error", err);
            this.setState({
                reportCardBusyId: "",
                reportCardError: "Unable to build the report card right now. Please try again.",
                reportCardErrorSource: source,
            });
        }
    };

    fetchWeeklySchedule = async () => {
        const { branchId } = this.state;
        if (!branchId) {
            this.setState({ weeklyScheduleError: true, weeklyScheduleLoading: false });
            return;
        }

        this.setState({ weeklyScheduleLoading: true, weeklyScheduleError: false });
        const weekStart = moment().startOf("isoWeek");
        const weekEnd = moment(weekStart).add(5, "days").endOf("day");
        try {
            const result = await this.props.client.query({
                query: GET_LECTURER_TIME_TABLE,
                variables: {
                    institute_id: parseInt(Cookies.get("institutionid")),
                    branch_id: parseInt(branchId),
                    employee_id: parseInt(Cookies.get("id") || Cookies.get("employee_id")),
                    class_start_date: String(weekStart.unix()),
                    class_end_date: String(weekEnd.unix()),
                },
                fetchPolicy: "network-only",
            });
            const periods = result.data.getLecturerTimeTable || [];
            this.setState({ weeklySchedule: periods, weeklyScheduleLoading: false });
        } catch (err) {
            console.error("LectureDashboardSection fetchWeeklySchedule error", err);
            this.setState({ weeklyScheduleError: true, weeklyScheduleLoading: false });
        }
    };

    // Buckets the flat period list into { day: { period_time: period } } and derives the
    // sorted set of time-slot columns actually used this week.
    buildWeeklyGrid = (periods) => {
        const slotSet = new Set();
        periods.forEach((p) => {
            if (p.period_time) slotSet.add(p.period_time);
        });
        const slots = Array.from(slotSet).sort((a, b) =>
            a.split("-")[0].trim().localeCompare(b.split("-")[0].trim())
        );

        const grid = {};
        WEEK_DAYS.forEach((d) => {
            grid[d] = {};
        });
        periods.forEach((p) => {
            const dayLabel = p.class_date ? moment.unix(+p.class_date).format("ddd") : null;
            if (dayLabel && grid[dayLabel] && p.period_time) {
                grid[dayLabel][p.period_time] = p;
            }
        });

        return { slots, grid };
    };

    formatSlotLabel = (slot) => {
        const [start, end] = slot.split("-").map((s) => s.trim());
        const fmt = (t) => (moment(t, "HH:mm", true).isValid() ? moment(t, "HH:mm").format("hh:mm") : t);
        return `${fmt(start)} - ${fmt(end)}`;
    };

    // Best-effort exam tag for a period, by matching this lecturer's exam names (from
    // GET_LECTURER_DASHBOARD_HEADER) against the period's class/section name — the
    // timetable API itself carries no exam field. Returns null when nothing matches,
    // and the cell then renders the subject name alone.
    resolveExamTag = (period) => {
        const { exams } = this.state;
        if (!period || !exams.length) return null;
        const haystack = `${period.class_name || ""} ${period.section_name || ""}`.toLowerCase();
        const match = exams.find((e) => e.exam && haystack.includes(String(e.exam).toLowerCase()));
        return match ? match.exam : null;
    };

    getCellColor = (examTag) => EXAM_CELL_COLORS[String(examTag || "").toUpperCase()] || DEFAULT_CELL_COLOR;

    renderScheduleCell = (period) => {
        if (!period) {
            return (
                <div
                    style={{
                        background: "#fafafa",
                        border: "1px dashed #e2e8f0",
                        borderRadius: 10,
                        padding: "8px 6px",
                        textAlign: "center",
                        color: "#94a3b8",
                        fontSize: 11.5,
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 4,
                        minHeight: 52,
                    }}
                >
                    <i className="fal fa-coffee" style={{ fontSize: 12, opacity: 0.6 }} />
                    <span>Free</span>
                </div>
            );
        }
        const examTag = this.resolveExamTag(period);
        const colors = this.getCellColor(examTag);
        const subject = period.subject_name || period.subject || "Lecture";
        const classSec = `${period.class_name || ""} ${period.section_name ? `(${period.section_name})` : ""}`.trim();

        return (
            <div
                className="schedule-cell-card"
                style={{
                    background: colors.bg,
                    border: `1px solid ${colors.bg}`,
                    borderLeft: `4px solid ${colors.bar}`,
                    borderRadius: 10,
                    padding: "8px 10px",
                    transition: "all 0.2s ease",
                    cursor: "pointer",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
                    minHeight: 52,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.08)";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "";
                    e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.03)";
                }}
            >
                <div className="d-flex justify-content-between align-items-center mb-1">
                    <span style={{ fontWeight: 800, fontSize: 12.5, color: colors.color, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {subject}
                    </span>
                    {examTag && (
                        <span
                            style={{
                                background: colors.bar,
                                color: "#fff",
                                borderRadius: 12,
                                padding: "1px 6px",
                                fontSize: 9.5,
                                fontWeight: 800,
                                textTransform: "uppercase",
                                letterSpacing: "0.03em",
                                flexShrink: 0,
                            }}
                        >
                            {examTag}
                        </span>
                    )}
                </div>
                {classSec && (
                    <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                        <i className="fal fa-users" style={{ fontSize: 10 }} />
                        <span>{classSec}</span>
                    </div>
                )}
            </div>
        );
    };

    // Filters a flat period list down to a single date's rows, splitting period_time
    // into start/end for status classification. Shared by today's list and the
    // "View Details" modal (whichever periods list it's given).
    getScheduleForDate = (dateUnix, periods) => {
        const target = moment.unix(dateUnix);
        const statusOrder = {
            "Upcoming": 1,
            "In Progress": 2,
            "Completed": 3,
        };
        return (periods || [])
            .filter((p) => p.class_date && moment.unix(+p.class_date).isSame(target, "day"))
            .map((p) => {
                const [startStr, endStr] = (p.period_time || "").split("-").map((s) => (s || "").trim());
                const status = this.classifyPeriodStatus(startStr, endStr, dateUnix);
                return { ...p, start: startStr, end: endStr, status };
            })
            .sort((a, b) => {
                const orderA = statusOrder[a.status] || 99;
                const orderB = statusOrder[b.status] || 99;
                if (orderA !== orderB) {
                    return orderA - orderB;
                }
                return (a.start || "").localeCompare(b.start || "");
            });
    };

    // Today's list-style schedule is derived from the already-fetched weeklySchedule —
    // no separate API call, just a client-side filter down to today's class_date rows.
    getTodaySchedule = () => this.getScheduleForDate(this.state.todayUnix, this.state.weeklySchedule);

    openScheduleModal = () => {
        this.setState(
            {
                scheduleModalShow: true,
                scheduleModalDate: this.state.todayUnix,
                scheduleModalClassId: "",
                scheduleModalSectionId: "",
            },
            () => this.loadScheduleModalDate(this.state.todayUnix)
        );
    };

    closeScheduleModal = () => this.setState({ scheduleModalShow: false });

    // Class/Section filters inside the modal — purely client-side over the already-
    // fetched scheduleModalPeriods, no refetch.
    handleScheduleModalClassChange = (name, value) => {
        this.setState({ scheduleModalClassId: value || "", scheduleModalSectionId: "" });
    };

    handleScheduleModalSectionChange = (name, value) => {
        this.setState({ scheduleModalSectionId: value || "" });
    };

    filterScheduleByClassSection = (periods) => {
        const { scheduleModalClassId, scheduleModalSectionId } = this.state;
        return periods.filter((p) => {
            if (scheduleModalClassId && String(p.class_id) !== String(scheduleModalClassId)) return false;
            if (scheduleModalSectionId && String(p.section_id) !== String(scheduleModalSectionId)) return false;
            return true;
        });
    };

    // Always calls getLecturerTimeTable fresh for whichever date is picked — the earlier
    // "reuse weeklySchedule when the date falls in the current week" shortcut meant most
    // date changes fired no request at all, which read as "the date filter doesn't work".
    loadScheduleModalDate = async (dateUnix) => {
        const target = moment.unix(dateUnix);
        const { branchId } = this.state;
        if (!branchId) return;

        this.setState({ scheduleModalLoading: true, scheduleModalError: false });
        try {
            const result = await this.props.client.query({
                query: GET_LECTURER_TIME_TABLE,
                variables: {
                    institute_id: parseInt(Cookies.get("institutionid")),
                    branch_id: parseInt(branchId),
                    employee_id: parseInt(Cookies.get("id") || Cookies.get("employee_id")),
                    class_start_date: String(target.clone().startOf("day").unix()),
                    class_end_date: String(target.clone().endOf("day").unix()),
                },
                fetchPolicy: "network-only",
            });
            const periods = result.data.getLecturerTimeTable || [];
            this.setState({
                scheduleModalPeriods: this.getScheduleForDate(dateUnix, periods),
                scheduleModalLoading: false,
            });
        } catch (err) {
            console.error("LectureDashboardSection loadScheduleModalDate error", err);
            this.setState({ scheduleModalError: true, scheduleModalLoading: false });
        }
    };

    // Single-date input (native <input type="date">), not DateBlock's range picker —
    // a timetable is always for one day, so a range control was the wrong fit here.
    handleScheduleModalDateChange = (e) => {
        const value = e.target.value;
        if (!value) return;
        const dateUnix = moment(value, "YYYY-MM-DD").startOf("day").unix();
        this.setState({ scheduleModalDate: dateUnix }, () => this.loadScheduleModalDate(dateUnix));
    };

    // dateUnix is the day this period belongs to — a past/future date (e.g. picked in
    // the "View Details" modal) is classified purely by day comparison, since comparing
    // its "HH:mm" times against the current clock only makes sense for today.
    classifyPeriodStatus = (startStr, endStr, dateUnix) => {
        const now = moment();
        const targetDay = dateUnix ? moment.unix(dateUnix) : now;
        if (!targetDay.isSame(now, "day")) {
            return targetDay.isBefore(now, "day") ? "Completed" : "Upcoming";
        }
        if (!startStr || !endStr) return "Upcoming";
        const start = moment(startStr, "HH:mm");
        const end = moment(endStr, "HH:mm");
        start.date(now.date()).month(now.month()).year(now.year());
        end.date(now.date()).month(now.month()).year(now.year());
        if (now.isBefore(start)) return "Upcoming";
        if (now.isAfter(end)) return "Completed";
        return "In Progress";
    };

    formatPeriodRange = (startStr, endStr) => {
        const sm = moment(startStr, "HH:mm");
        const em = moment(endStr, "HH:mm");
        if (!sm.isValid() || !em.isValid()) return `${startStr} - ${endStr}`;
        return sm.format("A") === em.format("A")
            ? `${sm.format("hh:mm")} - ${em.format("hh:mm A")}`
            : `${sm.format("hh:mm A")} - ${em.format("hh:mm A")}`;
    };

    // Shared card used by both the compact Today's Schedule list and the "View Details"
    // modal, so the two stay visually identical. dateUnix scopes the status calculation
    // to whichever day is being displayed (see classifyPeriodStatus).
    renderScheduleItemCard = (period, dateUnix, idx) => {
        const status = this.classifyPeriodStatus(period.start, period.end, dateUnix);
        const examTag = this.resolveExamTag(period);
        const inProgress = status === "In Progress";
        const isCompleted = status === "Completed";
        const colors = this.getCellColor(examTag);

        return (
            <div
                key={`${period.class_id}-${period.section_id}-${idx}`}
                className="schedule-item-anim"
                style={{
                    padding: "12px 14px",
                    borderRadius: 12,
                    height: "100%",
                    marginBottom: 0,
                    border: inProgress
                        ? "1px solid #bfdbfe"
                        : isCompleted
                            ? "1px solid #bbf7d0"
                            : "1px solid #e2e8f0",
                    borderTop: inProgress
                        ? "4px solid #2563eb"
                        : isCompleted
                            ? "4px solid #16a34a"
                            : "4px solid #f59e0b",
                    background: inProgress
                        ? "linear-gradient(180deg, rgba(37, 99, 235, 0.08) 0%, #eff6ff 100%)"
                        : isCompleted
                            ? "linear-gradient(180deg, rgba(22, 163, 74, 0.08) 0%, #f0fdf4 100%)"
                            : "linear-gradient(180deg, rgba(245, 158, 11, 0.08) 0%, #ffffff 100%)",
                    animationDelay: `${Math.min(idx, 8) * 40}ms`,
                    boxShadow: inProgress
                        ? "0 4px 14px rgba(37, 99, 235, 0.12)"
                        : "0 2px 6px rgba(0,0,0,0.02)",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    overflow: "hidden",
                }}
            >
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="d-flex align-items-center" style={{ fontSize: 12, color: "#64748b", fontWeight: 600, gap: 5 }}>
                        <i className="fal fa-clock text-primary" />
                        {this.formatPeriodRange(period.start, period.end)}
                    </span>
                    {this.scheduleStatusBadge(status)}
                </div>
                <div style={{ color: "#1e293b", fontWeight: 800, fontSize: 14, marginBottom: 4 }}>
                    {period.subject_name || "Lecture"}
                    {period.class_topic ? <span style={{ fontWeight: 600, color: "#475569", fontSize: 13 }}>: {period.class_topic}</span> : ""}
                </div>
                <div className="d-flex justify-content-between align-items-center">
                    <span className="d-flex align-items-center text-muted" style={{ fontSize: 12, fontWeight: 600, gap: 6 }}>
                        <i className="fal fa-users text-muted" />
                        {period.class_name || "Class"}
                        {period.section_name ? ` (${period.section_name})` : ""}
                    </span>
                    {examTag && (
                        <span
                            style={{
                                border: `1px solid ${colors.bar}`,
                                borderRadius: 12,
                                padding: "2px 8px",
                                fontSize: 10.5,
                                fontWeight: 800,
                                color: colors.color,
                                background: colors.bg,
                                textTransform: "uppercase",
                            }}
                        >
                            {examTag}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    fetchStudentDoubts = async () => {
        this.setState({ studentDoubtsLoading: true, studentDoubtsError: false });
        try {
            const instId = parseInt(Cookies.get("institutionid"));
            const branchId = parseInt(Cookies.get("branch_id"));
            const result = await this.props.client.query({
                query: GET_STUDENT_DOUBTS,
                variables: {
                    institution_id: instId || 0,
                    branch_id: branchId || null,
                },
                fetchPolicy: "network-only",
            });
            const allDoubts = result.data?.getStudentDoubts || [];

            // Scope doubts to the logged-in lecturer's assigned scope ("his lecturer data")
            const authorizedBranchIds = getAuthorizedBranchIds();
            const authorizedClassIds = getAuthorizedClassIds();
            const authorizedSectionIds = getAuthorizedSectionIds();
            const empId = parseInt(Cookies.get("id") || Cookies.get("employee_id"));

            const filteredDoubts = allDoubts.filter((d) => {
                if (d.assigned_faculty_id && empId && parseInt(d.assigned_faculty_id) === empId) {
                    return true;
                }
                const branchOk = authorizedBranchIds.length === 0 || authorizedBranchIds.includes(Number(d.branch_id));
                const classOk = authorizedClassIds.length === 0 || authorizedClassIds.includes(Number(d.class_id));
                const sectionOk = authorizedSectionIds.length === 0 || authorizedSectionIds.includes(Number(d.section_id));
                return branchOk && classOk && sectionOk;
            });

            this.setState({
                studentDoubts: filteredDoubts,
                studentDoubtsLoading: false,
            });
        } catch (err) {
            console.error("LectureDashboardSection fetchStudentDoubts error:", err);
            this.setState({ studentDoubtsError: true, studentDoubtsLoading: false });
        }
    };

    getDoubtStatusInfo = (doubt) => {
        const rawStatus = doubt ? doubt.doubt_status : null;
        const statusNum = Number(rawStatus);
        const isClarifiedFlag = doubt ? doubt.is_clarified === true : false;

        if (isClarifiedFlag || statusNum === 3 || String(rawStatus).toLowerCase() === "completed" || String(rawStatus).toLowerCase() === "clarified") {
            return { label: "✓ Completed", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", accent: "#16a34a", isCompleted: true };
        }
        if (statusNum === 2 || String(rawStatus).toLowerCase() === "reviewing" || String(rawStatus).toLowerCase() === "inprocess") {
            return { label: "🔍 Reviewing", color: "#0284c7", bg: "#f0f9ff", border: "#bae6fd", accent: "#0284c7", isCompleted: false };
        }
        if (statusNum === 4 || String(rawStatus).toLowerCase() === "irrelevant") {
            return { label: "⚠️ Irrelevant", color: "#64748b", bg: "#f8fafc", border: "#cbd5e1", accent: "#94a3b8", isCompleted: false };
        }
        // Default 1: Pending
        return { label: "⏳ Pending", color: "#ea580c", bg: "#fff7ed", border: "#fed7aa", accent: "#f97316", isCompleted: false };
    };

    renderStudentDoubtsCountGraph = (subjectCountData, totalDisplayed) => {
        if (!subjectCountData || subjectCountData.length === 0) return null;

        return (
            <div className="mb-3 p-3" style={{ background: "#fafafa", borderRadius: 12, border: "1px solid #f1f5f9" }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", margin: 0 }}>
                        <i className="fal fa-chart-bar me-1 text-primary" /> Subject-wise Doubt Counts Overview
                    </h6>
                    <span style={{ fontSize: 11.5, color: "#64748b", fontWeight: 600 }}>
                        {totalDisplayed} Doubts
                    </span>
                </div>
                <div style={{ width: "100%", height: 210 }}>
                    <ResponsiveContainer>
                        <BarChart data={subjectCountData} margin={{ top: 10, right: 15, left: -15, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                            <XAxis dataKey="subject" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
                            <Legend wrapperStyle={{ fontSize: 11.5 }} />
                            <Bar dataKey="pending" name="Pending Doubts" fill="#ea580c" radius={[6, 6, 0, 0]} maxBarSize={36} />
                            <Bar dataKey="clarified" name="Clarified Doubts" fill="#16a34a" radius={[6, 6, 0, 0]} maxBarSize={36} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    };

    renderStudentDoubtsSection = () => {
        const {
            studentDoubts,
            studentDoubtsLoading,
            studentDoubtsError,
            studentDoubtsFilter,
            studentDoubtsSubjectFilter,
            studentDoubtsView,
            selectedDoubtModal,
        } = this.state;

        // Extract available unique subjects for filtering
        const subjectOptions = [
            ...new Set(studentDoubts.map((d) => d.subject_name).filter(Boolean)),
        ];

        // Filter and sort doubts by latest raised_time/id descending
        const displayedDoubts = studentDoubts
            .filter((d) => {
                if (studentDoubtsSubjectFilter && d.subject_name !== studentDoubtsSubjectFilter) {
                    return false;
                }
                const statusInfo = this.getDoubtStatusInfo(d);
                if (studentDoubtsFilter === "pending") {
                    return !statusInfo.isCompleted;
                }
                if (studentDoubtsFilter === "clarified") {
                    return statusInfo.isCompleted;
                }
                return true;
            })
            .sort((a, b) => {
                const timeA = a.raised_time ? moment(a.raised_time).unix() : Number(a.id) || 0;
                const timeB = b.raised_time ? moment(b.raised_time).unix() : Number(b.id) || 0;
                return timeB - timeA;
            });

        // Subject count aggregation for top count graph
        const subjectMap = {};
        displayedDoubts.forEach((d) => {
            const subName = d.subject_name || "General";
            if (!subjectMap[subName]) {
                subjectMap[subName] = { subject: subName, pending: 0, clarified: 0, total: 0 };
            }
            const statusInfo = this.getDoubtStatusInfo(d);
            if (statusInfo.isCompleted) {
                subjectMap[subName].clarified += 1;
            } else {
                subjectMap[subName].pending += 1;
            }
            subjectMap[subName].total += 1;
        });
        const subjectCountData = Object.values(subjectMap);

        const pendingCount = studentDoubts.filter((d) => !this.getDoubtStatusInfo(d).isCompleted).length;
        const clarifiedCount = studentDoubts.filter((d) => this.getDoubtStatusInfo(d).isCompleted).length;

        const getImgUrl = (path) => {
            if (!path) return null;
            if (path.startsWith("http://") || path.startsWith("https://")) return path;
            return `https://upload.rizee.in/${path}`;
        };

        return (
            <Card className="modern-card border-0 shadow-sm mb-3 mt-3">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: 10 }}>
                        <div className="d-flex align-items-center" style={{ gap: 10 }}>
                            <div
                                className="lec-stat-icon"
                                style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 10,
                                    background: "linear-gradient(135deg, #ede9fe, #ddd6fe)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}
                            >
                                <i className="fal fa-question-circle" style={{ color: "#7c3aed", fontSize: 18 }} />
                            </div>
                            <div>
                                <h5 className="font-weight-bold text-dark mb-0">Student Doubts &amp; Clarifications</h5>
                                <span className="text-muted" style={{ fontSize: 11.5 }}>
                                    Assigned Lecturer &amp; Class Scope Doubts
                                </span>
                            </div>
                        </div>

                        <div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
                            {subjectOptions.length > 0 && (
                                <select
                                    className="form-select form-select-sm"
                                    value={studentDoubtsSubjectFilter}
                                    onChange={(e) => this.setState({ studentDoubtsSubjectFilter: e.target.value })}
                                    style={{ fontSize: 12, borderRadius: 8, padding: "4px 10px", width: "auto", minWidth: 130 }}
                                >
                                    <option value="">All Subjects</option>
                                    {subjectOptions.map((sub) => (
                                        <option key={sub} value={sub}>{sub}</option>
                                    ))}
                                </select>
                            )}

                            {/* Counts in Filter ButtonGroup */}
                            <div className="btn-group btn-group-sm" role="group">
                                <button
                                    type="button"
                                    className={`btn btn-sm ${studentDoubtsFilter === "all" ? "btn-primary font-weight-bold" : "btn-outline-primary"}`}
                                    style={{ fontSize: 11.5, padding: "4px 12px" }}
                                    onClick={() => this.setState({ studentDoubtsFilter: "all" })}
                                >
                                    <i className="fal fa-layer-group me-1" /> All ({studentDoubts.length})
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${studentDoubtsFilter === "pending" ? "btn-warning text-white font-weight-bold" : "btn-outline-warning"}`}
                                    style={{ fontSize: 11.5, padding: "4px 12px" }}
                                    onClick={() => this.setState({ studentDoubtsFilter: "pending" })}
                                >
                                    <i className="fal fa-clock me-1" /> Pending ({pendingCount})
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${studentDoubtsFilter === "clarified" ? "btn-success font-weight-bold" : "btn-outline-success"}`}
                                    style={{ fontSize: 11.5, padding: "4px 12px" }}
                                    onClick={() => this.setState({ studentDoubtsFilter: "clarified" })}
                                >
                                    <i className="fal fa-check me-1" /> Clarified ({clarifiedCount})
                                </button>
                            </div>

                            {/* End ButtonGroup: Table | Graph Toggle */}
                            <div className="btn-group btn-group-sm" role="group">
                                <button
                                    type="button"
                                    className={`btn btn-sm ${studentDoubtsView !== "graph" ? "btn-primary font-weight-bold" : "btn-outline-primary"}`}
                                    style={{ fontSize: 11.5, padding: "4px 12px" }}
                                    onClick={() => this.setState({ studentDoubtsView: "table" })}
                                >
                                    <i className="fal fa-table me-1" /> Table
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${studentDoubtsView === "graph" ? "btn-primary font-weight-bold" : "btn-outline-primary"}`}
                                    style={{ fontSize: 11.5, padding: "4px 12px" }}
                                    onClick={() => this.setState({ studentDoubtsView: "graph" })}
                                >
                                    <i className="fal fa-chart-bar me-1" /> Graph
                                </button>
                            </div>
                        </div>
                    </div>

                    {studentDoubtsLoading ? (
                        <Row className="g-3">
                            {[1, 2, 3].map((n) => (
                                <Col xs={12} md={4} key={n}>
                                    <div className="lecture-exam-shim w-100" style={{ height: 160, borderRadius: 12 }} />
                                </Col>
                            ))}
                        </Row>
                    ) : studentDoubtsError ? (
                        <p className="text-danger mb-0" style={{ fontSize: 13 }}>Unable to load student doubts.</p>
                    ) : displayedDoubts.length === 0 ? (
                        <div className="text-center py-4" style={{ background: "#f8fafc", borderRadius: 12, border: "1px dashed #cbd5e1" }}>
                            <i className="fal fa-comment-alt-check text-muted mb-2" style={{ fontSize: 32 }} />
                            <p className="text-muted mb-0" style={{ fontSize: 13, fontWeight: 600 }}>
                                No student doubts found for the selected filter.
                            </p>
                        </div>
                    ) : studentDoubtsView === "graph" ? (
                        this.renderStudentDoubtsCountGraph(subjectCountData, displayedDoubts.length)
                    ) : (
                        <CardLessDataTableWithSearch
                            parentData={displayedDoubts}
                            particlecolumns={[
                                {
                                    dataField: "name",
                                    text: "Student",
                                    sort: true,
                                    formatter: (cell, row) => {
                                        const statusInfo = this.getDoubtStatusInfo(row);
                                        return (
                                            <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
                                                <div style={{ width: 28, height: 28, borderRadius: "50%", background: statusInfo.bg, color: statusInfo.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, border: `1px solid ${statusInfo.border}`, flexShrink: 0 }}>
                                                    {(cell || "S").charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 700, fontSize: 13, color: "#0f172a", lineHeight: 1.2 }}>{cell || "Student"}</div>
                                                    <div style={{ fontSize: 10.5, color: "#64748b", fontWeight: 500 }}>
                                                        {row.class_name ? `${row.class_name}${row.section_name ? ` · ${row.section_name}` : ""}` : row.branch_name || "Assigned Batch"}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    },
                                },
                                {
                                    dataField: "subject_name",
                                    text: "Subject & Chapter",
                                    sort: true,
                                    formatter: (cell, row) => (
                                        <div className="d-flex flex-column" style={{ gap: 3 }}>
                                            {cell && (
                                                <span style={{ background: "#f5f3ff", color: "#7c3aed", fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 6, width: "fit-content" }}>
                                                    📘 {cell}
                                                </span>
                                            )}
                                            {row.chapter_name && (
                                                <span style={{ background: "#f0f9ff", color: "#0369a1", fontSize: 10.5, fontWeight: 600, padding: "2px 7px", borderRadius: 6, width: "fit-content" }}>
                                                    📖 {row.chapter_name}
                                                </span>
                                            )}
                                        </div>
                                    ),
                                },
                                {
                                    dataField: "title",
                                    text: "Doubt Question",
                                    formatter: (cell, row) => {
                                        const questionText = cell || row.description || "Student Question";
                                        return (
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#0f172a", maxWidth: 340, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                                {questionText}
                                            </div>
                                        );
                                    },
                                },
                                {
                                    dataField: "doubt_status",
                                    text: "Status",
                                    sort: true,
                                    formatter: (cell, row) => {
                                        const statusInfo = this.getDoubtStatusInfo(row);
                                        return (
                                            <span style={{ background: statusInfo.bg, color: statusInfo.color, fontWeight: 700, fontSize: 11, padding: "3px 10px", borderRadius: 12, border: `1px solid ${statusInfo.border}`, display: "inline-block" }}>
                                                {statusInfo.label}
                                            </span>
                                        );
                                    },
                                },
                                {
                                    dataField: "raised_time",
                                    text: "Raised Time",
                                    sort: true,
                                    formatter: (cell) => (
                                        <span className="text-muted" style={{ fontSize: 11.5, fontWeight: 500 }}>
                                            <i className="fal fa-clock me-1" />
                                            {cell ? moment(cell).fromNow() : "Recently"}
                                        </span>
                                    ),
                                },
                                {
                                    dataField: "id",
                                    text: "Action",
                                    formatter: (cell, row) => (
                                        // Deep link into the Doubts Inbox with THIS doubt opened — the id
                                        // travels as a query param so the link survives a refresh/share.
                                        <Link
                                            to={`/student-doubts?doubtId=${row.id}`}
                                            className="btn btn-sm btn-outline-primary"
                                            style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 6 }}
                                        >
                                            View Details <i className="fal fa-arrow-right ms-1" />
                                        </Link>
                                    ),
                                },
                            ]}
                            defaultSorted={[{ dataField: "id", order: "desc" }]}
                            name="student_doubts_list"
                            placeholder="Search student or doubt..."
                        />
                    )}
                </Card.Body>

                {/* Detail Modal for full Doubt & Answer */}
                <Modal
                    show={!!selectedDoubtModal}
                    onHide={() => this.setState({ selectedDoubtModal: null })}
                    centered
                    size="lg"
                    dialogClassName="lecturer-dashboard-modern"
                >
                    {selectedDoubtModal && (
                        <>
                            <Modal.Header closeButton style={{ background: "linear-gradient(135deg, #1e1b4b, #312e81)", color: "#fff" }}>
                                <Modal.Title style={{ fontSize: 16, fontWeight: 800 }} className="text-white">
                                    <i className="fal fa-comments-question me-2" /> Student Doubt Details
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body style={{ background: "#f8fafc", padding: 20 }}>
                                <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap" style={{ gap: 10 }}>
                                    <div>
                                        <h5 style={{ fontWeight: 800, color: "#0f172a", marginBottom: 2 }}>{selectedDoubtModal.title || "Student Doubt"}</h5>
                                        <div className="d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: 12.5, color: "#475569" }}>
                                            <span><strong>Student:</strong> {selectedDoubtModal.name || "-"}</span>
                                            {selectedDoubtModal.mobile && <span>· <strong>Mobile:</strong> {selectedDoubtModal.mobile}</span>}
                                            {selectedDoubtModal.class_name && <span>· <strong>Class:</strong> {selectedDoubtModal.class_name} {selectedDoubtModal.section_name}</span>}
                                        </div>
                                    </div>
                                    <span style={{ background: selectedDoubtModal.is_clarified || selectedDoubtModal.doubt_status === "Completed" ? "#dcfce7" : "#ffedd5", color: selectedDoubtModal.is_clarified || selectedDoubtModal.doubt_status === "Completed" ? "#15803d" : "#ea580c", fontWeight: 800, fontSize: 12, padding: "4px 12px", borderRadius: 20, border: `1px solid ${selectedDoubtModal.is_clarified || selectedDoubtModal.doubt_status === "Completed" ? "#bbf7d0" : "#fed7aa"}` }}>
                                        {selectedDoubtModal.is_clarified || selectedDoubtModal.doubt_status === "Completed" ? "✓ Clarified" : "⏳ Pending Response"}
                                    </span>
                                </div>

                                <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: 12 }}>
                                    <Card.Body>
                                        <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
                                            {selectedDoubtModal.subject_name && (
                                                <span style={{ background: "#f5f3ff", color: "#7c3aed", fontWeight: 700, fontSize: 11.5, padding: "3px 10px", borderRadius: 8 }}>
                                                    📘 {selectedDoubtModal.subject_name}
                                                </span>
                                            )}
                                            {selectedDoubtModal.chapter_name && (
                                                <span style={{ background: "#ecfeff", color: "#0891b2", fontWeight: 700, fontSize: 11.5, padding: "3px 10px", borderRadius: 8 }}>
                                                    📖 {selectedDoubtModal.chapter_name}
                                                </span>
                                            )}
                                            <span className="text-muted ms-auto" style={{ fontSize: 11.5 }}>
                                                Raised: {selectedDoubtModal.raised_time ? moment(selectedDoubtModal.raised_time).format("DD MMM YYYY, h:mm A") : "-"}
                                            </span>
                                        </div>

                                        <p style={{ fontSize: 13.5, color: "#334155", lineHeight: 1.6, whiteSpace: "pre-line" }}>
                                            {selectedDoubtModal.description || selectedDoubtModal.title}
                                        </p>

                                        {selectedDoubtModal.image_path && (
                                            <div className="mt-3 text-center" style={{ background: "#f1f5f9", padding: 10, borderRadius: 10 }}>
                                                <img
                                                    src={getImgUrl(selectedDoubtModal.image_path)}
                                                    alt="Attached Question"
                                                    style={{ maxHeight: 350, maxWidth: "100%", borderRadius: 8, objectFit: "contain" }}
                                                />
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>

                                {(selectedDoubtModal.answer_text || selectedDoubtModal.answer_image) && (
                                    <Card className="border-0 shadow-sm" style={{ borderRadius: 12, background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                                        <Card.Body>
                                            <h6 style={{ color: "#16a34a", fontWeight: 800, fontSize: 13.5 }} className="mb-2">
                                                <i className="fal fa-check-circle me-1" /> Answer / Faculty Clarification
                                            </h6>
                                            {selectedDoubtModal.answered_by && (
                                                <p style={{ fontSize: 11.5, color: "#15803d", fontWeight: 600 }} className="mb-2">
                                                    Answered by: {selectedDoubtModal.answered_by} {selectedDoubtModal.answered_time && `on ${moment(selectedDoubtModal.answered_time).format("DD MMM YYYY, h:mm A")}`}
                                                </p>
                                            )}
                                            {selectedDoubtModal.answer_text && (
                                                <p style={{ fontSize: 13, color: "#14532d", lineHeight: 1.5, whiteSpace: "pre-line" }}>
                                                    {selectedDoubtModal.answer_text}
                                                </p>
                                            )}
                                            {selectedDoubtModal.answer_image && (
                                                <div className="mt-2 text-center" style={{ background: "#fff", padding: 8, borderRadius: 8 }}>
                                                    <img
                                                        src={getImgUrl(selectedDoubtModal.answer_image)}
                                                        alt="Answer attachment"
                                                        style={{ maxHeight: 300, maxWidth: "100%", borderRadius: 6, objectFit: "contain" }}
                                                    />
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                )}
                            </Modal.Body>
                            <Modal.Footer style={{ background: "#fff" }}>
                                <button className="btn btn-secondary btn-sm" onClick={() => this.setState({ selectedDoubtModal: null })}>
                                    Close
                                </button>
                            </Modal.Footer>
                        </>
                    )}
                </Modal>
            </Card>
        );
    };

    openAttendanceModal = (cardType) => {
        this.setState(
            {
                showAttendanceModal: true,
                activeCard: cardType,
                modalClassId: "",
                modalSectionId: "",
                modalFromDate: this.state.todayUnix,
                modalToDate: this.state.todayUnix,
                modalStatusFilter: "",
            },
            this.fetchModalDetails
        );
    };

    closeAttendanceModal = () => {
        this.setState({ showAttendanceModal: false });
    };

    handleSummaryCardClick = (card) => {
        if (!card.clickable || this.state.attendanceSummaryLoading) return;
        if (card.key === "classes" || card.key === "sections" || card.key === "class_sections") {
            this.openClassSectionModal("class_sections");
        } else if (card.key === "highest_marks") {
            this.openHighestMarksModal();
        } else {
            this.openAttendanceModal(card.key);
        }
    };

    openClassSectionModal = (type) => {
        this.setState({ classSectionModalShow: true, classSectionModalType: type });
    };

    closeClassSectionModal = () => {
        this.setState({ classSectionModalShow: false });
    };

    openHighestMarksModal = () => {
        const { examReports } = this.state;
        const highestMarksExam = examReports.length > 0
            ? examReports.reduce((prev, curr) => {
                const pVal = Number(prev.highPct || prev.highMarks || 0);
                const cVal = Number(curr.highPct || curr.highMarks || 0);
                return cVal > pVal ? curr : prev;
            }, examReports[0])
            : null;

        if (highestMarksExam) {
            this.openExamDetailsModal(highestMarksExam);
        }
    };

    closeHighestMarksModal = () => {
        this.setState({ highestMarksModalShow: false });
    };

    openExamDetailsModal = (examRow) => {
        this.setState({ examDetailsModalShow: true, selectedExamForDetails: examRow });
    };

    closeExamDetailsModal = () => {
        this.setState({ examDetailsModalShow: false, selectedExamForDetails: null });
    };

    fetchModalDetails = async () => {
        const {
            academicYearId,
            branchId,
            modalFromDate,
            modalToDate,
            modalClassId,
            modalSectionId,
            authorizedClassIdsCsv,
            authorizedSectionIdsCsv,
        } = this.state;
        if (!academicYearId || !branchId) return;

        const cacheKey = `${modalFromDate}-${modalToDate}|${modalClassId}|${modalSectionId}`;
        if (this.detailsCache[cacheKey]) {
            this.setState({ modalDetails: this.detailsCache[cacheKey], modalLoading: false, modalError: false });
            return;
        }

        this.setState({ modalLoading: true, modalError: false });
        try {
            const result = await this.props.client.query({
                query: GET_STUDENT_ATTENDANCE_REPORT_DETAILS,
                variables: {
                    academic_year_id: academicYearId,
                    institute_id: parseInt(Cookies.get("institutionid")),
                    branch_id: parseInt(branchId),
                    class_id: modalClassId ? String(modalClassId) : (authorizedClassIdsCsv || undefined),
                    section_id: modalSectionId ? String(modalSectionId) : (authorizedSectionIdsCsv || undefined),
                    from_date: String(modalFromDate),
                    to_date: String(modalToDate),
                },
                fetchPolicy: "network-only",
            });
            const details = result.data.getStudentAttendanceReportDetails || [];
            this.detailsCache[cacheKey] = details;
            this.setState((s) => ({
                modalDetails: details,
                modalLoading: false,
                totalStudents: details.length > s.totalStudents ? details.length : s.totalStudents,
            }));
        } catch (err) {
            console.error("LectureDashboardSection fetchModalDetails error", err);
            this.setState({ modalError: true, modalLoading: false });
        }
    };

    handleModalClassChange = (name, value) => {
        this.setState({ modalClassId: value || "", modalSectionId: "" }, this.fetchModalDetails);
    };

    handleModalSectionChange = (name, value) => {
        this.setState({ modalSectionId: value || "" }, this.fetchModalDetails);
    };

    // DateBlock's applyCallback fires with moment start/end when the user hits Apply
    // in the range picker — mirrors startDateCalender/endDateCalender in attendanceReport.jsx.
    handleModalDateChange = (startDate, endDate) => {
        const modalFromDate = moment(startDate).startOf("day").unix();
        const modalToDate = moment(endDate).startOf("day").unix();
        this.setState({ modalFromDate, modalToDate }, this.fetchModalDetails);
    };

    // Status filter is purely local — the full modalDetails list is already in state,
    // so narrowing to Present/Absent/Leave is just a setState, no refetch.
    handleStatusFilterChange = (e) => {
        this.setState({ modalStatusFilter: e.target.value });
    };

    resolveStudentStatus = (row) => {
        if (row.present > 0) return "Present";
        if (row.absent > 0) return "Absent";
        if (row.not_marked > 0) return "Leave";
        return "-";
    };

    statusBadge = (status) => {
        const style = STATUS_STYLES[status] || STATUS_STYLES["-"];
        return (
            <span
                style={{
                    background: style.background,
                    color: style.color,
                    fontWeight: 700,
                    fontSize: 12,
                    padding: "4px 10px",
                    borderRadius: 20,
                    display: "inline-block",
                }}
            >
                {status}
            </span>
        );
    };

    percentageBadge = (value) => {
        if (value == null) return "-";
        const color = value >= 75 ? "#15803d" : value >= 50 ? "#c2410c" : "#b91c1c";
        return <span style={{ color, fontWeight: 700 }}>{value}%</span>;
    };

    scheduleStatusBadge = (status) => {
        const style = SCHEDULE_STATUS_STYLES[status] || SCHEDULE_STATUS_STYLES.Upcoming;
        return (
            <span
                style={{
                    background: style.background,
                    color: style.color,
                    fontWeight: 700,
                    fontSize: 12,
                    padding: "3px 12px",
                    borderRadius: 20,
                    display: "inline-block",
                }}
            >
                {status}
            </span>
        );
    };

    renderAssignmentPillGroup = (title, items, colors) => (
        <div className="mb-3">
            <p className="text-muted mb-2" style={{ fontSize: 12.5, fontWeight: 700 }}>{title}</p>
            {items.length === 0 ? (
                <p className="text-muted mb-0" style={{ fontSize: 12.5 }}>None assigned.</p>
            ) : (
                <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                    {items.map((item) => (
                        <span
                            key={item.id}
                            style={{
                                background: colors.bg,
                                color: colors.color,
                                fontWeight: 600,
                                fontSize: 12,
                                padding: "4px 10px",
                                borderRadius: 20,
                            }}
                        >
                            {item.label}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );

    openStudentActionModal = (student, type) => {
        this.setState({
            studentActionModalShow: true,
            selectedStudentForAction: student,
            studentActionType: type,
            actionNotificationSent: false,
        });
    };

    closeStudentActionModal = () => {
        this.setState({
            studentActionModalShow: false,
            selectedStudentForAction: null,
            studentActionType: "",
            actionNotificationSent: false,
        });
    };

    handleSendStudentActionNotification = () => {
        this.setState({ actionNotificationSent: true });
        setTimeout(() => {
            this.setState({ actionNotificationSent: false });
        }, 3000);
    };

    // Modules this lecturer is actually granted (is_view), each mapped to its route —
    // reuses the same visibility map the sidebar renders from (buildModuleVisibility)
    // and the same route table the URL guard checks (MODULE_ROUTES), so this list can
    // never show a module the lecturer can't actually open.
    getAccessibleModules = () => {
        const visibility = buildModuleVisibility();

        let roleAccess = [];
        try {
            roleAccess = JSON.parse(localStorage.getItem("roleModuleAccess")) || [];
        } catch (err) {
            roleAccess = [];
        }
        const labelByKey = {};
        (roleAccess || []).forEach((row) => {
            const key = row.module_key || row.module_name;
            if (key && !labelByKey[key]) {
                labelByKey[key] = row.module_name || key.replace(/_/g, " ");
            }
        });

        // The Dashboard/Lecture_Dashboard entry is this page itself — no point listing
        // it as a "module to redirect to" in its own module grid.
        const seenPaths = new Set(["/dashboard", "/lecture-dashboard"]);
        const seenLabels = new Set();
        const modules = [];
        MODULE_ROUTES.forEach((route) => {
            const grantedKey = route.keys.find((k) => visibility[k] === false);
            if (!grantedKey || seenPaths.has(route.path)) return;
            seenPaths.add(route.path);

            const meta = ROUTE_MODULE_META[route.path];
            const rawLabel = meta ? meta.label : (labelByKey[grantedKey] || grantedKey.replace(/_/g, " "));

            // Deduplicate by title label so every card in My Access Modules has a unique title
            if (seenLabels.has(rawLabel)) return;
            seenLabels.add(rawLabel);

            const palette = meta || MODULE_ICON_PALETTE[modules.length % MODULE_ICON_PALETTE.length];

            modules.push({
                path: route.path,
                label: rawLabel,
                icon: palette.icon || "fal fa-th-large",
                color: palette.color || "#2563eb",
                bg: palette.bg || "#eff6ff",
                desc: palette.desc || "Click below to open this module.",
            });
        });
        return modules;
    };

    render() {
        const {
            exams,
            selectedExamId,
            selectedExamName,
            examsLoading,
            examsError,
            currentDateTime,
            branchId,
            totalStudents,
            averageAttendance,
            presentCount,
            absentCount,
            notMarkedCount,
            attendanceSummaryLoading,
            attendanceSummaryError,
            attendanceCardClassId,
            attendanceCardSectionId,
            weeklySchedule,
            weeklyScheduleLoading,
            weeklyScheduleError,
            scheduleModalShow,
            scheduleModalDate,
            scheduleModalPeriods,
            scheduleModalLoading,
            scheduleModalError,
            scheduleModalClassId,
            scheduleModalSectionId,
            showAttendanceModal,
            activeCard,
            classOptions,
            allSectionOptions,
            classMap,
            modalClassId,
            modalSectionId,
            modalFromDate,
            modalToDate,
            modalStatusFilter,
            modalDetails,
            modalLoading,
            modalError,
            lecturerAssignments,
            performanceView,
            subjectPerformance,
            subjectPerformanceLoading,
            subjectPerformanceError,
            classPerformance,
            classPerformanceLoading,
            classPerformanceError,
            sectionPerformance,
            sectionPerformanceLoading,
            sectionPerformanceError,
            topStudents,
            topStudentsLoading,
            topStudentsError,
            attentionStudents,
            attentionStudentsLoading,
            attentionStudentsError,
            studentDoubts,
            examReports,
            examReportsLoading,
            examReportsError,
            classSectionModalShow,
            classSectionModalType,
            highestMarksModalShow,
            highestMarksExamDetail,
            highestMarksDetailLoading,
            examDetailsModalShow,
            selectedExamForDetails,
        } = this.state;
        const greetingWord = this.getGreeting();
        const lecturerName = Cookies.get("name") || "Lecturer";
        const formattedDate = currentDateTime.toLocaleDateString("en-US", {
            weekday: "long",
            day: "numeric",
            month: "short",
            year: "numeric",
        });
        const formattedTime = currentDateTime.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
        });

        const sectionOptionsForModal = modalClassId
            ? allSectionOptions.filter((s) => String(s.class_id) === String(modalClassId))
            : allSectionOptions;

        const { slots: weeklySlots, grid: weeklyGrid } = this.buildWeeklyGrid(weeklySchedule);
        const todaySchedule = this.getTodaySchedule();
        const filteredScheduleModalPeriods = this.filterScheduleByClassSection(scheduleModalPeriods);
        const sectionOptionsForScheduleModal = scheduleModalClassId
            ? allSectionOptions.filter((s) => String(s.class_id) === String(scheduleModalClassId))
            : allSectionOptions;
        const modalDateDisplay =
            modalFromDate === modalToDate
                ? moment.unix(modalFromDate).format("DD/MM/YYYY")
                : `${moment.unix(modalFromDate).format("DD/MM/YYYY")} - ${moment.unix(modalToDate).format("DD/MM/YYYY")}`;
        const tableRows = modalDetails.map((row) => ({
            ...row,
            class_name: classMap[row.class_id] || row.class_id,
            section_name:
                (allSectionOptions.find((s) => String(s.value) === String(row.section_id)) || {}).label ||
                row.section_id,
            status: this.resolveStudentStatus(row),
            date_display: modalDateDisplay,
        }));

        // Counts always reflect the full fetched list (not the status filter below), so
        // the lecturer can see totals for every status while filtering to just one.
        const statusCounts = tableRows.reduce(
            (acc, row) => {
                acc[row.status] = (acc[row.status] || 0) + 1;
                return acc;
            },
            { Present: 0, Absent: 0, Leave: 0 }
        );

        const filteredTableRows = modalStatusFilter
            ? tableRows.filter((row) => row.status === modalStatusFilter)
            : tableRows;

        const tableColumns = [
            { dataField: "student_name", text: "Student Name", sort: true },
            {
                dataField: "admission_no",
                text: "Admission No / Roll No",
                formatter: (cell, row) => row.admission_no || row.roll_no || "-",
            },
            { dataField: "class_name", text: "Class", sort: true },
            { dataField: "section_name", text: "Section", sort: true },
            { dataField: "status", text: "Status", sort: true, formatter: (cell) => this.statusBadge(cell) },
            {
                dataField: "attendance_percentage",
                text: "Attendance %",
                sort: true,
                formatter: (cell) => this.percentageBadge(cell),
            },
            { dataField: "date_display", text: "Date" },
        ];

        const sectionOptionsForAttendanceCard = attendanceCardClassId
            ? allSectionOptions.filter((s) => String(s.class_id) === String(attendanceCardClassId))
            : allSectionOptions;

        const subjectPerformanceTableColumns = [
            {
                dataField: "subject_name",
                text: "Subject",
                sort: true,
                formatter: (cell) => (
                    <span style={{ background: "#eff6ff", color: "#1d4ed8", padding: "3px 8px", borderRadius: 6, fontWeight: 700, fontSize: 11.5, display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <i className="fal fa-book" style={{ fontSize: 11 }} /> {cell || "-"}
                    </span>
                ),
            },
            {
                dataField: "average_marks",
                text: "Avg Marks",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 800, color: "#4f46e5", background: "#eef2ff", border: "1px solid #c7d2fe", padding: "3px 8px", borderRadius: 6, fontSize: 11.5 }}>
                        {cell ?? "-"}
                    </span>
                ),
            },
            {
                dataField: "average_accuracy",
                text: "Accuracy",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 700, color: "#0891b2", background: "#ecfeff", border: "1px solid #a5f3fc", padding: "3px 8px", borderRadius: 6, fontSize: 11.5 }}>
                        {cell != null ? `${cell}%` : "-"}
                    </span>
                ),
            },
            {
                dataField: "highest_marks",
                text: "Highest",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 700, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "3px 7px", borderRadius: 6, fontSize: 11 }}>
                        ↑ {cell ?? "-"}
                    </span>
                ),
            },
            {
                dataField: "lowest_marks",
                text: "Lowest",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 700, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", padding: "3px 7px", borderRadius: 6, fontSize: 11 }}>
                        ↓ {cell ?? "-"}
                    </span>
                ),
            },
            {
                dataField: "pass_percentage",
                text: "Pass %",
                sort: true,
                formatter: (cell) => {
                    const val = Number(cell) || 0;
                    const isGood = val >= 75;
                    return (
                        <span style={{ fontWeight: 800, color: isGood ? "#15803d" : "#b91c1c", background: isGood ? "#dcfce7" : "#fee2e2", border: `1px solid ${isGood ? "#86efac" : "#fca5a5"}`, padding: "3px 8px", borderRadius: 12, fontSize: 11.5 }}>
                            {val}%
                        </span>
                    );
                },
            },
            {
                dataField: "attendance_percentage",
                text: "Attendance",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 700, color: "#1d4ed8", background: "#eff6ff", border: "1px solid #bfdbfe", padding: "3px 8px", borderRadius: 6, fontSize: 11.5 }}>
                        {cell != null ? `${cell}%` : "-"}
                    </span>
                ),
            },
        ];
        // CardLessDataTableWithSearch hardcodes keyField="id" — subjectPerformance rows
        // only carry subject_id from the API.
        const subjectPerformanceRows = subjectPerformance.map((s) => ({ ...s, id: s.subject_id }));

        const classPerformanceTableColumns = [
            {
                dataField: "class_name",
                text: "Class",
                sort: true,
                formatter: (cell) => (
                    <span style={{ background: "#f5f3ff", color: "#7c3aed", padding: "3px 8px", borderRadius: 6, fontWeight: 700, fontSize: 11.5, display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <i className="fal fa-chalkboard" style={{ fontSize: 11 }} /> {cell || "-"}
                    </span>
                ),
            },
            {
                dataField: "students",
                text: "Students",
                sort: true,
                formatter: (cell) => (
                    <span style={{ background: "#f8fafc", color: "#475569", border: "1px solid #e2e8f0", padding: "3px 8px", borderRadius: 12, fontWeight: 700, fontSize: 11.5 }}>
                        {cell ?? "-"}
                    </span>
                ),
            },
            {
                dataField: "average_marks",
                text: "Avg Marks",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 800, color: "#4f46e5", background: "#eef2ff", border: "1px solid #c7d2fe", padding: "3px 8px", borderRadius: 6, fontSize: 11.5 }}>
                        {cell ?? "-"}
                    </span>
                ),
            },
            {
                dataField: "average_accuracy",
                text: "Accuracy",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 700, color: "#0891b2", background: "#ecfeff", border: "1px solid #a5f3fc", padding: "3px 8px", borderRadius: 6, fontSize: 11.5 }}>
                        {cell != null ? `${cell}%` : "-"}
                    </span>
                ),
            },
            {
                dataField: "highest_marks",
                text: "Highest",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 700, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "3px 7px", borderRadius: 6, fontSize: 11 }}>
                        ↑ {cell ?? "-"}
                    </span>
                ),
            },
            {
                dataField: "lowest_marks",
                text: "Lowest",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 700, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", padding: "3px 7px", borderRadius: 6, fontSize: 11 }}>
                        ↓ {cell ?? "-"}
                    </span>
                ),
            },
            {
                dataField: "pass_percentage",
                text: "Pass %",
                sort: true,
                formatter: (cell) => {
                    const val = Number(cell) || 0;
                    const isGood = val >= 75;
                    return (
                        <span style={{ fontWeight: 800, color: isGood ? "#15803d" : "#b91c1c", background: isGood ? "#dcfce7" : "#fee2e2", border: `1px solid ${isGood ? "#86efac" : "#fca5a5"}`, padding: "3px 8px", borderRadius: 12, fontSize: 11.5 }}>
                            {val}%
                        </span>
                    );
                },
            },
            {
                dataField: "attendance_percentage",
                text: "Attendance",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 700, color: "#1d4ed8", background: "#eff6ff", border: "1px solid #bfdbfe", padding: "3px 8px", borderRadius: 6, fontSize: 11.5 }}>
                        {cell != null ? `${cell}%` : "-"}
                    </span>
                ),
            },
        ];
        // CardLessDataTableWithSearch hardcodes keyField="id" — classPerformance rows
        // only carry class_id from the API.
        const classPerformanceRows = classPerformance.map((c) => ({ ...c, id: c.class_id }));

        const sectionPerformanceTableColumns = [
            {
                dataField: "section_name",
                text: "Section",
                sort: true,
                formatter: (cell) => (
                    <span style={{ background: "#ecfeff", color: "#0891b2", padding: "3px 8px", borderRadius: 6, fontWeight: 700, fontSize: 11.5, display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <i className="fal fa-layer-group" style={{ fontSize: 11 }} /> {cell || "-"}
                    </span>
                ),
            },
            {
                dataField: "students",
                text: "Students",
                sort: true,
                formatter: (cell) => (
                    <span style={{ background: "#f8fafc", color: "#475569", border: "1px solid #e2e8f0", padding: "3px 8px", borderRadius: 12, fontWeight: 700, fontSize: 11.5 }}>
                        {cell ?? "-"}
                    </span>
                ),
            },
            {
                dataField: "average_marks",
                text: "Avg Marks",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 800, color: "#4f46e5", background: "#eef2ff", border: "1px solid #c7d2fe", padding: "3px 8px", borderRadius: 6, fontSize: 11.5 }}>
                        {cell ?? "-"}
                    </span>
                ),
            },
            {
                dataField: "average_accuracy",
                text: "Accuracy",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 700, color: "#0891b2", background: "#ecfeff", border: "1px solid #a5f3fc", padding: "3px 8px", borderRadius: 6, fontSize: 11.5 }}>
                        {cell != null ? `${cell}%` : "-"}
                    </span>
                ),
            },
            {
                dataField: "highest_marks",
                text: "Highest",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 700, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "3px 7px", borderRadius: 6, fontSize: 11 }}>
                        ↑ {cell ?? "-"}
                    </span>
                ),
            },
            {
                dataField: "lowest_marks",
                text: "Lowest",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 700, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", padding: "3px 7px", borderRadius: 6, fontSize: 11 }}>
                        ↓ {cell ?? "-"}
                    </span>
                ),
            },
            {
                dataField: "pass_percentage",
                text: "Pass %",
                sort: true,
                formatter: (cell) => {
                    const val = Number(cell) || 0;
                    const isGood = val >= 75;
                    return (
                        <span style={{ fontWeight: 800, color: isGood ? "#15803d" : "#b91c1c", background: isGood ? "#dcfce7" : "#fee2e2", border: `1px solid ${isGood ? "#86efac" : "#fca5a5"}`, padding: "3px 8px", borderRadius: 12, fontSize: 11.5 }}>
                            {val}%
                        </span>
                    );
                },
            },
            {
                dataField: "attendance_percentage",
                text: "Attendance",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 700, color: "#1d4ed8", background: "#eff6ff", border: "1px solid #bfdbfe", padding: "3px 8px", borderRadius: 6, fontSize: 11.5 }}>
                        {cell != null ? `${cell}%` : "-"}
                    </span>
                ),
            },
        ];
        // CardLessDataTableWithSearch hardcodes keyField="id" — sectionPerformance rows
        // only carry section_id from the API.
        const sectionPerformanceRows = sectionPerformance.map((s) => ({ ...s, id: s.section_id }));

        const topStudentsTableColumns = [
            {
                dataField: "rank",
                text: "Rank",
                sort: true,
                formatter: (cell) => {
                    const r = Number(cell);
                    if (r === 1) {
                        return (
                            <span style={{ background: "linear-gradient(135deg, #fef3c7, #fde68a)", color: "#92400e", border: "1px solid #fcd34d", padding: "4px 10px", borderRadius: 20, fontWeight: 800, fontSize: 11.5, display: "inline-flex", alignItems: "center", gap: 4 }}>
                                🥇 #1
                            </span>
                        );
                    }
                    if (r === 2) {
                        return (
                            <span style={{ background: "linear-gradient(135deg, #f1f5f9, #e2e8f0)", color: "#334155", border: "1px solid #cbd5e1", padding: "4px 10px", borderRadius: 20, fontWeight: 800, fontSize: 11.5, display: "inline-flex", alignItems: "center", gap: 4 }}>
                                🥈 #2
                            </span>
                        );
                    }
                    if (r === 3) {
                        return (
                            <span style={{ background: "linear-gradient(135deg, #ffedd5, #fed7aa)", color: "#9a3412", border: "1px solid #fdba74", padding: "4px 10px", borderRadius: 20, fontWeight: 800, fontSize: 11.5, display: "inline-flex", alignItems: "center", gap: 4 }}>
                                🥉 #3
                            </span>
                        );
                    }
                    return (
                        <span style={{ background: "#f8fafc", color: "#475569", border: "1px solid #e2e8f0", padding: "3px 9px", borderRadius: 20, fontWeight: 700, fontSize: 11.5 }}>
                            #{r || cell || "-"}
                        </span>
                    );
                },
            },
            {
                dataField: "student_name",
                text: "Student",
                sort: true,
                formatter: (cell, row) => (
                    <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 26, height: 26, borderRadius: "50%", background: "#f0fdf4", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, flexShrink: 0, border: "1px solid #bbf7d0" }}>
                            {(cell || "S").charAt(0).toUpperCase()}
                        </div>
                        <span>{cell}</span>
                    </div>
                ),
            },
            {
                dataField: "class_name",
                text: "Class",
                sort: true,
                formatter: (cell) => (
                    <span style={{ background: "#f5f3ff", color: "#7c3aed", padding: "3px 8px", borderRadius: 6, fontWeight: 600, fontSize: 11.5 }}>
                        {cell || "-"}
                    </span>
                ),
            },
            {
                dataField: "section_name",
                text: "Section",
                sort: true,
                formatter: (cell) => (
                    <span style={{ background: "#ecfeff", color: "#0891b2", padding: "3px 8px", borderRadius: 6, fontWeight: 600, fontSize: 11.5 }}>
                        {cell || "-"}
                    </span>
                ),
            },
            {
                dataField: "marks",
                text: "Avg Marks",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 800, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "3px 9px", borderRadius: 8, fontSize: 12 }}>
                        {cell ?? "-"}
                    </span>
                ),
            },
            {
                dataField: "accuracy",
                text: "Accuracy",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 700, color: "#0369a1", background: "#f0f9ff", border: "1px solid #bae6fd", padding: "3px 9px", borderRadius: 8, fontSize: 12 }}>
                        {cell}%
                    </span>
                ),
            },
            // {
            //     dataField: "attendance_percentage",
            //     text: "Attendance",
            //     sort: true,
            //     formatter: (cell, row) => {
            //         const val = Number(cell ?? row.attendance ?? row.attendance_pct) || 0;
            //         const isGood = val >= 75;
            //         return (
            //             <span style={{ fontWeight: 800, color: isGood ? "#15803d" : "#b91c1c", background: isGood ? "#f0fdf4" : "#fef2f2", border: `1px solid ${isGood ? "#bbf7d0" : "#fecaca"}`, padding: "3px 9px", borderRadius: 8, fontSize: 12 }}>
            //                 {val}%
            //             </span>
            //         );
            //     },
            // },
            // {
            //     dataField: "actions",
            //     text: "Action",
            //     formatter: (cell, row) => {
            //         const busy = this.state.reportCardBusyId === `top:${row.id}`;
            //         return (
            //             <button
            //                 className="btn btn-sm"
            //                 disabled={busy}
            //                 title="Print this student's Exam Report Card"
            //                 style={{ background: "#eef2ff", color: "#4f46e5", border: "1px solid #c7d2fe", borderRadius: 8, fontSize: 11.5, fontWeight: 700, padding: "4px 10px", whiteSpace: "nowrap" }}
            //                 onClick={() => this.printStudentReportCard(row, "top")}
            //             >
            //                 <i className={`fal ${busy ? "fa-spinner fa-spin" : "fa-print"} me-1`} />
            //                 {busy ? "Preparing..." : "Report"}
            //             </button>
            //         );
            //     },
            // },
            // {
            //     dataField: "actions",
            //     text: "Action",
            //     formatter: (cell, row) => (
            //         <button
            //             className="btn btn-sm"
            //             style={{ background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0", borderRadius: 8, fontSize: 11.5, fontWeight: 700, padding: "4px 10px" }}
            //             onClick={() => this.openStudentActionModal(row, "top")}
            //         >
            //             <i className="fal fa-trophy me-1" /> View Profile
            //         </button>
            //     ),
            // },
        ];
        // CardLessDataTableWithSearch hardcodes keyField="id" — top-students rows only
        // carry student_id from the API.
        const topStudentsRows = this.filterStudentsByAccess(topStudents).map((s, idx) => ({
            ...s,
            id: s.student_id || `top-${idx}`,
        }));

        const attentionStudentsTableColumns = [
            {
                dataField: "student_name",
                text: "Student",
                sort: true,
                formatter: (cell, row) => (
                    <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 26, height: 26, borderRadius: "50%", background: "#fef2f2", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, flexShrink: 0, border: "1px solid #fecaca" }}>
                            {(cell || "S").charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <div>{cell}</div>
                            {row.admission_no && <div style={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 500 }}>ID: {row.admission_no}</div>}
                        </div>
                    </div>
                ),
            },
            {
                dataField: "class_name",
                text: "Class",
                sort: true,
                formatter: (cell) => (
                    <span style={{ background: "#f5f3ff", color: "#7c3aed", padding: "3px 8px", borderRadius: 6, fontWeight: 600, fontSize: 11.5 }}>
                        {cell || "-"}
                    </span>
                ),
            },
            {
                dataField: "section_name",
                text: "Section",
                sort: true,
                formatter: (cell) => (
                    <span style={{ background: "#ecfeff", color: "#0891b2", padding: "3px 8px", borderRadius: 6, fontWeight: 600, fontSize: 11.5 }}>
                        {cell || "-"}
                    </span>
                ),
            },
            {
                dataField: "marks",
                text: "Avg Marks",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 800, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", padding: "3px 9px", borderRadius: 8, fontSize: 12 }}>
                        {cell ?? "-"}
                    </span>
                ),
            },
            {
                dataField: "accuracy",
                text: "Accuracy",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 700, color: "#c2410c", background: "#fff7ed", border: "1px solid #ffedd5", padding: "3px 9px", borderRadius: 8, fontSize: 12 }}>
                        {cell}%
                    </span>
                ),
            },
            // {
            //     dataField: "attendance_percentage",
            //     text: "Attendance",
            //     sort: true,
            //     formatter: (cell) => {
            //         const val = Number(cell) || 0;
            //         const isLow = val < 75;
            //         return (
            //             <span style={{ fontWeight: 700, color: isLow ? "#b91c1c" : "#15803d", background: isLow ? "#fef2f2" : "#f0fdf4", border: `1px solid ${isLow ? "#fecaca" : "#bbf7d0"}`, padding: "3px 9px", borderRadius: 8, fontSize: 12 }}>
            //                 {val}% {isLow ? "⚠" : ""}
            //             </span>
            //         );
            //     },
            // },
            // {
            //     dataField: "weak_subject",
            //     text: "Weak Subject",
            //     sort: true,
            //     formatter: (cell) => (
            //         <span style={{ fontWeight: 700, color: "#7c3aed", background: "#f5f3ff", border: "1px solid #ddd6fe", padding: "3px 8px", borderRadius: 6, fontSize: 11.5 }}>
            //             {/* <i className="fal fa-book-alt me-1" /> */}
            //             {cell || "Needs Review"}
            //         </span>
            //     ),
            // },
            // {
            //     dataField: "actions",
            //     text: "Action",
            //     formatter: (cell, row) => {
            //         const busy = this.state.reportCardBusyId === `attention:${row.id}`;
            //         return (
            //             <button
            //                 className="btn btn-sm"
            //                 disabled={busy}
            //                 title="Print this student's Exam Report Card"
            //                 style={{ background: "#eef2ff", color: "#4f46e5", border: "1px solid #c7d2fe", borderRadius: 8, fontSize: 11.5, fontWeight: 700, padding: "4px 10px", whiteSpace: "nowrap" }}
            //                 onClick={() => this.printStudentReportCard(row, "attention")}
            //             >
            //                 <i className={`fal ${busy ? "fa-spinner fa-spin" : "fa-print"} me-1`} />
            //                 {busy ? "Preparing..." : "Report"}
            //             </button>
            //         );
            //     },
            // },
            // {
            //     dataField: "actions",
            //     text: "Action",
            //     formatter: (cell, row) => (
            //         <button
            //             className="btn btn-sm"
            //             style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 8, fontSize: 11.5, fontWeight: 700, padding: "4px 10px" }}
            //             onClick={() => this.openStudentActionModal(row, "attention")}
            //         >
            //             <i className="fal fa-paper-plane me-1" /> Take Action
            //         </button>
            //     ),
            // },
        ];
        const attentionStudentsRows = this.filterStudentsByAccess(attentionStudents).map((s, idx) => ({
            ...s,
            id: s.student_id || `attn-${idx}`,
        }));

        const examReportsTableColumns = [
            {
                dataField: "name",
                text: "Exam Name",
                sort: true,
                formatter: (cell, row) => (
                    <div
                        role="button"
                        onClick={() => this.openExamDetailsModal(row)}
                        style={{ color: "#2563eb", fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}
                    >
                        <div style={{ width: 26, height: 26, borderRadius: 6, background: "#eff6ff", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <i className="fal fa-file-alt" style={{ fontSize: 12 }} />
                        </div>
                        <span style={{ textDecoration: "underline", textUnderlineOffset: 2 }}>{cell}</span>
                    </div>
                ),
            },
            {
                dataField: "className",
                text: "Class",
                sort: true,
                formatter: (cell) => (
                    <span style={{ background: "#f5f3ff", color: "#7c3aed", padding: "3px 8px", borderRadius: 6, fontWeight: 600, fontSize: 11.5 }}>
                        {cell || "-"}
                    </span>
                ),
            },
            {
                dataField: "date",
                text: "Start Date",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>
                        <i className="fal fa-calendar-alt me-1 text-muted" />
                        {formatReportDate(cell)}
                    </span>
                ),
            },
            {
                dataField: "endDate",
                text: "End Date",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>
                        <i className="fal fa-calendar-check me-1 text-muted" />
                        {formatReportDate(cell)}
                    </span>
                ),
            },
            {
                dataField: "appeared",
                text: "Appeared / Total",
                sort: true,
                formatter: (cell, row) => (
                    <span style={{ background: "#f1f5f9", color: "#334155", padding: "3px 9px", borderRadius: 12, fontWeight: 700, fontSize: 11.5 }}>
                        {cell ?? "-"} / {row.totalStudents ?? "-"}
                    </span>
                ),
            },
            {
                dataField: "avgPct",
                text: "Avg %",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 700, color: "#0369a1", background: "#f0f9ff", border: "1px solid #bae6fd", padding: "3px 8px", borderRadius: 6, fontSize: 12 }}>
                        {cell}%
                    </span>
                ),
            },
            {
                dataField: "highPct",
                text: "High %",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 800, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "3px 8px", borderRadius: 6, fontSize: 12 }}>
                        ↑ {cell}%
                    </span>
                ),
            },
            {
                dataField: "lowPct",
                text: "Low %",
                sort: true,
                formatter: (cell) => (
                    <span style={{ fontWeight: 700, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", padding: "3px 8px", borderRadius: 6, fontSize: 12 }}>
                        ↓ {cell}%
                    </span>
                ),
            },
            {
                dataField: "passPct",
                text: "Pass %",
                sort: true,
                formatter: (cell) => {
                    const val = Number(cell) || 0;
                    const isGood = val >= 75;
                    return (
                        <span style={{ fontWeight: 800, color: isGood ? "#15803d" : "#b91c1c", background: isGood ? "#dcfce7" : "#fee2e2", padding: "3px 8px", borderRadius: 12, fontSize: 12 }}>
                            {val}%
                        </span>
                    );
                },
            },
            {
                dataField: "actions",
                text: "Action",
                formatter: (cell, row) => (
                    <button
                        className="btn btn-sm"
                        style={{ background: "#eef2ff", color: "#4f46e5", border: "1px solid #c7d2fe", borderRadius: 8, fontSize: 11.5, fontWeight: 700, padding: "4px 10px" }}
                        onClick={() => this.openExamDetailsModal(row)}
                    >
                        <i className="fal fa-chart-pie me-1" /> View Details
                    </button>
                ),
            },
        ];

        const classSectionModalItems =
            classSectionModalType === "classes"
                ? classOptions.map((c) => ({ id: c.value, label: c.label }))
                : allSectionOptions.map((s) => ({ id: s.value, label: `${classMap[s.class_id] || ""}-${s.label}` }));

        const accessibleModules = this.getAccessibleModules();

        // Name-based authorization scope (these two APIs return names, not ids) for the
        // Exam Details modal's Student/Question analysis — same convention used for the
        // Top Performing Students / Students Needing Attention tables above.
        const authorizedClassNames = classOptions.map((c) => c.label);
        const authorizedSectionNames = allSectionOptions.map((s) => s.label);
        const authorizedSubjectNames = ((lecturerAssignments && lecturerAssignments.subjects) || []).map((s) => s.subject_name);

        // Primary/Success/Warning/Danger/Info — consistent Bootstrap-style semantic
        // color coding across the summary cards.
        const highestMarksExam = examReports.length > 0
            ? examReports.reduce((prev, curr) => {
                const pVal = Number(prev.highPct || prev.highMarks || 0);
                const cVal = Number(curr.highPct || curr.highMarks || 0);
                return cVal > pVal ? curr : prev;
            }, examReports[0])
            : null;

        const summaryCards = [
            {
                key: "students",
                icon: "fal fa-user-graduate",
                color: "#2563eb",
                background: "#eff6ff",
                title: "Total Students",
                value: totalStudents,
                status: totalStudents > 0 && (presentCount > 0 || absentCount > 0)
                    ? `${presentCount} Present · ${absentCount} Absent`
                    : "Active Enrolled",
                clickable: true,
            },
            {
                key: "attendance",
                icon: "fal fa-chart-line",
                color: averageAttendance >= 75 ? "#16a34a" : "#dc2626",
                background: averageAttendance >= 75 ? "#f0fdf4" : "#fef2f2",
                title: "Average Attendance",
                value: `${averageAttendance}%`,
                status: averageAttendance >= 75 ? "High Rate" : "Needs Attention",
                clickable: true,
            },
            {
                key: "class_sections",
                icon: "fal fa-chalkboard-teacher",
                color: "#7c3aed",
                background: "#f5f3ff",
                title: "Classes & Sections",
                value: `${classOptions.length} Classes · ${allSectionOptions.length} Sections`,
                status: "Class-wise Details",
                clickable: true,
            },
            {
                key: "highest_marks",
                icon: "fal fa-trophy-alt",
                color: "#d97706",
                background: "#fffbeb",
                title: "Highest Marks",
                value: highestMarksExam ? `${highestMarksExam.highPct}%` : "-",
                status: highestMarksExam ? (highestMarksExam.name.length > 18 ? highestMarksExam.name.substring(0, 18) + "..." : highestMarksExam.name) : "Top Score",
                clickable: true,
            },
        ];

        return (
            <Container fluid className="lecturer-dashboard-modern mt-4 mb-4">
                <style>{shimmerCss}</style>
                <Card
                    className="modern-card border-0 mb-3 overflow-hidden"
                    style={{
                        position: "relative",
                        background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #312e81 100%)",
                        borderRadius: 16,
                        boxShadow: "0 10px 30px -5px rgba(30, 27, 75, 0.35), 0 4px 12px rgba(0, 0, 0, 0.15)",
                        padding: "4px 6px",
                    }}
                >
                    {/* Ambient Glow Orbs in Background */}
                    <div
                        style={{
                            position: "absolute",
                            top: "-40px",
                            right: "-40px",
                            width: "200px",
                            height: "200px",
                            borderRadius: "50%",
                            background: "radial-gradient(circle, rgba(99, 102, 241, 0.35) 0%, rgba(0,0,0,0) 70%)",
                            filter: "blur(20px)",
                            pointerEvents: "none",
                        }}
                    />
                    <div
                        style={{
                            position: "absolute",
                            bottom: "-50px",
                            left: "10%",
                            width: "250px",
                            height: "150px",
                            borderRadius: "50%",
                            background: "radial-gradient(circle, rgba(56, 189, 248, 0.25) 0%, rgba(0,0,0,0) 70%)",
                            filter: "blur(25px)",
                            pointerEvents: "none",
                        }}
                    />

                    <Card.Body style={{ position: "relative", zIndex: 2, padding: "20px 24px" }}>
                        <Row className="align-items-center" style={{ position: "relative", zIndex: 1 }}>
                            <Col lg={7} className="d-flex flex-column justify-content-center mb-3 mb-lg-0">
                                <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
                                    {/* <span
                                        style={{
                                            background: "rgba(165, 180, 252, 0.18)",
                                            color: "#c7d2fe",
                                            border: "1px solid rgba(165, 180, 252, 0.3)",
                                            padding: "3px 12px",
                                            borderRadius: 20,
                                            fontSize: 11.5,
                                            fontWeight: 700,
                                            letterSpacing: "0.03em",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                        }}
                                    >
                                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#4ade80", boxShadow: "0 0 8px #4ade80" }} />
                                        Lecturer Live Portal
                                    </span> */}
                                </div>
                                <h3 className="font-weight-bold mb-1" style={{ color: "#fff", fontSize: 24, letterSpacing: "-0.3px" }}>
                                    {greetingWord},{" "}
                                    <span
                                        style={{
                                            background: "linear-gradient(135deg, #a5b4fc 0%, #38bdf8 100%)",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                        }}
                                    >
                                        {lecturerName}
                                    </span>
                                    ! 👋
                                </h3>
                                <p className="mb-0" style={{ color: "rgba(226, 232, 240, 0.88)", fontSize: 13.5, fontWeight: 500 }}>
                                    Here's what's happening in your classes today.
                                </p>
                            </Col>

                            <Col lg={5} className="d-flex flex-column align-items-lg-end">
                                {examsLoading ? (
                                    <div className="lecture-exam-shim w-100" style={{ height: 44, maxWidth: 300, borderRadius: 12 }} />
                                ) : examsError ? (
                                    <p className="mb-0" style={{ color: "#fecaca" }}>Unable to load exam list.</p>
                                ) : exams.length === 0 ? (
                                    <p className="mb-0" style={{ color: "rgba(255,255,255,0.82)" }}>No exams available.</p>
                                ) : (
                                    <div
                                        className="d-flex align-items-center w-100"
                                        style={{
                                            maxWidth: 300,
                                            background: "rgba(255, 255, 255, 0.12)",
                                            backdropFilter: "blur(12px)",
                                            WebkitBackdropFilter: "blur(12px)",
                                            border: "1px solid rgba(255, 255, 255, 0.25)",
                                            borderRadius: 12,
                                            padding: "8px 12px",
                                            gap: 8,
                                            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: 8,
                                                background: "rgba(255, 255, 255, 0.15)",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <i className="fal fa-graduation-cap" style={{ color: "#a5b4fc", fontSize: 16 }} />
                                        </div>
                                        <Form.Label className="mb-0 font-weight-bold text-nowrap" style={{ color: "#fff", fontSize: 13 }}>
                                            Exam:
                                        </Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={selectedExamId}
                                            onChange={this.handleExamChange}
                                            size="sm"
                                            style={{
                                                border: "1px solid rgba(147, 197, 253, 0.4)",
                                                borderRadius: 8,
                                                background: "#ffffff",
                                                color: "#1e1b4b",
                                                fontWeight: 700,
                                                fontSize: 13,
                                                boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                                            }}
                                        >
                                            <option value="">All Exams</option>
                                            {exams.map((exam) => (
                                                <option key={exam.id} value={exam.id}>
                                                    {exam.exam}
                                                </option>
                                            ))}
                                        </Form.Control>
                                    </div>
                                )}

                                <div
                                    className="d-inline-flex align-items-center"
                                    style={{
                                        background: "rgba(15, 23, 42, 0.5)",
                                        backdropFilter: "blur(6px)",
                                        border: "1px solid rgba(255, 255, 255, 0.14)",
                                        borderRadius: 20,
                                        padding: "4px 14px",
                                        gap: 6,
                                        marginTop: 10,
                                    }}
                                >
                                    <i className="fal fa-clock" style={{ color: "#38bdf8", fontSize: 12 }} />
                                    <span style={{ fontSize: 12, color: "#e0e7ff", fontWeight: 600 }}>
                                        {formattedDate} &middot; {formattedTime}
                                    </span>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Row className="g-3 mb-3">
                    {attendanceSummaryError ? (
                        <Col xs={12}>
                            <p className="text-danger mb-0">Unable to load attendance summary.</p>
                        </Col>
                    ) : (
                        summaryCards.map((card) => (
                            <Col md={3} sm={6} key={card.key}>
                                <Card
                                    className="modern-card border-0 shadow-sm h-100"
                                    role={card.clickable ? "button" : undefined}
                                    onClick={() => this.handleSummaryCardClick(card)}
                                    style={{
                                        cursor: card.clickable && !attendanceSummaryLoading ? "pointer" : "default",
                                        borderTop: `5px solid ${card.color}`,
                                        borderRadius: 14,
                                        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                                        overflow: "hidden",
                                        background: `linear-gradient(180deg, ${card.color}15 0%, #ffffff 45%)`,
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = "translateY(-4px)";
                                        e.currentTarget.style.boxShadow = `0 12px 24px -6px ${card.color}40, 0 4px 12px rgba(0,0,0,0.06)`;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "none";
                                        e.currentTarget.style.boxShadow = "";
                                    }}
                                >
                                    <Card.Body className="d-flex align-items-center" style={{ gap: 16 }}>
                                        <div
                                            style={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: 12,
                                                background: card.background,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <i className={card.icon} style={{ color: card.color, fontSize: 20 }} />
                                        </div>
                                        <div className="flex-grow-1">
                                            {attendanceSummaryLoading ? (
                                                <div className="lecture-exam-shim" style={{ height: 30, width: 100 }} />
                                            ) : (
                                                <h3 className="font-weight-bold text-dark mb-0" style={{ fontSize: typeof card.value === "string" && card.value.includes("Classes") ? 16.5 : undefined }}>
                                                    {card.value}
                                                </h3>
                                            )}
                                            <p className="text-muted mb-0" style={{ fontSize: 13.5 }}>{card.title}</p>
                                            <span
                                                className="font-weight-bold"
                                                style={{ fontSize: 12, color: card.color }}
                                            >
                                                {card.status}
                                            </span>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))
                    )}
                </Row>

                <Row className="g-3 mt-0 mb-3">
                    <Col md={6}>
                        <Card className="modern-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                                        <div
                                            className="lec-stat-icon"
                                            style={{
                                                width: 34,
                                                height: 34,
                                                borderRadius: 8,
                                                background: "#eff6ff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <i className="fal fa-briefcase" style={{ color: "#2563eb", fontSize: 16 }} />
                                        </div>
                                        <h5 className="font-weight-bold text-dark mb-0">Today's Schedule</h5>
                                    </div>
                                    <div className="d-flex align-items-center" style={{ gap: 12 }}>
                                        {!weeklyScheduleLoading && !weeklyScheduleError && (
                                            <span
                                                style={{
                                                    background: "#dbeafe",
                                                    color: "#1d4ed8",
                                                    fontWeight: 700,
                                                    fontSize: 12,
                                                    padding: "4px 12px",
                                                    borderRadius: 20,
                                                }}
                                            >
                                                {todaySchedule.length} Lectures
                                            </span>
                                        )}
                                        <span
                                            role="button"
                                            onClick={this.openScheduleModal}
                                            style={{ color: "#2563eb", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                                        >
                                            View Details
                                        </span>
                                    </div>
                                </div>

                                {weeklyScheduleLoading ? (
                                    <div className="lecture-exam-shim w-100" style={{ height: 120 }} />
                                ) : weeklyScheduleError ? (
                                    <p className="text-danger mb-0">Unable to load today's schedule.</p>
                                ) : todaySchedule.length === 0 ? (
                                    <p className="text-muted mb-0">No lectures scheduled for the selected date.</p>
                                ) : (
                                    <div style={{ maxHeight: 260, overflowY: "auto" }}>
                                        <Row className="g-2">
                                            {todaySchedule.map((period, idx) => (
                                                <Col xs={12} sm={6} key={`${period.class_id}-${period.section_id}-${idx}`} className='mt-3'>
                                                    {this.renderScheduleItemCard(period, this.state.todayUnix, idx)}
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>



                    <Col md={6}>
                        <Card className="modern-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: 10 }}>
                                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                                        <div
                                            className="lec-stat-icon"
                                            style={{
                                                width: 34,
                                                height: 34,
                                                borderRadius: 8,
                                                background: "#f5f3ff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <i className="fal fa-list-alt" style={{ color: "#7c3aed", fontSize: 16 }} />
                                        </div>
                                        <h5 className="font-weight-bold text-dark mb-0">Syllabus Progress &amp; Teaching Analytics</h5>
                                    </div>
                                    <span
                                        style={{
                                            background: "#f5f3ff",
                                            color: "#7c3aed",
                                            fontWeight: 700,
                                            fontSize: 12.5,
                                            padding: "5px 14px",
                                            borderRadius: 20,
                                        }}
                                    >
                                        {SYLLABUS_OVERALL_DUMMY}% Overall
                                    </span>
                                </div>

                                <p className="text-muted mb-2" style={{ fontSize: 12.5, fontWeight: 700 }}>Subject Syllabus Completion</p>
                                {SYLLABUS_PROGRESS_DUMMY.map((s) => (
                                    <div key={s.subject} className="mb-2">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <span className="font-weight-bold text-dark" style={{ fontSize: 13 }}>{s.subject}</span>
                                            <span className="font-weight-bold" style={{ fontSize: 13 }}>{s.percent}%</span>
                                        </div>
                                        <div style={{ height: 6, borderRadius: 4, background: "#f1f5f9", overflow: "hidden" }}>
                                            <div style={{ width: `${s.percent}%`, height: "100%", background: s.color, borderRadius: 4 }} />
                                        </div>
                                    </div>
                                ))}

                                <hr />

                                <p className="text-muted mb-2" style={{ fontSize: 12.5, fontWeight: 700 }}>Teaching Metrics This Term</p>
                                <Row className="g-2">
                                    {TEACHING_METRICS_DUMMY.map((m) => (
                                        <Col xs={6} sm={3} key={m.label}>
                                            <div className="text-center" style={{ border: "1px solid #f1f5f9", borderRadius: 10, padding: "10px 6px" }}>
                                                <p className="text-muted mb-1" style={{ fontSize: 11.5 }}>{m.label}</p>
                                                <div className="font-weight-bold" style={{ fontSize: 15, color: m.color || "#1f2937" }}>{m.value}</div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>

                </Row>

                <Row className="g-3 mt-0 mb-3">
                    <Col md={12}>
                        <Card className="modern-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: 10 }}>
                                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                                        <div
                                            className="lec-stat-icon"
                                            style={{
                                                width: 36,
                                                height: 36,
                                                borderRadius: 10,
                                                background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                                boxShadow: "0 4px 12px rgba(16, 185, 129, 0.25)",
                                            }}
                                        >
                                            <i className="fal fa-calendar-alt" style={{ color: "#fff", fontSize: 17 }} />
                                        </div>
                                        <div>
                                            <h5 className="font-weight-bold text-dark mb-0">Weekly Timetable</h5>
                                            <span className="text-muted" style={{ fontSize: 12 }}>Mon &ndash; Sat Schedule Grid</span>
                                        </div>
                                    </div>
                                    {!weeklyScheduleLoading && !weeklyScheduleError && weeklySlots.length > 0 && (
                                        <span
                                            style={{
                                                background: "#ecfdf5",
                                                color: "#047857",
                                                border: "1px solid #a7f3d0",
                                                fontWeight: 700,
                                                fontSize: 12,
                                                padding: "4px 14px",
                                                borderRadius: 20,
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 6,
                                            }}
                                        >
                                            <i className="fal fa-clock" /> {weeklySlots.length} Time Slots
                                        </span>
                                    )}
                                </div>

                                {weeklyScheduleLoading ? (
                                    <div className="lecture-exam-shim w-100" style={{ height: 220 }} />
                                ) : weeklyScheduleError ? (
                                    <p className="text-danger mb-0">Unable to load weekly timetable.</p>
                                ) : weeklySlots.length === 0 ? (
                                    <p className="text-muted mb-0">No timetable scheduled for this week.</p>
                                ) : (
                                    <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
                                        <table className="w-100" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
                                            <thead>
                                                <tr>
                                                    <th
                                                        style={{
                                                            padding: "12px 16px",
                                                            fontWeight: 800,
                                                            fontSize: 12.5,
                                                            color: "#334155",
                                                            background: "#f8fafc",
                                                            borderBottom: "2px solid #cbd5e1",
                                                            textAlign: "center",
                                                            whiteSpace: "nowrap",
                                                            width: 90,
                                                        }}
                                                    >
                                                        Day
                                                    </th>
                                                    {weeklySlots.map((slot) => (
                                                        <th
                                                            key={slot}
                                                            style={{
                                                                padding: "12px 14px",
                                                                fontWeight: 700,
                                                                fontSize: 12,
                                                                color: "#334155",
                                                                background: "#f8fafc",
                                                                borderBottom: "2px solid #cbd5e1",
                                                                textAlign: "center",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                                                                <i className="fal fa-clock text-primary" style={{ fontSize: 11 }} />
                                                                <span>{this.formatSlotLabel(slot)}</span>
                                                            </div>
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {WEEK_DAYS.map((day) => {
                                                    const todayStr = moment().format("ddd");
                                                    const isToday = day === todayStr;
                                                    return (
                                                        <tr key={day} style={{ background: isToday ? "#f0fdf4" : "#fff" }}>
                                                            <td
                                                                style={{
                                                                    padding: "12px 14px",
                                                                    fontWeight: 800,
                                                                    fontSize: 13,
                                                                    color: isToday ? "#15803d" : "#1e293b",
                                                                    background: isToday ? "#dcfce7" : "#f8fafc",
                                                                    borderBottom: "1px solid #e2e8f0",
                                                                    borderRight: "1px solid #e2e8f0",
                                                                    textAlign: "center",
                                                                }}
                                                            >
                                                                <div className="d-flex flex-column align-items-center justify-content-center">
                                                                    <span>{day}</span>
                                                                    {isToday && (
                                                                        <span
                                                                            style={{
                                                                                background: "#16a34a",
                                                                                color: "#fff",
                                                                                fontSize: 9.5,
                                                                                fontWeight: 800,
                                                                                padding: "1px 6px",
                                                                                borderRadius: 10,
                                                                                marginTop: 2,
                                                                                textTransform: "uppercase",
                                                                            }}
                                                                        >
                                                                            Today
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            {weeklySlots.map((slot) => (
                                                                <td
                                                                    key={slot}
                                                                    style={{
                                                                        padding: 6,
                                                                        borderBottom: "1px solid #e2e8f0",
                                                                        borderRight: "1px solid #f1f5f9",
                                                                        minWidth: 170,
                                                                        verticalAlign: "middle",
                                                                    }}
                                                                >
                                                                    {this.renderScheduleCell(weeklyGrid[day][slot])}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* <Col md={6}>
                        <Card className="modern-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                                        <div
                                            className="lec-stat-icon"
                                            style={{
                                                width: 34,
                                                height: 34,
                                                borderRadius: 8,
                                                background: "#f5f3ff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <i className="fal fa-id-badge" style={{ color: "#7c3aed", fontSize: 16 }} />
                                        </div>
                                        <h5 className="font-weight-bold text-dark mb-0">My Assignments</h5>
                                    </div>
                                </div>

                                {examsLoading ? (
                                    <div className="lecture-exam-shim w-100" style={{ height: 220 }} />
                                ) : examsError || !lecturerAssignments ? (
                                    <p className="text-danger mb-0">Unable to load your assignments.</p>
                                ) : (
                                    <div style={{ maxHeight: 260, overflowY: "auto" }}>
                                        {this.renderAssignmentPillGroup(
                                            "Branches",
                                            (lecturerAssignments.branches || []).map((b) => ({ id: b.branch_id, label: b.branch_name })),
                                            { bg: "#eff6ff", color: "#1d4ed8" }
                                        )}
                                        {this.renderAssignmentPillGroup(
                                            "Classes",
                                            (lecturerAssignments.classes || []).map((c) => ({ id: c.class_id, label: c.class_name })),
                                            { bg: "#f0fdf4", color: "#15803d" }
                                        )}
                                        {this.renderAssignmentPillGroup(
                                            "Sections",
                                            (lecturerAssignments.sections || []).map((s) => ({ id: s.section_id, label: s.section_name })),
                                            { bg: "#fff7ed", color: "#c2410c" }
                                        )}
                                        {this.renderAssignmentPillGroup(
                                            "Subjects",
                                            (lecturerAssignments.subjects || []).map((s) => ({ id: s.subject_id, label: s.subject_name })),
                                            { bg: "#f5f3ff", color: "#7c3aed" }
                                        )}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col> */}
                </Row>

                <Row className="g-3 mb-3">
                    <Col md={6}>
                        <Card className="modern-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: 10 }}>
                                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                                        <div
                                            className="lec-stat-icon"
                                            style={{
                                                width: 34,
                                                height: 34,
                                                borderRadius: 8,
                                                background: "#eff6ff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <i className="fal fa-users" style={{ color: "#2563eb", fontSize: 16 }} />
                                        </div>
                                        <h5 className="font-weight-bold text-dark mb-0">Today's Attendance Overview</h5>
                                    </div>
                                    <span
                                        style={{
                                            background: "#eff6ff",
                                            color: "#1d4ed8",
                                            fontWeight: 700,
                                            fontSize: 12.5,
                                            padding: "5px 14px",
                                            borderRadius: 20,
                                        }}
                                    >
                                        Total: {totalStudents} Students
                                    </span>
                                </div>

                                <Row className="g-2 mb-3">
                                    <Col xs={6} sm={4} md={3}>
                                        <SelectDropDown
                                            name="attendanceCardClassId"
                                            placeholderName="All Classes"
                                            options={classOptions}
                                            stateData={
                                                attendanceCardClassId
                                                    ? classOptions.find((c) => String(c.value) === String(attendanceCardClassId)) || null
                                                    : null
                                            }
                                            handleChange={this.handleAttendanceCardClassChange}
                                        />
                                    </Col>
                                    <Col xs={6} sm={4} md={3}>
                                        <SelectDropDown
                                            name="attendanceCardSectionId"
                                            placeholderName="All Sections"
                                            options={sectionOptionsForAttendanceCard}
                                            stateData={
                                                attendanceCardSectionId
                                                    ? sectionOptionsForAttendanceCard.find((s) => String(s.value) === String(attendanceCardSectionId)) || null
                                                    : null
                                            }
                                            handleChange={this.handleAttendanceCardSectionChange}
                                        />
                                    </Col>
                                    <Col xs={12} sm={4} md={3} className="d-flex align-items-center">
                                        <span
                                            role="button"
                                            onClick={() => this.openAttendanceModal("students")}
                                            style={{ color: "#2563eb", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                                        >
                                            View Details &rarr;
                                        </span>
                                    </Col>
                                </Row>

                                {attendanceSummaryError ? (
                                    <p className="text-danger mb-0">Unable to load attendance summary.</p>
                                ) : attendanceSummaryLoading ? (
                                    <div className="lecture-exam-shim w-100" style={{ height: 180 }} />
                                ) : (
                                    <Row className="align-items-center mt-4">
                                        <Col md={6} className="text-center mb-3 mb-md-0">
                                            {this.renderAttendanceDonut()}
                                        </Col>
                                        <Col md={6}>
                                            <div style={{ border: "1px solid #f1f5f9", borderRadius: 10, padding: "10px 14px", marginBottom: 12 }}>
                                                <p className="text-muted mb-1" style={{ fontSize: 12.5 }}>Average Attendance Rate</p>
                                                <h3 className="font-weight-bold mb-0" style={{ color: "#15803d" }}>{averageAttendance}%</h3>
                                            </div>
                                            {[
                                                { label: "Present", color: "#16a34a", count: presentCount },
                                                { label: "Absent", color: "#dc2626", count: absentCount },
                                                { label: "Not Marked", color: "#f59e0b", count: notMarkedCount },
                                            ].map((row) => (
                                                <div key={row.label} className="d-flex justify-content-between align-items-center mb-2">
                                                    <span className="d-flex align-items-center" style={{ gap: 8, fontSize: 13.5 }}>
                                                        <span style={{ width: 10, height: 10, borderRadius: "50%", background: row.color, display: "inline-block" }} />
                                                        {row.label}
                                                    </span>
                                                    <span className="font-weight-bold" style={{ fontSize: 13.5 }}>
                                                        {row.count}
                                                        {totalStudents > 0 ? ` (${Math.round((row.count / totalStudents) * 1000) / 10}%)` : ""}
                                                    </span>
                                                </div>
                                            ))}
                                        </Col>
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={6}>
                        <UpcomingExamsWidget
                            client={this.props.client}
                            branchId={branchId}
                            classOptions={classOptions}
                            allSectionOptions={allSectionOptions}
                            classMap={classMap}
                            selectedExamId={selectedExamId}
                            onExamSelect={this.openExamDetailsModal}
                        />
                    </Col>
                </Row>

                <Card className="modern-card border-0 shadow-sm mb-3">
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-end mb-3 flex-wrap" style={{ gap: 12 }}>
                            <div className="btn-group" role="group">
                                <button
                                    type="button"
                                    className={`btn btn-sm ${performanceView === "graph" ? "btn-primary font-weight-bold" : "btn-outline-primary"}`}
                                    onClick={() => this.setState({ performanceView: "graph" })}
                                    style={{ fontSize: 12.5, padding: "5px 16px", borderRadius: "8px 0 0 8px" }}
                                >
                                    <i className="fal fa-chart-bar me-1" style={{ marginRight: 5 }} /> Graph
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${performanceView === "table" ? "btn-primary font-weight-bold" : "btn-outline-primary"}`}
                                    onClick={() => this.setState({ performanceView: "table" })}
                                    style={{ fontSize: 12.5, padding: "5px 16px", borderRadius: "0 8px 8px 0" }}
                                >
                                    <i className="fal fa-table me-1" style={{ marginRight: 5 }} /> Table
                                </button>
                            </div>

                            {/* <div>
                                <Form.Label className="mb-1 d-block text-right" style={{ fontSize: 12, fontWeight: 600 }}>
                                    Date Range
                                </Form.Label>
                                <DateBlock applyCallback={this.handlePerformanceDateChange} />
                            </div> */}
                        </div>

                        <Row className="g-3">
                            <Col md={4}>
                                <div style={{ border: "1px solid #f1f5f9", borderRadius: 10, padding: 14, height: "100%" }}>
                                    <div className="d-flex align-items-center mb-2" style={{ gap: 10 }}>
                                        <div
                                            style={{
                                                width: 30,
                                                height: 30,
                                                borderRadius: 8,
                                                background: "#eef2ff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <i className="fal fa-chart-bar" style={{ color: "#4f46e5", fontSize: 14 }} />
                                        </div>
                                        <h6 className="font-weight-bold text-dark mb-0">Subject Performance</h6>
                                    </div>

                                    {subjectPerformanceLoading ? (
                                        <div className="lecture-exam-shim w-100" style={{ height: 180 }} />
                                    ) : subjectPerformanceError ? (
                                        <p className="text-danger mb-0">Unable to load subject performance.</p>
                                    ) : subjectPerformance.length === 0 ? (
                                        <p className="text-muted mb-0">No subject performance data for the selected date range.</p>
                                    ) : performanceView === "graph" ? (
                                        this.renderSubjectPerformanceChart()
                                    ) : (
                                        <CardLessDataTableWithSearch
                                            parentData={subjectPerformanceRows}
                                            particlecolumns={subjectPerformanceTableColumns}
                                            defaultSorted={[{ dataField: "average_marks", order: "desc" }]}
                                            name="subject_performance"
                                            placeholder="Search subjects..."
                                        />
                                    )}
                                </div>
                            </Col>

                            <Col md={4}>
                                <div style={{ border: "1px solid #f1f5f9", borderRadius: 10, padding: 14, height: "100%" }}>
                                    <div className="d-flex align-items-center mb-2" style={{ gap: 10 }}>
                                        <div
                                            style={{
                                                width: 30,
                                                height: 30,
                                                borderRadius: 8,
                                                background: "#f0fdf4",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <i className="fal fa-school" style={{ color: "#16a34a", fontSize: 14 }} />
                                        </div>
                                        <h6 className="font-weight-bold text-dark mb-0">Class Performance</h6>
                                    </div>

                                    {classPerformanceLoading ? (
                                        <div className="lecture-exam-shim w-100" style={{ height: 180 }} />
                                    ) : classPerformanceError ? (
                                        <p className="text-danger mb-0">Unable to load class performance.</p>
                                    ) : classPerformance.length === 0 ? (
                                        <p className="text-muted mb-0">No class performance data for the selected date range.</p>
                                    ) : performanceView === "graph" ? (
                                        this.renderClassPerformanceChart()
                                    ) : (
                                        <CardLessDataTableWithSearch
                                            parentData={classPerformanceRows}
                                            particlecolumns={classPerformanceTableColumns}
                                            defaultSorted={[{ dataField: "average_marks", order: "desc" }]}
                                            name="class_performance"
                                            placeholder="Search classes..."
                                        />
                                    )}
                                </div>
                            </Col>

                            <Col md={4}>
                                <div style={{ border: "1px solid #f1f5f9", borderRadius: 10, padding: 14, height: "100%" }}>
                                    <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
                                        <div
                                            style={{
                                                width: 30,
                                                height: 30,
                                                borderRadius: 8,
                                                background: "#fff7ed",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <i className="fal fa-layer-group" style={{ color: "#c2410c", fontSize: 14 }} />
                                        </div>
                                        <h6 className="font-weight-bold text-dark mb-0">Section Performance</h6>
                                    </div>

                                    {sectionPerformanceLoading ? (
                                        <div className="lecture-exam-shim w-100" style={{ height: 180 }} />
                                    ) : sectionPerformanceError ? (
                                        <p className="text-danger mb-0">Unable to load section performance.</p>
                                    ) : sectionPerformance.length === 0 ? (
                                        <p className="text-muted mb-0">No section performance data for the selected date range.</p>
                                    ) : performanceView === "graph" ? (
                                        this.renderSectionPerformanceChart()
                                    ) : (
                                        <CardLessDataTableWithSearch
                                            parentData={sectionPerformanceRows}
                                            particlecolumns={sectionPerformanceTableColumns}
                                            defaultSorted={[{ dataField: "average_marks", order: "desc" }]}
                                            name="section_performance"
                                            placeholder="Search sections..."
                                        />
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Row className="g-3 mb-3">
                    <Col md={6}>
                        <Card className="modern-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap" style={{ gap: 8 }}>
                                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                                        <div
                                            className="lec-stat-icon"
                                            style={{
                                                width: 34,
                                                height: 34,
                                                borderRadius: 8,
                                                background: "#f0fdf4",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <i className="fal fa-trophy" style={{ color: "#16a34a", fontSize: 16 }} />
                                        </div>
                                        <h5 className="font-weight-bold text-dark mb-0">Top Performing Students</h5>
                                    </div>
                                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                        <span style={{ background: "#dcfce7", color: "#15803d", fontWeight: 700, fontSize: 12, padding: "3px 10px", borderRadius: 20 }}>
                                            🏆 {topStudentsRows.length} Performers
                                        </span>
                                        <div className="btn-group btn-group-sm" role="group">
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${this.state.topStudentsView === "table" ? "btn-primary" : "btn-outline-primary"}`}
                                                style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px" }}
                                                onClick={() => this.setState({ topStudentsView: "table" })}
                                            >
                                                <i className="fal fa-table me-1" /> Table
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${this.state.topStudentsView === "graph" ? "btn-primary" : "btn-outline-primary"}`}
                                                style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px" }}
                                                onClick={() => this.setState({ topStudentsView: "graph" })}
                                            >
                                                <i className="fal fa-chart-bar me-1" /> Graph
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {this.renderReportCardNotice("top")}

                                {topStudentsLoading ? (
                                    <div className="lecture-exam-shim w-100" style={{ height: 320 }} />
                                ) : topStudentsError ? (
                                    <p className="text-danger mb-0">Unable to load top performing students.</p>
                                ) : topStudentsRows.length === 0 ? (
                                    <p className="text-muted mb-0">No data for the selected date range.</p>
                                ) : (
                                    <div style={{ height: 320, overflowY: "auto" }}>
                                        {this.state.topStudentsView === "graph" ? (
                                            this.renderTopStudentsChart(topStudentsRows)
                                        ) : (
                                            <CardLessDataTableWithSearch
                                                parentData={topStudentsRows}
                                                particlecolumns={topStudentsTableColumns}
                                                defaultSorted={[{ dataField: "rank", order: "asc" }]}
                                                name="top_performing_students"
                                                placeholder="Search students..."
                                            />
                                        )}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={6}>
                        <Card className="modern-card border-0 shadow-sm h-100">
                            <Card.Body>
                                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap" style={{ gap: 8 }}>
                                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                                        <div
                                            className="lec-stat-icon"
                                            style={{
                                                width: 34,
                                                height: 34,
                                                borderRadius: 8,
                                                background: "#fef2f2",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <i className="fal fa-hand-holding-heart" style={{ color: "#b91c1c", fontSize: 16 }} />
                                        </div>
                                        <h5 className="font-weight-bold text-dark mb-0">Students Needing Attention</h5>
                                    </div>
                                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                        <span style={{ background: "#fee2e2", color: "#b91c1c", fontWeight: 700, fontSize: 12, padding: "3px 10px", borderRadius: 20 }}>
                                            ⚠️ {attentionStudentsRows.length} Need Attention
                                        </span>
                                        <div className="btn-group btn-group-sm" role="group">
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${this.state.attentionStudentsView === "table" ? "btn-danger" : "btn-outline-danger"}`}
                                                style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px" }}
                                                onClick={() => this.setState({ attentionStudentsView: "table" })}
                                            >
                                                <i className="fal fa-table me-1" /> Table
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${this.state.attentionStudentsView === "graph" ? "btn-danger" : "btn-outline-danger"}`}
                                                style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px" }}
                                                onClick={() => this.setState({ attentionStudentsView: "graph" })}
                                            >
                                                <i className="fal fa-chart-bar me-1" /> Graph
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {this.renderReportCardNotice("attention")}

                                {attentionStudentsLoading ? (
                                    <div className="lecture-exam-shim w-100" style={{ height: 450 }} />
                                ) : attentionStudentsError ? (
                                    <p className="text-danger mb-0">Unable to load students needing attention.</p>
                                ) : attentionStudentsRows.length === 0 ? (
                                    <p className="text-muted mb-0">No data for the selected date range.</p>
                                ) : (
                                    <div style={{ height: 320, overflowY: "auto" }}>
                                        {this.state.attentionStudentsView === "graph" ? (
                                            this.renderAttentionStudentsChart(attentionStudentsRows)
                                        ) : (
                                            <CardLessDataTableWithSearch
                                                parentData={attentionStudentsRows}
                                                particlecolumns={attentionStudentsTableColumns}
                                                defaultSorted={[{ dataField: "marks", order: "asc" }]}
                                                name="students_needing_attention"
                                                placeholder="Search students..."
                                            />
                                        )}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Card className="modern-card border-0 shadow-sm mb-3">
                    <Card.Body>
                        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap" style={{ gap: 10 }}>
                            <div className="d-flex align-items-center" style={{ gap: 10 }}>
                                <div
                                    className="lec-stat-icon"
                                    style={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: 10,
                                        background: "linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                        boxShadow: "0 4px 12px rgba(79, 70, 229, 0.25)",
                                    }}
                                >
                                    <i className="fal fa-file-chart-line" style={{ color: "#fff", fontSize: 17 }} />
                                </div>
                                <div>
                                    <h5 className="font-weight-bold text-dark mb-0">Exam Reports</h5>
                                    <span className="text-muted" style={{ fontSize: 12 }}>Comprehensive Exam Performance &amp; Analytics</span>
                                </div>
                            </div>
                            {!examReportsLoading && !examReportsError && examReports.length > 0 && (
                                <span style={{ background: "#eef2ff", color: "#4f46e5", border: "1px solid #c7d2fe", fontWeight: 700, fontSize: 12, padding: "4px 14px", borderRadius: 20 }}>
                                    📊 {examReports.length} Reports Available
                                </span>
                            )}
                        </div>

                        {examReportsLoading ? (
                            <div className="lecture-exam-shim w-100" style={{ height: 220 }} />
                        ) : examReportsError ? (
                            <p className="text-danger mb-0">Unable to load exam reports.</p>
                        ) : examReports.length === 0 ? (
                            <p className="text-muted mb-0">No exam reports available.</p>
                        ) : (
                            <CardLessDataTableWithSearch
                                parentData={examReports}
                                particlecolumns={examReportsTableColumns}
                                defaultSorted={[{ dataField: "date", order: "desc" }]}
                                name="exam_reports"
                                placeholder="Search exams..."
                            />
                        )}
                    </Card.Body>
                </Card>

                <Modal show={classSectionModalShow} onHide={this.closeClassSectionModal} size="lg" centered scrollable dialogClassName="lecturer-dashboard-modern">
                    <Modal.Header closeButton style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)", color: "#fff" }}>
                        <Modal.Title style={{ fontSize: 16, fontWeight: 800 }} className="text-white">
                            <i className="fal fa-chalkboard-teacher me-2" /> Class-wise Section Details
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ background: "#f8fafc", padding: 20 }}>
                        {classOptions.length === 0 ? (
                            <p className="text-muted mb-0">No assigned classes or sections found.</p>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-bordered table-hover bg-white shadow-sm" style={{ borderRadius: 10, overflow: "hidden", fontSize: 13 }}>
                                    <thead style={{ background: "#f1f5f9" }}>
                                        <tr>
                                            <th style={{ width: "35%", color: "#334155", fontWeight: 700 }}>Class</th>
                                            <th style={{ color: "#334155", fontWeight: 700 }}>Assigned Sections</th>
                                            <th style={{ width: "15%", color: "#334155", fontWeight: 700, textAlign: "center" }}>Count</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {classOptions.map((cls) => {
                                            const clsSections = allSectionOptions.filter((s) => String(s.class_id) === String(cls.value));
                                            return (
                                                <tr key={cls.value}>
                                                    <td style={{ fontWeight: 700, color: "#4f46e5", verticalAlign: "middle" }}>
                                                        <i className="fal fa-chalkboard me-2 text-indigo" />
                                                        {cls.label}
                                                    </td>
                                                    <td style={{ verticalAlign: "middle" }}>
                                                        {clsSections.length === 0 ? (
                                                            <span className="text-muted" style={{ fontSize: 12 }}>No sections</span>
                                                        ) : (
                                                            <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                                                                {clsSections.map((sec) => (
                                                                    <span
                                                                        key={sec.value}
                                                                        style={{
                                                                            background: "#ecfeff",
                                                                            color: "#0891b2",
                                                                            fontWeight: 700,
                                                                            fontSize: 11.5,
                                                                            padding: "3px 10px",
                                                                            borderRadius: 12,
                                                                            border: "1px solid #a5f3fc",
                                                                        }}
                                                                    >
                                                                        {sec.label}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: "center", fontWeight: 800, color: "#0f172a", verticalAlign: "middle" }}>
                                                        <span className="badge bg-light text-dark border" style={{ fontSize: 11.5 }}>
                                                            {clsSections.length}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Modal.Body>
                </Modal>

                <Modal show={highestMarksModalShow} onHide={this.closeHighestMarksModal} size="xl" centered scrollable dialogClassName="lecturer-dashboard-modern">
                    <Modal.Header closeButton style={{ background: "linear-gradient(135deg, #7c2d12 0%, #9a3412 50%, #c2410c 100%)", color: "#fff" }}>
                        <Modal.Title style={{ fontSize: 16, fontWeight: 800 }} className="text-white">
                            <i className="fal fa-trophy-alt me-2" /> Highest Marks Exam Performance &amp; Analytics
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ background: "#f8fafc", padding: 20 }}>
                        {!highestMarksExam ? (
                            <p className="text-muted mb-0">No exam data available.</p>
                        ) : (() => {
                            const detail = highestMarksExamDetail || {};
                            const maxMarks = detail.maxMarks || highestMarksExam.maxMarks || 300;
                            const totalQuestions = detail.totalQuestions || highestMarksExam.totalQuestions || 75;
                            const highMarksVal = detail.highMarks ?? (highestMarksExam.highMarks ?? (maxMarks * (highestMarksExam.highPct || 0) / 100).toFixed(0));
                            const avgMarksVal = detail.avgMarks ?? (highestMarksExam.avgMarks ?? (maxMarks * (highestMarksExam.avgPct || 0) / 100).toFixed(1));
                            const lowMarksVal = detail.lowMarks ?? (highestMarksExam.lowMarks ?? 0);
                            const rawStudentsList = detail.students || [];

                            // Scoped Access Filtering: Only show students for Branches, Classes, and Sections the user has permission to access!
                            const allowedBranchNames = ((lecturerAssignments && lecturerAssignments.branches) || []).map((b) => String(b.branch_name).trim().toLowerCase());
                            const allowedBranchIds = ((lecturerAssignments && lecturerAssignments.branches) || []).map((b) => String(b.branch_id));

                            const allowedClassNames = ((lecturerAssignments && lecturerAssignments.classes) || []).map((c) => String(c.class_name).trim().toLowerCase());
                            const allowedClassIds = ((lecturerAssignments && lecturerAssignments.classes) || []).map((c) => String(c.class_id));

                            const allowedSectionNames = ((lecturerAssignments && lecturerAssignments.sections) || []).map((s) => String(s.section_name).trim().toLowerCase());
                            const allowedSectionIds = ((lecturerAssignments && lecturerAssignments.sections) || []).map((s) => String(s.section_id));

                            const studentsList = rawStudentsList.filter((s) => {
                                if (!lecturerAssignments) return true;

                                if (allowedBranchNames.length > 0 || allowedBranchIds.length > 0) {
                                    const bNameMatch = s.branchName && allowedBranchNames.includes(String(s.branchName).trim().toLowerCase());
                                    const bIdMatch = s.branch_id != null && allowedBranchIds.includes(String(s.branch_id));
                                    if (s.branchName || s.branch_id) {
                                        if (!bNameMatch && !bIdMatch) return false;
                                    }
                                }

                                if (allowedClassNames.length > 0 || allowedClassIds.length > 0) {
                                    const cNameMatch = s.className && allowedClassNames.includes(String(s.className).trim().toLowerCase());
                                    const cIdMatch = s.class_id != null && allowedClassIds.includes(String(s.class_id));
                                    if (s.className || s.class_id) {
                                        if (!cNameMatch && !cIdMatch) return false;
                                    }
                                }

                                if (allowedSectionNames.length > 0 || allowedSectionIds.length > 0) {
                                    const sNameMatch = s.sectionName && allowedSectionNames.includes(String(s.sectionName).trim().toLowerCase());
                                    const sIdMatch = s.section_id != null && allowedSectionIds.includes(String(s.section_id));
                                    if (s.sectionName || s.section_id) {
                                        if (!sNameMatch && !sIdMatch) return false;
                                    }
                                }

                                return true;
                            });

                            // Subject Average Chart Data
                            const subjectAvgList = detail.subjectAvg || [];

                            // Exam Attendance Graph Data (Appeared vs Absent)
                            const appearedCount = highestMarksExam.appeared || detail.appeared || 0;
                            const totalStudentsCount = highestMarksExam.totalStudents || detail.totalStudents || appearedCount;
                            const absentCount = Math.max(0, totalStudentsCount - appearedCount);

                            const attendanceGraphData = [
                                { status: "Appeared (Present)", count: appearedCount, fill: "#16a34a", pct: totalStudentsCount > 0 ? ((appearedCount / totalStudentsCount) * 100).toFixed(1) : 100 },
                                { status: "Absent Students", count: absentCount, fill: "#dc2626", pct: totalStudentsCount > 0 ? ((absentCount / totalStudentsCount) * 100).toFixed(1) : 0 },
                            ];

                            return (
                                <>
                                    {/* ── Top Exam Overview Card & Key Metrics ── */}
                                    <div className="mb-4 p-3 bg-white shadow-sm" style={{ borderRadius: 12, border: "1px solid #fed7aa" }}>
                                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: 10 }}>
                                            <div>
                                                <span className="badge bg-warning text-dark mb-1" style={{ fontSize: 11, fontWeight: 800, padding: "4px 12px", borderRadius: 12 }}>
                                                    🏆 Highest Score Exam ({highestMarksExam.highPct}% Top Score)
                                                </span>
                                                <h5 className="mb-0 font-weight-bold" style={{ color: "#7c2d12" }}>
                                                    {highestMarksExam.name} {highestMarksExam.className ? `· Class ${highestMarksExam.className}` : ""}
                                                </h5>
                                            </div>
                                            <div className="d-flex align-items-center gap-2 flex-wrap text-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger font-weight-bold shadow-sm"
                                                    style={{ fontSize: 12, borderRadius: 8, padding: "6px 16px" }}
                                                    onClick={() => {
                                                        this.closeHighestMarksModal();
                                                        this.openExamDetailsModal(highestMarksExam);
                                                    }}
                                                >
                                                    <i className="fal fa-chart-line me-1" /> View Full Exam Analysis
                                                </button>
                                                <div>
                                                    <span className="text-muted d-block" style={{ fontSize: 12 }}>
                                                        <i className="fal fa-calendar-alt me-1" />
                                                        {formatReportDate(highestMarksExam.date)}
                                                    </span>
                                                    <span className="badge bg-light text-secondary border mt-1" style={{ fontSize: 11 }}>
                                                        {maxMarks} Max Marks · {totalQuestions} Qs (+{detail.marksPerCorrect || 4}/-{detail.marksPerWrong || 1})
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* ── Useful Exam Metrics Badges ── */}
                                        <Row className="g-2 text-center">
                                            <Col xs={6} md={2.4} style={{ flex: "0 0 20%", maxWidth: "20%" }}>
                                                <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "8px 10px", borderRadius: 8 }}>
                                                    <div style={{ fontSize: 11, color: "#166534", fontWeight: 700 }}>Highest Score</div>
                                                    <div style={{ fontSize: 16, fontWeight: 800, color: "#15803d" }}>
                                                        {highMarksVal} <span style={{ fontSize: 11, fontWeight: 600 }}>({highestMarksExam.highPct}%)</span>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col xs={6} md={2.4} style={{ flex: "0 0 20%", maxWidth: "20%" }}>
                                                <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", padding: "8px 10px", borderRadius: 8 }}>
                                                    <div style={{ fontSize: 11, color: "#075985", fontWeight: 700 }}>Class Average</div>
                                                    <div style={{ fontSize: 16, fontWeight: 800, color: "#0369a1" }}>
                                                        {avgMarksVal} <span style={{ fontSize: 11, fontWeight: 600 }}>({highestMarksExam.avgPct}%)</span>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col xs={6} md={2.4} style={{ flex: "0 0 20%", maxWidth: "20%" }}>
                                                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", padding: "8px 10px", borderRadius: 8 }}>
                                                    <div style={{ fontSize: 11, color: "#991b1b", fontWeight: 700 }}>Lowest Score</div>
                                                    <div style={{ fontSize: 16, fontWeight: 800, color: "#dc2626" }}>
                                                        {lowMarksVal} <span style={{ fontSize: 11, fontWeight: 600 }}>({highestMarksExam.lowPct || 0}%)</span>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col xs={6} md={2.4} style={{ flex: "0 0 20%", maxWidth: "20%" }}>
                                                <div style={{ background: "#fef3c7", border: "1px solid #fde68a", padding: "8px 10px", borderRadius: 8 }}>
                                                    <div style={{ fontSize: 11, color: "#92400e", fontWeight: 700 }}>Pass Rate</div>
                                                    <div style={{ fontSize: 16, fontWeight: 800, color: "#b45309" }}>{highestMarksExam.passPct}%</div>
                                                </div>
                                            </Col>
                                            <Col xs={6} md={2.4} style={{ flex: "0 0 20%", maxWidth: "20%" }}>
                                                <div style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", padding: "8px 10px", borderRadius: 8 }}>
                                                    <div style={{ fontSize: 11, color: "#5b21b6", fontWeight: 700 }}>Appeared Students</div>
                                                    <div style={{ fontSize: 16, fontWeight: 800, color: "#6d28d9" }}>{appearedCount} / {totalStudentsCount}</div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>

                                    {highestMarksDetailLoading ? (
                                        <div className="lecture-exam-shim w-100" style={{ height: 220, borderRadius: 12 }} />
                                    ) : (
                                        <>
                                            {/* ── Graphs: Subject Average & Exam Attendance ── */}
                                            <Row className="g-3 mb-4">
                                                {/* Graph 1: Subject Average Performance (subjectAvg) */}
                                                <Col md={6}>
                                                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
                                                        <Card.Body>
                                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                                <h6 style={{ fontWeight: 700, color: "#1e293b", fontSize: 13 }} className="mb-0">
                                                                    <i className="fal fa-books text-indigo me-2" /> Subject-wise Average Performance (subjectAvg)
                                                                </h6>
                                                            </div>
                                                            {subjectAvgList.length === 0 ? (
                                                                <div className="text-center py-4 text-muted" style={{ fontSize: 12.5 }}>
                                                                    <i className="fal fa-info-circle me-1" /> Subject average breakdown loading...
                                                                </div>
                                                            ) : (
                                                                <div style={{ height: 210, width: "100%" }}>
                                                                    <ResponsiveContainer width="100%" height="100%">
                                                                        <BarChart data={subjectAvgList} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                                                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                                            <XAxis dataKey="subject" tick={{ fontSize: 11, fill: "#475569", fontWeight: 600 }} />
                                                                            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} domain={[0, 100]} unit="%" />
                                                                            <Tooltip formatter={(value, name, item) => [`${item.payload.avgMarks || value} Marks (${value}%)`, "Average Score"]} />
                                                                            <Bar dataKey="pct" radius={[6, 6, 0, 0]} fill="#4f46e5">
                                                                                {subjectAvgList.map((s, idx) => {
                                                                                    const colors = ["#4f46e5", "#059669", "#d97706", "#dc2626", "#0284c7"];
                                                                                    return <Cell key={idx} fill={colors[idx % colors.length]} />;
                                                                                })}
                                                                            </Bar>
                                                                        </BarChart>
                                                                    </ResponsiveContainer>
                                                                </div>
                                                            )}
                                                        </Card.Body>
                                                    </Card>
                                                </Col>

                                                {/* Graph 2: Attendance & Participation Graph (Appeared vs Absent) */}
                                                <Col md={6}>
                                                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
                                                        <Card.Body>
                                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                                <h6 style={{ fontWeight: 700, color: "#1e293b", fontSize: 13 }} className="mb-0">
                                                                    <i className="fal fa-user-check text-success me-2" /> Student Exam Attendance Ratio
                                                                </h6>
                                                                <span className="badge bg-light text-success border" style={{ fontSize: 11, fontWeight: 700 }}>
                                                                    {totalStudentsCount > 0 ? ((appearedCount / totalStudentsCount) * 100).toFixed(1) : 100}% Turnout
                                                                </span>
                                                            </div>
                                                            <div style={{ height: 210, width: "100%" }}>
                                                                <ResponsiveContainer width="100%" height="100%">
                                                                    <BarChart data={attendanceGraphData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                                                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                                        <XAxis dataKey="status" tick={{ fontSize: 11, fill: "#475569", fontWeight: 600 }} />
                                                                        <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                                                                        <Tooltip formatter={(value, name, item) => [`${value} Students (${item.payload.pct}%)`, "Count"]} />
                                                                        <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                                                                            {attendanceGraphData.map((entry, idx) => (
                                                                                <Cell key={idx} fill={entry.fill} />
                                                                            ))}
                                                                        </Bar>
                                                                    </BarChart>
                                                                </ResponsiveContainer>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            </Row>

                                            {/* ── Student Details Table ── */}
                                            <Card className="border-0 shadow-sm" style={{ borderRadius: 12 }}>
                                                <Card.Body>
                                                    <h6 className="font-weight-bold text-dark mb-3" style={{ fontSize: 13.5 }}>
                                                        <i className="fal fa-user-graduate me-2 text-indigo" /> Student Performance &amp; Subject Ranks ({studentsList.length} Students)
                                                    </h6>
                                                    {studentsList.length === 0 ? (
                                                        <p className="text-muted mb-0" style={{ fontSize: 13 }}>No student details found for this exam.</p>
                                                    ) : (() => {
                                                        const processedStudents = studentsList.map((s, idx) => {
                                                            const phySub = (s.subjects || []).find((sub) => sub.subject?.toLowerCase().includes("phys"));
                                                            const chemSub = (s.subjects || []).find((sub) => sub.subject?.toLowerCase().includes("chem"));
                                                            const mathSub = (s.subjects || []).find((sub) => sub.subject?.toLowerCase().includes("math") || sub.subject?.toLowerCase().includes("bio") || sub.subject?.toLowerCase().includes("bot") || sub.subject?.toLowerCase().includes("zoo"));

                                                            const getSubMarks = (sub) => {
                                                                if (!sub) return "-";
                                                                if (sub.marks != null) return sub.marks;
                                                                if (sub.correctMarks != null) return sub.correctMarks;
                                                                if (sub.correct != null) return sub.correct * (detail.marksPerCorrect || 4) - (sub.wrong || 0) * (detail.marksPerWrong || 1);
                                                                return 0;
                                                            };

                                                            const totalAns = (s.correct || 0) + (s.wrong || 0);
                                                            const accVal = s.accuracy != null ? s.accuracy : (totalAns > 0 ? ((s.correct / totalAns) * 100).toFixed(0) : 0);
                                                            const overallAvgVal = s.percentage != null ? s.percentage : (s.pct != null ? s.pct : 0);
                                                            const totalMarksVal = s.marks != null ? s.marks : (s.totalMarks != null ? s.totalMarks : (s.correctMarks != null ? s.correctMarks : 0));

                                                            return {
                                                                ...s,
                                                                id: s.studentUserId || `stud-${idx}`,
                                                                overallRank: s.rank || idx + 1,
                                                                examRank: s.rank || idx + 1,
                                                                overallAverage: overallAvgVal,
                                                                totalMarksDisplay: totalMarksVal,
                                                                accuracyDisplay: accVal,
                                                                branchDisplayName: s.branchName || "Main Branch",
                                                                physicsMarks: getSubMarks(phySub),
                                                                physicsRank: phySub ? (phySub.rank || "-") : "-",
                                                                chemistryMarks: getSubMarks(chemSub),
                                                                chemistryRank: chemSub ? (chemSub.rank || "-") : "-",
                                                                mathMarks: getSubMarks(mathSub),
                                                                mathRank: mathSub ? (mathSub.rank || "-") : "-",
                                                            };
                                                        });

                                                        return (
                                                            <CardLessDataTableWithSearch
                                                                parentData={processedStudents}
                                                                particlecolumns={[
                                                                    {
                                                                        dataField: "overallRank",
                                                                        text: "Overall rank",
                                                                        sort: true,
                                                                        formatter: (cell) => <span style={{ fontWeight: 800, color: "#1d4ed8" }}>#{cell}</span>,
                                                                    },
                                                                    {
                                                                        dataField: "name",
                                                                        text: "Student Name",
                                                                        sort: true,
                                                                        formatter: (cell) => <span style={{ fontWeight: 700, color: "#0f172a" }}>{cell || "Student"}</span>,
                                                                    },
                                                                    {
                                                                        dataField: "overallAverage",
                                                                        text: "Overall Average",
                                                                        sort: true,
                                                                        formatter: (cell) => (
                                                                            <span style={{ fontWeight: 800, color: Number(cell) >= 75 ? "#15803d" : "#b91c1c" }}>
                                                                                {cell != null ? `${cell}%` : "-"}
                                                                            </span>
                                                                        ),
                                                                    },
                                                                    {
                                                                        dataField: "sectionName",
                                                                        text: "Section",
                                                                        sort: true,
                                                                        formatter: (cell) => (
                                                                            <span style={{ background: "#ecfeff", color: "#0891b2", fontWeight: 700, fontSize: 11, padding: "2px 8px", borderRadius: 6 }}>
                                                                                {cell || "-"}
                                                                            </span>
                                                                        ),
                                                                    },
                                                                    {
                                                                        dataField: "branchDisplayName",
                                                                        text: "Branch",
                                                                        sort: true,
                                                                        formatter: (cell) => (
                                                                            <span style={{ background: "#f1f5f9", color: "#475569", fontWeight: 600, fontSize: 11, padding: "2px 8px", borderRadius: 6 }}>
                                                                                {cell}
                                                                            </span>
                                                                        ),
                                                                    },
                                                                    {
                                                                        dataField: "totalMarksDisplay",
                                                                        text: "Marks",
                                                                        sort: true,
                                                                        formatter: (cell) => (
                                                                            <span style={{ fontWeight: 800, color: "#4f46e5", background: "#eef2ff", padding: "3px 8px", borderRadius: 6 }}>
                                                                                {cell} / {maxMarks}
                                                                            </span>
                                                                        ),
                                                                    },
                                                                    {
                                                                        dataField: "accuracyDisplay",
                                                                        text: "Accuracy",
                                                                        sort: true,
                                                                        formatter: (cell) => (
                                                                            <span style={{ fontWeight: 700, color: "#0284c7" }}>
                                                                                {cell != null ? `${cell}%` : "-"}
                                                                            </span>
                                                                        ),
                                                                    },
                                                                    {
                                                                        dataField: "examRank",
                                                                        text: "Exam Rank",
                                                                        sort: true,
                                                                        formatter: (cell) => <span style={{ fontWeight: 800, color: "#059669" }}>#{cell}</span>,
                                                                    },
                                                                    {
                                                                        dataField: "physicsMarks",
                                                                        text: "Physics Marks",
                                                                        sort: true,
                                                                        formatter: (cell) => <span style={{ fontWeight: 700, color: "#4338ca" }}>{cell}</span>,
                                                                    },
                                                                    // {
                                                                    //     dataField: "physicsRank",
                                                                    //     text: "Physics Rank",
                                                                    //     sort: true,
                                                                    //     formatter: (cell) => <span style={{ fontSize: 11.5, fontWeight: 700, color: "#6366f1" }}>{cell !== "-" ? `#${cell}` : "-"}</span>,
                                                                    // },
                                                                    {
                                                                        dataField: "chemistryMarks",
                                                                        text: "Chemistry Marks",
                                                                        sort: true,
                                                                        formatter: (cell) => <span style={{ fontWeight: 700, color: "#047857" }}>{cell}</span>,
                                                                    },
                                                                    // {
                                                                    //     dataField: "chemistryRank",
                                                                    //     text: "Chemistry Rank",
                                                                    //     sort: true,
                                                                    //     formatter: (cell) => <span style={{ fontSize: 11.5, fontWeight: 700, color: "#10b981" }}>{cell !== "-" ? `#${cell}` : "-"}</span>,
                                                                    // },
                                                                    {
                                                                        dataField: "mathMarks",
                                                                        text: "Mathematics Marks",
                                                                        sort: true,
                                                                        formatter: (cell) => <span style={{ fontWeight: 700, color: "#b45309" }}>{cell}</span>,
                                                                    },
                                                                    // {
                                                                    //     dataField: "mathRank",
                                                                    //     text: "Mathematics Rank",
                                                                    //     sort: true,
                                                                    //     formatter: (cell) => <span style={{ fontSize: 11.5, fontWeight: 700, color: "#f59e0b" }}>{cell !== "-" ? `#${cell}` : "-"}</span>,
                                                                    // },
                                                                ]}
                                                                defaultSorted={[{ dataField: "overallRank", order: "asc" }]}
                                                                name="highest_exam_students_details"
                                                                placeholder="Search student..."
                                                            />
                                                        );
                                                    })()}
                                                </Card.Body>
                                            </Card>
                                        </>
                                    )}
                                </>
                            );
                        })()}
                    </Modal.Body>
                </Modal>

                <Modal show={showAttendanceModal} onHide={this.closeAttendanceModal} size="xl" centered scrollable dialogClassName="lecturer-dashboard-modern">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {activeCard === "students" ? "Total Students" : "Average Attendance"} &mdash; Attendance Details
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row className="g-3 mb-3">
                            <Col xs={12} sm={6} md={3}>
                                <Form.Label className="mb-1" style={{ fontSize: 13, fontWeight: 600 }}>Class</Form.Label>
                                <SelectDropDown
                                    name="modalClassId"
                                    placeholderName="Select Class"
                                    options={classOptions}
                                    stateData={
                                        modalClassId
                                            ? classOptions.find((c) => String(c.value) === String(modalClassId)) || null
                                            : null
                                    }
                                    handleChange={this.handleModalClassChange}
                                />
                            </Col>
                            <Col xs={12} sm={6} md={3}>
                                <Form.Label className="mb-1" style={{ fontSize: 13, fontWeight: 600 }}>Section</Form.Label>
                                <SelectDropDown
                                    name="modalSectionId"
                                    placeholderName="Select Section"
                                    options={sectionOptionsForModal}
                                    stateData={
                                        modalSectionId
                                            ? sectionOptionsForModal.find((s) => String(s.value) === String(modalSectionId)) || null
                                            : null
                                    }
                                    handleChange={this.handleModalSectionChange}
                                />
                            </Col>
                            <Col xs={12} sm={6} md={3}>
                                <Form.Label className="mb-1" style={{ fontSize: 13, fontWeight: 600 }}>Date</Form.Label>
                                <DateBlock applyCallback={this.handleModalDateChange} defaultToday={true} />
                            </Col>
                            <Col xs={12} sm={6} md={3}>
                                <Form.Label className="mb-1" style={{ fontSize: 13, fontWeight: 600 }}>Status</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={modalStatusFilter}
                                    onChange={this.handleStatusFilterChange}
                                >
                                    <option value="">All Statuses</option>
                                    <option value="Present">Present ({statusCounts.Present})</option>
                                    <option value="Absent">Absent ({statusCounts.Absent})</option>
                                    <option value="Leave">Leave ({statusCounts.Leave})</option>
                                </Form.Control>
                            </Col>
                        </Row>

                        {modalLoading ? (
                            <div className="lecture-exam-shim w-100" style={{ height: 200 }} />
                        ) : modalError ? (
                            <p className="text-danger mb-0">Unable to load attendance details.</p>
                        ) : filteredTableRows.length === 0 && tableRows.length === 0 ? (
                            <p className="text-muted mb-0">No attendance records found.</p>
                        ) : (
                            <>
                                {/* ── Summary stat cards ── */}
                                {tableRows.length > 0 && (() => {
                                    const total = tableRows.length;
                                    const presentPct = total > 0 ? Math.round((statusCounts.Present / total) * 100) : 0;
                                    const absentPct = total > 0 ? Math.round((statusCounts.Absent / total) * 100) : 0;
                                    const leavePct = total > 0 ? Math.round((statusCounts.Leave / total) * 100) : 0;

                                    // class-wise and section-wise breakdown
                                    const classBreakdown = {};
                                    const sectionBreakdown = {};
                                    tableRows.forEach((row) => {
                                        const cn = row.class_name || "Unknown";
                                        const sn = row.section_name || "Unknown";
                                        if (!classBreakdown[cn]) classBreakdown[cn] = { total: 0, present: 0 };
                                        classBreakdown[cn].total += 1;
                                        if (row.status === "Present") classBreakdown[cn].present += 1;

                                        if (!sectionBreakdown[sn]) sectionBreakdown[sn] = { total: 0, present: 0 };
                                        sectionBreakdown[sn].total += 1;
                                        if (row.status === "Present") sectionBreakdown[sn].present += 1;
                                    });

                                    const chipBtn = (label, count, pct, bg, color, border) => (
                                        <div
                                            key={label}
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 6,
                                                background: bg,
                                                border: `1px solid ${border}`,
                                                borderRadius: 20,
                                                padding: "5px 12px",
                                                cursor: "default",
                                            }}
                                        >
                                            <span style={{ fontWeight: 700, fontSize: 12.5, color: "#1f2937" }}>{label}</span>
                                            <span style={{ fontWeight: 700, fontSize: 12, color, background: border, padding: "1px 7px", borderRadius: 12 }}>{count}</span>
                                            <span style={{ fontSize: 11, color, fontWeight: 600 }}>{pct}%</span>
                                        </div>
                                    );

                                    return (
                                        <div className="mb-3">
                                            {/* ── Row 1: 5 stat cards ── */}
                                            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 12 }}>
                                                {/* Total Students */}
                                                <div style={{ background: "linear-gradient(180deg, rgba(37,99,235,0.12) 0%, #eff6ff 100%)", borderRadius: 12, padding: "12px 14px", border: "1px solid #bfdbfe", borderTop: "5px solid #2563eb", overflow: "hidden" }}>
                                                    <p style={{ margin: 0, fontSize: 11, color: "#1d4ed8", fontWeight: 700 }}>Total Students</p>
                                                    <div style={{ fontSize: 28, fontWeight: 800, color: "#1e40af", lineHeight: 1.2 }}>{total}</div>
                                                </div>
                                                {/* Present */}
                                                <div style={{ background: "linear-gradient(180deg, rgba(22,163,74,0.12) 0%, #f0fdf4 100%)", borderRadius: 12, padding: "12px 14px", border: "1px solid #bbf7d0", borderTop: "5px solid #16a34a", overflow: "hidden" }}>
                                                    <p style={{ margin: 0, fontSize: 11, color: "#15803d", fontWeight: 700 }}>Present</p>
                                                    <div style={{ fontSize: 28, fontWeight: 800, color: "#16a34a", lineHeight: 1.2 }}>{statusCounts.Present}</div>
                                                    <div style={{ fontSize: 10.5, color: "#15803d", fontWeight: 600, marginTop: 2 }}>{presentPct}% of total</div>
                                                </div>
                                                {/* Absent */}
                                                <div style={{ background: "linear-gradient(180deg, rgba(220,38,38,0.12) 0%, #fef2f2 100%)", borderRadius: 12, padding: "12px 14px", border: "1px solid #fecaca", borderTop: "5px solid #dc2626", overflow: "hidden" }}>
                                                    <p style={{ margin: 0, fontSize: 11, color: "#b91c1c", fontWeight: 700 }}>Absent</p>
                                                    <div style={{ fontSize: 28, fontWeight: 800, color: "#dc2626", lineHeight: 1.2 }}>{statusCounts.Absent}</div>
                                                    <div style={{ fontSize: 10.5, color: "#b91c1c", fontWeight: 600, marginTop: 2 }}>{absentPct}% of total</div>
                                                </div>
                                                {/* Leave */}
                                                <div style={{ background: "linear-gradient(180deg, rgba(234,88,12,0.12) 0%, #fff7ed 100%)", borderRadius: 12, padding: "12px 14px", border: "1px solid #fed7aa", borderTop: "5px solid #ea580c", overflow: "hidden" }}>
                                                    <p style={{ margin: 0, fontSize: 11, color: "#c2410c", fontWeight: 700 }}>Leave</p>
                                                    <div style={{ fontSize: 28, fontWeight: 800, color: "#ea580c", lineHeight: 1.2 }}>{statusCounts.Leave}</div>
                                                    <div style={{ fontSize: 10.5, color: "#c2410c", fontWeight: 600, marginTop: 2 }}>{leavePct}% of total</div>
                                                </div>
                                                {/* Attendance Rate */}
                                                <div style={{
                                                    background: presentPct >= 75 ? "linear-gradient(180deg, rgba(22,163,74,0.12) 0%, #f0fdf4 100%)" : "linear-gradient(180deg, rgba(220,38,38,0.12) 0%, #fef2f2 100%)",
                                                    borderRadius: 12,
                                                    padding: "12px 14px",
                                                    border: `1px solid ${presentPct >= 75 ? "#bbf7d0" : "#fecaca"}`,
                                                    borderTop: `5px solid ${presentPct >= 75 ? "#16a34a" : "#dc2626"}`,
                                                    overflow: "hidden",
                                                }}>
                                                    <p style={{ margin: 0, fontSize: 11, color: "#64748b", fontWeight: 700 }}>Attendance Rate</p>
                                                    <div style={{ fontSize: 28, fontWeight: 800, color: presentPct >= 75 ? "#16a34a" : "#dc2626", lineHeight: 1.2 }}>{presentPct}%</div>
                                                    <div style={{ fontSize: 10.5, color: presentPct >= 75 ? "#15803d" : "#b91c1c", fontWeight: 600, marginTop: 2 }}>
                                                        {presentPct >= 75 ? "✓ Good" : "⚠ Low"}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* ── Row 2: Class-wise | Section-wise ── */}
                                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                                                {/* Class-wise */}
                                                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #e2e8f0" }}>
                                                    <p style={{ margin: "0 0 8px", fontSize: 11.5, fontWeight: 700, color: "#7c3aed" }}>
                                                        <i className="fal fa-chalkboard" style={{ marginRight: 5 }} />
                                                        Class-wise ({Object.keys(classBreakdown).length})
                                                    </p>
                                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                                        {Object.entries(classBreakdown).map(([cls, data]) => {
                                                            const pct = data.total > 0 ? Math.round((data.present / data.total) * 100) : 0;
                                                            return chipBtn(cls, data.total, pct, "#f5f3ff", pct >= 75 ? "#15803d" : "#b91c1c", pct >= 75 ? "#d1fae5" : "#fee2e2");
                                                        })}
                                                    </div>
                                                </div>
                                                {/* Section-wise */}
                                                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px", border: "1px solid #e2e8f0" }}>
                                                    <p style={{ margin: "0 0 8px", fontSize: 11.5, fontWeight: 700, color: "#0891b2" }}>
                                                        <i className="fal fa-layer-group" style={{ marginRight: 5 }} />
                                                        Section-wise ({Object.keys(sectionBreakdown).length})
                                                    </p>
                                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                                        {Object.entries(sectionBreakdown).map(([sec, data]) => {
                                                            const pct = data.total > 0 ? Math.round((data.present / data.total) * 100) : 0;
                                                            return chipBtn(sec, data.total, pct, "#ecfeff", pct >= 75 ? "#15803d" : "#b91c1c", pct >= 75 ? "#d1fae5" : "#fee2e2");
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Data table */}
                                {filteredTableRows.length === 0 ? (
                                    <p className="text-muted mb-0">No records match the selected filters.</p>
                                ) : (
                                    <CardLessDataTableWithSearch
                                        parentData={filteredTableRows}
                                        particlecolumns={tableColumns}
                                        defaultSorted={[{ dataField: "student_name", order: "asc" }]}
                                        name="lecturer_attendance"
                                        placeholder="Search student..."
                                    />
                                )}
                            </>
                        )}
                    </Modal.Body>
                </Modal>

                <Modal show={scheduleModalShow} onHide={this.closeScheduleModal} size="xl" centered scrollable dialogClassName="lecturer-dashboard-modern">
                    <Modal.Header closeButton style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <Modal.Title style={{ fontWeight: 800, fontSize: 17, display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 34, height: 34, borderRadius: 8, background: "#ecfdf5", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <i className="fal fa-calendar-alt" style={{ color: "#059669", fontSize: 16 }} />
                            </div>
                            Timetable Details
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: "20px 24px" }}>
                        {/* Filter Bar */}
                        <div style={{ background: "#f8fafc", borderRadius: 12, padding: "14px 16px", border: "1px solid #e2e8f0", marginBottom: 16 }}>
                            <Row className="g-3">
                                <Col xs={12} sm={6} md={4}>
                                    <Form.Label className="mb-1" style={{ fontSize: 12.5, fontWeight: 700, color: "#374151" }}>
                                        <i className="fal fa-chalkboard me-1 text-primary" /> Class
                                    </Form.Label>
                                    <SelectDropDown
                                        name="scheduleModalClassId"
                                        placeholderName="Select Class"
                                        options={classOptions}
                                        stateData={
                                            scheduleModalClassId
                                                ? classOptions.find((c) => String(c.value) === String(scheduleModalClassId)) || null
                                                : null
                                        }
                                        handleChange={this.handleScheduleModalClassChange}
                                    />
                                </Col>

                                <Col xs={12} sm={6} md={4}>
                                    <Form.Label className="mb-1" style={{ fontSize: 12.5, fontWeight: 700, color: "#374151" }}>
                                        <i className="fal fa-layer-group me-1 text-info" /> Section
                                    </Form.Label>
                                    <SelectDropDown
                                        name="scheduleModalSectionId"
                                        placeholderName="Select Section"
                                        options={sectionOptionsForScheduleModal}
                                        stateData={
                                            scheduleModalSectionId
                                                ? sectionOptionsForScheduleModal.find((s) => String(s.value) === String(scheduleModalSectionId)) || null
                                                : null
                                        }
                                        handleChange={this.handleScheduleModalSectionChange}
                                    />
                                </Col>

                                <Col xs={12} sm={6} md={4}>
                                    <Form.Label className="mb-1" style={{ fontSize: 12.5, fontWeight: 700, color: "#374151" }}>
                                        <i className="fal fa-calendar me-1 text-success" /> Date
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={moment.unix(scheduleModalDate).format("YYYY-MM-DD")}
                                        onChange={this.handleScheduleModalDateChange}
                                        style={{ borderRadius: 8, fontSize: 13, fontWeight: 600 }}
                                    />
                                </Col>
                            </Row>
                        </div>

                        {/* Selected Date & Stat Count Cards */}
                        {(() => {
                            let completedCount = 0;
                            let inProgressCount = 0;
                            let upcomingCount = 0;
                            filteredScheduleModalPeriods.forEach((period) => {
                                const status = this.classifyPeriodStatus(period.start, period.end, scheduleModalDate);
                                if (status === "Completed") completedCount++;
                                else if (status === "In Progress") inProgressCount++;
                                else upcomingCount++;
                            });
                            const totalLectures = filteredScheduleModalPeriods.length;

                            return (
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap" style={{ gap: 8 }}>
                                        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#1e293b" }}>
                                            <i className="fal fa-calendar-day me-1 text-primary" />
                                            {moment.unix(scheduleModalDate).format("dddd, DD MMMM YYYY")}
                                        </p>
                                    </div>

                                    {/* 4 Stat Count Cards */}
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
                                        {/* Total Lectures */}
                                        <div style={{ background: "linear-gradient(180deg, rgba(124,58,237,0.12) 0%, #f5f3ff 100%)", borderRadius: 12, padding: "12px 14px", border: "1px solid #ddd6fe", borderTop: "5px solid #7c3aed", overflow: "hidden" }}>
                                            <p style={{ margin: 0, fontSize: 11, color: "#7c3aed", fontWeight: 700 }}>Total Lectures</p>
                                            <div style={{ fontSize: 26, fontWeight: 800, color: "#5b21b6", lineHeight: 1.2, marginTop: 2 }}>{totalLectures}</div>
                                        </div>
                                        {/* Completed */}
                                        <div style={{ background: "linear-gradient(180deg, rgba(22,163,74,0.12) 0%, #f0fdf4 100%)", borderRadius: 12, padding: "12px 14px", border: "1px solid #bbf7d0", borderTop: "5px solid #16a34a", overflow: "hidden" }}>
                                            <p style={{ margin: 0, fontSize: 11, color: "#15803d", fontWeight: 700 }}>Completed</p>
                                            <div style={{ fontSize: 26, fontWeight: 800, color: "#16a34a", lineHeight: 1.2, marginTop: 2 }}>{completedCount}</div>
                                        </div>
                                        {/* In Progress */}
                                        <div style={{ background: "linear-gradient(180deg, rgba(37,99,235,0.12) 0%, #eff6ff 100%)", borderRadius: 12, padding: "12px 14px", border: "1px solid #bfdbfe", borderTop: "5px solid #2563eb", overflow: "hidden" }}>
                                            <p style={{ margin: 0, fontSize: 11, color: "#1d4ed8", fontWeight: 700 }}>In Progress</p>
                                            <div style={{ fontSize: 26, fontWeight: 800, color: "#2563eb", lineHeight: 1.2, marginTop: 2 }}>{inProgressCount}</div>
                                        </div>
                                        {/* Upcoming */}
                                        <div style={{ background: "linear-gradient(180deg, rgba(234,88,12,0.12) 0%, #fff7ed 100%)", borderRadius: 12, padding: "12px 14px", border: "1px solid #fed7aa", borderTop: "5px solid #ea580c", overflow: "hidden" }}>
                                            <p style={{ margin: 0, fontSize: 11, color: "#c2410c", fontWeight: 700 }}>Upcoming</p>
                                            <div style={{ fontSize: 26, fontWeight: 800, color: "#ea580c", lineHeight: 1.2, marginTop: 2 }}>{upcomingCount}</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        {scheduleModalLoading ? (
                            <div className="lecture-exam-shim w-100" style={{ height: 160 }} />
                        ) : scheduleModalError ? (
                            <p className="text-danger mb-0">Unable to load the timetable for this date.</p>
                        ) : filteredScheduleModalPeriods.length === 0 ? (
                            <div className="text-center py-4" style={{ background: "#f8fafc", borderRadius: 12, border: "1px dashed #cbd5e1" }}>
                                <i className="fal fa-calendar-times text-muted" style={{ fontSize: 28 }} />
                                <p className="text-muted mb-0 mt-2" style={{ fontSize: 13, fontWeight: 600 }}>No lectures scheduled for the selected date.</p>
                            </div>
                        ) : (
                            <Row className="g-3">
                                {filteredScheduleModalPeriods.map((period, idx) => (
                                    <Col xs={12} sm={4} key={`${period.class_id}-${period.section_id}-${idx}`} className='mt-3'>
                                        {this.renderScheduleItemCard(period, scheduleModalDate, idx)}
                                    </Col>
                                ))}
                            </Row>
                        )}
                    </Modal.Body>
                </Modal>

                {/* Student Doubts & Clarifications Section (Scoped to Lecturer Data) */}
                {this.renderStudentDoubtsSection()}

                <Card className="modern-card border-0 shadow-sm mb-3">
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: 10 }}>
                            <div className="d-flex align-items-center" style={{ gap: 10 }}>
                                <div
                                    className="lec-stat-icon"
                                    style={{
                                        width: 34,
                                        height: 34,
                                        borderRadius: 8,
                                        background: "#eff6ff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                    }}
                                >
                                    <i className="fal fa-th-large" style={{ color: "#2563eb", fontSize: 16 }} />
                                </div>
                                <h5 className="font-weight-bold text-dark mb-0">My Access Modules</h5>
                            </div>
                            {accessibleModules.length > 0 && (
                                <span
                                    style={{
                                        background: "#eff6ff",
                                        color: "#1d4ed8",
                                        fontWeight: 700,
                                        fontSize: 12,
                                        padding: "4px 12px",
                                        borderRadius: 20,
                                    }}
                                >
                                    {accessibleModules.length} Modules
                                </span>
                            )}
                        </div>

                        {accessibleModules.length === 0 ? (
                            <p className="text-muted mb-0">No modules assigned.</p>
                        ) : (
                            <Row className="g-3">
                                {accessibleModules.map((module, idx) => (
                                    <Col xs={12} sm={6} md={4} lg={3} key={module.path}
                                        style={{ animationDelay: `${Math.min(idx, 12) * 40}ms` }}
                                    >
                                        <div
                                            style={{
                                                background: "#fff",
                                                border: `1px solid ${module.bg}`,
                                                borderRadius: 14,
                                                padding: "16px 16px 14px",
                                                height: "100%",
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 10,
                                                transition: "box-shadow 0.2s ease, transform 0.2s ease",
                                                animation: `moduleCardFadeIn 0.3s ease both`,
                                                animationDelay: `${Math.min(idx, 12) * 40}ms`,
                                            }}
                                            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 22px rgba(15,23,42,0.10)"; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
                                        >
                                            {/* Header: icon + title */}
                                            <div className="d-flex align-items-center" style={{ gap: 10 }}>
                                                <div
                                                    style={{
                                                        width: 42,
                                                        height: 42,
                                                        borderRadius: 10,
                                                        background: module.bg,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        flexShrink: 0,
                                                        fontSize: 18,
                                                        color: module.color,
                                                    }}
                                                >
                                                    <i className={module.icon} />
                                                </div>
                                                <span
                                                    style={{
                                                        fontWeight: 700,
                                                        fontSize: 13.5,
                                                        color: "#1f2937",
                                                        lineHeight: 1.3,
                                                        textTransform: "capitalize",
                                                    }}
                                                >
                                                    {module.label}
                                                </span>
                                            </div>

                                            {/* Description */}
                                            <p
                                                style={{
                                                    fontSize: 12,
                                                    color: "#64748b",
                                                    margin: 0,
                                                    lineHeight: 1.5,
                                                    flex: 1,
                                                }}
                                            >
                                                {module.desc || "Click below to open this module."}
                                            </p>

                                            {/* Redirect button */}
                                            <Link
                                                to={module.path}
                                                style={{
                                                    display: "block",
                                                    textAlign: "center",
                                                    padding: "7px 0",
                                                    borderRadius: 8,
                                                    background: module.bg,
                                                    color: module.color,
                                                    fontWeight: 700,
                                                    fontSize: 12.5,
                                                    textDecoration: "none",
                                                    border: `1px solid ${module.bg}`,
                                                    transition: "background 0.15s ease, color 0.15s ease",
                                                }}
                                                onMouseEnter={(e) => { e.currentTarget.style.background = module.color; e.currentTarget.style.color = "#fff"; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.background = module.bg; e.currentTarget.style.color = module.color; }}
                                            >
                                                Go to Module &rarr;
                                            </Link>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        )}
                    </Card.Body>
                </Card>

                {/* ── Student Performance / Action Modal ── */}
                <Modal
                    show={this.state.studentActionModalShow}
                    onHide={this.closeStudentActionModal}
                    size="lg"
                    centered
                    dialogClassName="lecturer-dashboard-modern"
                >
                    {this.state.selectedStudentForAction && (() => {
                        const student = this.state.selectedStudentForAction;
                        const isTop = this.state.studentActionType === "top";
                        return (
                            <>
                                <Modal.Header closeButton style={{ borderBottom: "1px solid #f1f5f9" }}>
                                    <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
                                        {isTop ? "🌟 Top Performer Profile" : "⚠️ Student Action Center"}
                                    </Modal.Title>
                                </Modal.Header>
                                <Modal.Body style={{ padding: "20px 24px" }}>
                                    {/* Student Header Card */}
                                    <div
                                        style={{
                                            background: isTop ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)" : "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
                                            borderRadius: 14,
                                            padding: "16px 20px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            marginBottom: 20,
                                            border: `1px solid ${isTop ? "#bbf7d0" : "#fecaca"}`,
                                        }}
                                    >
                                        <div className="d-flex align-items-center" style={{ gap: 14 }}>
                                            <div
                                                style={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: "50%",
                                                    background: isTop ? "#16a34a" : "#dc2626",
                                                    color: "#fff",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: 20,
                                                    fontWeight: 800,
                                                }}
                                            >
                                                {(student.student_name || "S").charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <h5 style={{ margin: 0, fontWeight: 800, color: "#1f2937" }}>{student.student_name}</h5>
                                                <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "#4b5563", fontWeight: 600 }}>
                                                    Class: <strong>{student.class_name || "-"}</strong> &nbsp;|&nbsp; Section: <strong>{student.section_name || "-"}</strong>
                                                </p>
                                            </div>
                                        </div>
                                        <span
                                            style={{
                                                background: "#fff",
                                                color: isTop ? "#16a34a" : "#dc2626",
                                                fontWeight: 800,
                                                fontSize: 12.5,
                                                padding: "6px 14px",
                                                borderRadius: 20,
                                                border: `1px solid ${isTop ? "#bbf7d0" : "#fecaca"}`,
                                            }}
                                        >
                                            {isTop ? `🏆 Rank #${student.rank || 1}` : "⚠️ Action Required"}
                                        </span>
                                    </div>

                                    {/* Stat Grid */}
                                    <Row className="g-3 mb-4">
                                        <Col xs={6} md={3}>
                                            <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 14px", border: "1px solid #e2e8f0", textAlign: "center" }}>
                                                <p style={{ margin: 0, fontSize: 11, color: "#64748b", fontWeight: 600 }}>Marks Scored</p>
                                                <div style={{ fontSize: 22, fontWeight: 800, color: isTop ? "#16a34a" : "#dc2626", marginTop: 2 }}>
                                                    {student.marks ?? "-"}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 14px", border: "1px solid #e2e8f0", textAlign: "center" }}>
                                                <p style={{ margin: 0, fontSize: 11, color: "#64748b", fontWeight: 600 }}>Accuracy</p>
                                                <div style={{ fontSize: 22, fontWeight: 800, color: "#0284c7", marginTop: 2 }}>
                                                    {student.accuracy ?? "-"}%
                                                </div>
                                            </div>
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 14px", border: "1px solid #e2e8f0", textAlign: "center" }}>
                                                <p style={{ margin: 0, fontSize: 11, color: "#64748b", fontWeight: 600 }}>Attendance</p>
                                                <div style={{ fontSize: 22, fontWeight: 800, color: (student.attendance_percentage || 100) < 75 ? "#dc2626" : "#16a34a", marginTop: 2 }}>
                                                    {student.attendance_percentage ?? 100}%
                                                </div>
                                            </div>
                                        </Col>
                                        <Col xs={6} md={3}>
                                            <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 14px", border: "1px solid #e2e8f0", textAlign: "center" }}>
                                                <p style={{ margin: 0, fontSize: 11, color: "#64748b", fontWeight: 600 }}>Weak Subject</p>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: "#7c3aed", marginTop: 6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {student.weak_subject || "None"}
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>

                                    {/* Action Notification Banner */}
                                    {this.state.actionNotificationSent && (
                                        <div className="alert alert-success d-flex align-items-center mb-3" role="alert" style={{ borderRadius: 10, fontSize: 13, fontWeight: 600 }}>
                                            <i className="fal fa-check-circle me-2" style={{ fontSize: 18 }} />
                                            Action notification sent successfully to student and parent!
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    <p style={{ fontSize: 12.5, fontWeight: 700, color: "#374151", marginBottom: 8 }}>
                                        <i className="fal fa-bolt me-1 text-primary" />
                                        Recommended Quick Actions:
                                    </p>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                                        <button
                                            className="btn btn-outline-primary d-flex align-items-center justify-content-center gap-2 py-2"
                                            style={{ borderRadius: 10, fontWeight: 700, fontSize: 13 }}
                                            onClick={this.handleSendStudentActionNotification}
                                        >
                                            <i className="fal fa-paper-plane" />
                                            Send SMS / Parent Alert
                                        </button>
                                        <button
                                            className="btn btn-outline-success d-flex align-items-center justify-content-center gap-2 py-2"
                                            style={{ borderRadius: 10, fontWeight: 700, fontSize: 13 }}
                                            onClick={this.handleSendStudentActionNotification}
                                        >
                                            <i className="fal fa-award" />
                                            {isTop ? "Send Congratulations" : "Assign Remedial Class"}
                                        </button>
                                        <button
                                            className="btn btn-outline-warning d-flex align-items-center justify-content-center gap-2 py-2"
                                            style={{ borderRadius: 10, fontWeight: 700, fontSize: 13 }}
                                            onClick={this.handleSendStudentActionNotification}
                                        >
                                            <i className="fal fa-user-edit" />
                                            Add Mentor Note
                                        </button>
                                        <button
                                            className="btn btn-outline-info d-flex align-items-center justify-content-center gap-2 py-2"
                                            style={{ borderRadius: 10, fontWeight: 700, fontSize: 13 }}
                                            onClick={this.closeStudentActionModal}
                                        >
                                            <i className="fal fa-chart-line" />
                                            View Performance History
                                        </button>
                                    </div>
                                </Modal.Body>
                            </>
                        );
                    })()}
                </Modal>

                {examDetailsModalShow && selectedExamForDetails && (
                    <ExamDetailsModal
                        exam={selectedExamForDetails}
                        client={this.props.client}
                        onClose={this.closeExamDetailsModal}
                        authorizedClassNames={authorizedClassNames}
                        authorizedSectionNames={authorizedSectionNames}
                        authorizedSubjectNames={authorizedSubjectNames}
                        orgProfile={this.state.orgProfile}
                    />
                )}

                {/* <DashboardAIChatbot
                    classOptions={classOptions}
                    allSectionOptions={allSectionOptions}
                    totalStudents={totalStudents}
                    presentCount={presentCount}
                    absentCount={absentCount}
                    averageAttendance={averageAttendance}
                    subjectPerformance={subjectPerformance}
                    classPerformance={classPerformance}
                    sectionPerformance={sectionPerformance}
                    topStudents={topStudents}
                    attentionStudents={attentionStudents}
                    studentDoubts={studentDoubts}
                    examReports={examReports}
                    lecturerName={lecturerName}
                    modules={accessibleModules}
                /> */}
            </Container>
        );
    }
}

export default withApollo(LectureDashboardSection);
